import React, { useState, useEffect, useLayoutEffect, useRef, useContext } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import visitService from '../../services/visitService';
import auditService from '../../services/auditService';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { Modal } from '../Common/Modal';
import { ElectronicSign } from '../Common/ElectronicSign';
import { AuthContext } from '../../context/AuthContext';
import { useSession } from '../../context/SessionContext';
import { useProject } from '../../context/ProjectContext';
import CreateActionItemModal from '../ActionItems/CreateActionItemModal';
import { FiPlus } from 'react-icons/fi';
import { report as reportMessages, dcf as dcfMessages, common as commonMessages, actionItems as actionItemMessages } from '../../utils/validationMessages';
import { formatDateDDMMMYYYY, formatDateTimeDDMMMYYYY, formatOffsetForDisplay, formatPerformedOnUtcToLocal } from '../../utils/dateFormat';
import { dispatchNotificationsRefresh } from '../../utils/constants';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import './CRFForm.css';

// Read value from object using first matching key (handles API naming convention differences)
const getAnyCase = (obj, ...keys) => {
  if (obj == null) return undefined;
  for (const k of keys) {
    if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k];
  }
  return undefined;
};

// STS AutoCapture: execute external API (via CTMS.API) and populate disabled textarea.
const STS_AUTOCAPTURE_SEL = '[data-sts-autocapture="true"][data-sts-api-detail-no]';
const STS_AUTOCAPTURE_ERROR_ATTR = 'data-sts-autocapture-error';
const STS_AUTOCAPTURE_LOADING_ATTR = 'data-sts-autocapture-loading';

/**
 * html2canvas output must stay within browser canvas limits or the bitmap is empty (blank PDF pages).
 * Use per-page vertical slices so each snapshot stays small enough for scale 2–3 instead of one giant canvas.
 */
function computeHtml2PdfCanvasScaleForRect(cssWidth, cssHeight, preferredScale = 2) {
  const w = Math.max(1, Math.ceil(cssWidth));
  const h = Math.max(1, Math.ceil(cssHeight));
  const MAX_EDGE = 8192;
  const MAX_AREA = 100663296;
  const byW = MAX_EDGE / w;
  const byH = MAX_EDGE / h;
  const byArea = Math.sqrt(MAX_AREA / (w * h));
  const raw = Math.min(preferredScale, byW, byH, byArea);
  if (!(raw > 0) || !Number.isFinite(raw)) return Math.min(preferredScale, 1);
  return Math.round(raw * 10000) / 10000;
}

/**
 * html2canvas+jsPDF uses fixed Y slices; cutting through table rows clips text and drops borders.
 * Build slice heights that end at row/section bottoms (and wrapped-line bottoms inside preview fields).
 */
function computeVisitReportPdfSlices(container, sliceCssH, cssH) {
  const EPS = 2;
  const cr = container.getBoundingClientRect();
  const scrollTop = container.scrollTop || 0;
  const bottomY = (el) => {
    if (!el || !container.contains(el)) return null;
    const er = el.getBoundingClientRect();
    return er.bottom - cr.top + scrollTop;
  };

  const candidates = new Set();
  const addBottom = (el) => {
    const y = bottomY(el);
    if (y != null && Number.isFinite(y) && y > EPS) {
      candidates.add(Math.min(Math.ceil(y), cssH));
    }
  };

  const addSelectorAll = (sel) => {
    container.querySelectorAll(sel).forEach(addBottom);
  };

  addSelectorAll('.crf-report-preview-header');
  addSelectorAll('.crf-metadata-section');
  addSelectorAll('.crf-report-table .crf-table-header');
  addSelectorAll('.crf-report-table .crf-report-row');
  addSelectorAll('.crf-open-issues-section h3.crf-open-issues-title');
  addSelectorAll('.crf-open-issues-table thead tr');
  addSelectorAll('.crf-open-issues-table tbody tr');
  addSelectorAll('.crf-open-dcfs-section h3.crf-open-issues-title');
  addSelectorAll('.crf-open-dcfs-table thead tr');
  addSelectorAll('.crf-open-dcfs-table tbody tr');
  addSelectorAll('.crf-generated-content .templateSubGroup');

  // Line-level breaks inside long preview text so slices rarely cut mid-line inside a cell
  container.querySelectorAll('.crf-preview-field-value, .crf-question-cell, .crf-answer-cell, .crf-comments-cell').forEach((el) => {
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      const rects = range.getClientRects();
      for (let i = 0; i < rects.length; i += 1) {
        const b = rects[i].bottom - cr.top + scrollTop;
        if (Number.isFinite(b) && b > EPS) {
          candidates.add(Math.min(Math.ceil(b), cssH));
        }
      }
      range.detach?.();
    } catch (_) {
      addBottom(el);
    }
  });

  candidates.add(cssH);
  const breakpoints = [...candidates].filter((y) => y > 0).sort((a, b) => a - b);

  const slices = [];
  let y0 = 0;
  const maxY = Math.max(1, cssH);
  let guard = 0;
  while (y0 < maxY - EPS && guard < 5000) {
    guard += 1;
    const targetEnd = Math.min(y0 + sliceCssH, maxY);
    let y1 = targetEnd;
    for (let i = breakpoints.length - 1; i >= 0; i -= 1) {
      const b = breakpoints[i];
      if (b > y0 + EPS && b <= targetEnd + EPS) {
        y1 = Math.min(b, maxY);
        break;
      }
    }
    if (y1 <= y0 + EPS) {
      y1 = Math.min(targetEnd, maxY);
    }
    if (y1 <= y0 + EPS) {
      y1 = Math.min(y0 + Math.max(40, Math.floor(sliceCssH / 4)), maxY);
    }
    const sliceH = y1 - y0;
    if (sliceH > EPS) {
      slices.push({ y0, height: sliceH });
    }
    y0 = y1;
  }
  return slices.length > 0 ? slices : [{ y0: 0, height: maxY }];
}

/** First/Second Review By: blank when API sent em dash, en dash, or "--" placeholder. */
function formatWorkflowReviewerDisplay(value) {
  if (value == null) return '';
  const t = String(value).trim();
  if (t === '' || t === '--' || t === '\u2014' || t === '\u2013') return '';
  return String(value);
}

/**
 * Format formula Calc result. When template "decimal places" is 0, toFixed(0) would turn 0.375 into "0";
 * for non-integers we still show a trimmed decimal string.
 */
function formatFormulaCalcResult(num, decimalPlaces) {
  if (num === undefined || Number.isNaN(num) || !Number.isFinite(num)) return '';
  const n = parseFloat(num);
  const places = Math.max(0, Number(decimalPlaces) || 0);
  if (places > 0) {
    return n.toFixed(places);
  }
  const roundedInt = Math.round(n);
  if (Math.abs(n - roundedInt) < 1e-9) {
    return String(roundedInt);
  }
  let s = n.toFixed(10);
  if (s.includes('.')) {
    s = s.replace(/\.?0+$/, '');
  }
  return s;
}

/**
 * Turn math-style implicit multiplication into valid JS for eval, e.g.
 * "( a * b ) / c ( d * 2 )" -> "( a * b ) / c*( d * 2 )"; also ")(" -> ")*(".
 */
function insertImplicitMultiplicationForFormula(expr) {
  if (expr == null || typeof expr !== 'string') return expr;
  let s = expr;
  // )(  ->  )*(
  s = s.replace(/\)\(/g, ')*(');
  // number (  ->  number*(
  s = s.replace(/(-?\d+(?:\.\d+)?)\s+\(/g, '$1*(');
  // number(  ->  number*(  (no space, e.g. "5(10+1)")
  s = s.replace(/(-?\d+(?:\.\d+)?)\(/g, '$1*(');
  // ) (  ->  )*(
  s = s.replace(/\)\s+\(/g, ')*(');
  // ) number  ->  )*number
  s = s.replace(/\)\s+(-?\d+(?:\.\d+)?)/g, ')*$1');
  return s;
}

/**
 * Replace field codes in formula string with values from the report DOM; normalize "1 0 0" → 100; map ^ to ** for JS.
 */
function substituteFieldCodesInFormula(content, formulaStr) {
  if (!content || formulaStr == null) return '';
  const formulaStrClean = String(formulaStr).replace(/"/g, '').replace(/'/g, '');
  const tokens = formulaStrClean
    .replace(/\(|\)|\?/g, ' ')
    .replace(/[+\-*/^]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const isFieldCode = (token) => /^\d+$/.test(token) && token.length >= 5;
  let reduced = formulaStrClean.replace(/\?/g, '');
  for (let i = 0; i < tokens.length; i++) {
    const code = tokens[i];
    if (!isFieldCode(code)) continue;
    const el = content.querySelector(`input[id$="${code}"], select[id$="${code}"], textarea[id$="${code}"]`);
    let val = el ? (el.value || '').trim() : '';
    if (val === '' || val === undefined) val = 0;
    else val = parseFloat(val);
    if (Number.isNaN(val)) val = 0;
    const regex = new RegExp(code.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
    reduced = reduced.replace(regex, String(val));
  }
  reduced = reduced.replace(/\s*1\s+0\s+0\s*/g, ' 100 ');
  reduced = reduced.replace(/\^/g, '**');
  let sanitized = reduced.replace(/[^0-9+\-*/.()\s]/g, '');
  sanitized = insertImplicitMultiplicationForFormula(sanitized.trim());
  return sanitized;
}

/**
 * Evaluate sanitized arithmetic (digits, + - * / **, parentheses, spaces). Returns ok + value or error message (never conflate with numeric 0).
 */
function evaluateSanitizedFormula(sanitized) {
  const s = (sanitized || '').trim();
  if (!s) {
    return { ok: false, value: null, message: 'Formula is empty or invalid after replacing field values.' };
  }
  try {
    // eslint-disable-next-line no-eval -- only runs on charset-restricted arithmetic strings built above
    const num = eval(s);
    if (typeof num === 'bigint') {
      return { ok: true, value: Number(num), message: '' };
    }
    if (typeof num !== 'number') {
      return { ok: false, value: null, message: 'Formula did not evaluate to a number.' };
    }
    if (Number.isNaN(num) || !Number.isFinite(num)) {
      const message = Number.isNaN(num)
        ? 'Result is not a number (NaN).'
        : 'Result is infinite (for example, division by zero).';
      return { ok: false, value: null, message };
    }
    return { ok: true, value: num, message: '' };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    return { ok: false, value: null, message: msg ? `Invalid formula: ${msg}` : 'Invalid formula expression.' };
  }
}

/**
 * Audit trail values for CRF date fields commonly arrive as ISO dates (yyyy-mm-dd).
 * Normalize them to the same UI date format (dd-mmm-yyyy) for consistent display.
 */
function formatAuditValueForDisplay(value) {
  if (value == null) return '';
  const raw = String(value).trim();
  if (!raw) return '';

  // Match "2026-04-08" or "2026-04-08T00:00:00" (we only care about date part)
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s].*)?$/.exec(raw);
  if (!m) return raw;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return raw;
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return raw;

  // Use local date constructor to avoid UTC offset shifting the calendar day.
  const dt = new Date(y, mo - 1, d);
  if (Number.isNaN(dt.getTime())) return raw;
  return formatDateDDMMMYYYY(dt);
}

// Report title by visit type (dynamic per plan)
const reportTitleByVisitType = {
  SSV: 'Site Selection Visit Report (SSV)',
  SIV: 'Site Initiation Visit Report (SIV)',
  SMV: 'Site Monitoring Visit Report (SMV)',
  SCV: 'Site Close-Out Visit Report (SCV)',
};

/**
 * True when Standard Date/DateTime string is only template placeholders (DD, MMM, YYYY, HH, MM).
 * UK/UNK/UKUK/0000 are not placeholders. Empty string returns false (use !val for "missing").
 */
function isPlaceholderStandardDateValue(val) {
  const v = (val || '').trim();
  if (!v) return false;
  if (v.length < 8) return false;
  const dateTimeMatch = v.match(/^([^-]+)-([^-]+)-([^\s-]+)(?:\s+([^:]+):([^:]+))?$/);
  if (!dateTimeMatch) return false;
  const day = (dateTimeMatch[1] || '').trim().toUpperCase();
  const month = (dateTimeMatch[2] || '').trim().toUpperCase();
  const year = (dateTimeMatch[3] || '').trim().toUpperCase();
  if (day === 'DD' || month === 'MMM' || year === 'YYYY') return true;
  if (dateTimeMatch[4] != null) {
    const hour = (dateTimeMatch[4] || '').trim().toUpperCase();
    const minute = (dateTimeMatch[5] || '').trim().toUpperCase();
    if (hour === 'HH' || minute === 'MM') return true;
  }
  return false;
}

/** Same as isPlaceholderStandardDateValue (legacy name for submit validation). */
const isPlaceholderOnlyStandardDateOrDateTime = isPlaceholderStandardDateValue;

/** Report table: Answer vs Comments columns (server `.crf-*-col` + client transform `.crf-*-cell`). */
const CRF_COMMENT_REGION_SEL = '.crf-comments-cell, .crf-comments-col';
const CRF_ANSWER_REGION_SEL = '.crf-answer-cell, .crf-answer-col';
const CRF_COMMENTS_TEXTAREA_SEL = '.crf-comments-cell textarea, .crf-comments-col textarea';
/** Row-scoped primary controls (any type) for DCF/highlight and field id resolution. */
const CRF_ROW_ANSWER_CONTROLS_SEL =
  '.crf-answer-cell [data-field-id], .crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col [data-field-id], .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea';

function isCrfCommentRegionElement(el) {
  return !!el?.closest?.(CRF_COMMENT_REGION_SEL);
}

function isCrfAnswerRegionElement(el) {
  return !!el?.closest?.(CRF_ANSWER_REGION_SEL);
}

/**
 * True if this DOM control is the Comments-side field (or legacy orphan *_comments), not the primary answer.
 * Used like collectFormData: never rely on id suffix alone for "comment" when the control sits in the Answer column.
 */
function isCrfCommentFieldControl(el) {
  if (!el) return false;
  if (isCrfCommentRegionElement(el)) return true;
  const id = (el.id || '').trim();
  const nm = (el.name || '').trim();
  if (!isCrfAnswerRegionElement(el) && (id.endsWith('_comments') || nm.endsWith('_comments'))) return true;
  return false;
}

/**
 * CRA: hide Comments column for rows whose DCF is Resolved or Closed (response already submitted).
 * When no strict Open DCFs remain, the API sets IsEditable on all fields and setFieldsEnabled(true) would re-enable every comment box — this re-applies per-row hiding.
 */
function applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, readOnly, isViewOnly) {
  if (!content) return;
  const clearMarkers = () => {
    content.querySelectorAll('.crf-comments-cell.crf-comments-dcf-resolved-cra, .crf-comments-col.crf-comments-dcf-resolved-cra').forEach((commentsCell) => {
      commentsCell.classList.remove('crf-comments-dcf-resolved-cra');
      commentsCell.querySelectorAll('.crf-comments-dcf-resolved-placeholder').forEach((p) => p.remove());
      const ta = commentsCell.querySelector('textarea, input');
      if (ta) ta.style.removeProperty('display');
    });
  };
  if (!isCraUser || readOnly || isViewOnly) {
    clearMarkers();
    return;
  }
  clearMarkers();
  const rows = content.querySelectorAll('.crf-report-row:not(.crf-report-row-label-only)');
  rows.forEach((row) => {
    const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
    if (!commentsCell) return;
    const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
    const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;
    let field = null;
    if (nCRFDtlNoForRow != null && !Number.isNaN(nCRFDtlNoForRow)) {
      field = (savedFields || []).find((f) => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
    }
    if (!field) {
      const input = row.querySelector(CRF_ROW_ANSWER_CONTROLS_SEL);
      const fieldId = (input?.getAttribute('data-field-id') || '').trim();
      if (fieldId) {
        field = (savedFields || []).find((f) => {
          const fId = (f.vFieldId || f.VFieldId || '').trim();
          return fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase();
        });
      }
    }
    if (!field) return;
    const status = (field.vDCFStatus ?? field.VDCFStatus ?? '').toString().trim().toLowerCase();
    const hide = status === 'resolved' || status === 'closed';
    if (!hide) return;
    // Keep the comment box visible and disabled like other non-DCF rows (no em dash placeholder).
    commentsCell.classList.remove('crf-comments-dcf-resolved-cra');
    commentsCell.querySelectorAll('.crf-comments-dcf-resolved-placeholder').forEach((p) => p.remove());
    const ta = commentsCell.querySelector('textarea, input');
    if (ta) {
      ta.style.removeProperty('display');
      ta.setAttribute('disabled', 'disabled');
      ta.setAttribute('readonly', 'readonly');
      ta.style.removeProperty('background-color');
    }
  });
}

/** Find `.crf-report-row` for a vFieldId (all generator prefixes: textbox_, combobox_, datetime_, time_, etc.). */
function findCrfReportRowForFieldId(content, vFieldId) {
  if (!content || vFieldId == null || vFieldId === '') return null;
  const vf = String(vFieldId).trim();
  if (!vf) return null;
  const byMed = content.querySelector(`.crf-report-row[data-medex-code="${vf}"]`);
  if (byMed) return byMed;
  const fromAnswerCell = content.querySelector(`.crf-answer-cell[data-field-id="${vf}"]`)?.closest('.crf-report-row');
  if (fromAnswerCell) return fromAnswerCell;
  const fromAnswerCol = content.querySelector(`.crf-answer-col [data-field-id="${vf}"]`)?.closest?.('.crf-report-row');
  if (fromAnswerCol) return fromAnswerCol;
  const idAnchors = [
    `textarea_${vf}`,
    `textbox_${vf}`,
    `combobox_${vf}`,
    `datetime_${vf}`,
    `time_${vf}`,
    `file_${vf}`,
    `formula_${vf}`,
    `checkbox_${vf}`,
    `reportfield_${vf}_answer`,
  ];
  for (const aid of idAnchors) {
    const el = content.querySelector(`[id="${aid}"]`);
    const row = el?.closest?.('.crf-report-row');
    if (row) return row;
  }
  const radioEl = content.querySelector(`input[type="radio"][name="radio_${vf}"], input[type="radio"][name^="radio_${vf}_"]`);
  const radioRow = radioEl?.closest?.('.crf-report-row');
  if (radioRow) return radioRow;
  const stdEl = content.querySelector(`input.standarddate-text[id*="${CSS.escape(vf)}"], input.standarddatetime-text[id*="${CSS.escape(vf)}"]`);
  const stdRow = stdEl?.closest?.('.crf-report-row');
  if (stdRow) return stdRow;
  return null;
}

/**
 * Same as collectFormData fourth pass: dropdowns first, then hidden composite. Returns '' for placeholder-only.
 */
function computeStandardDateFieldValueForSave(content, el, formatStd, formatStdDt) {
  const baseId = el.id || '';
  let value = '';
  const isDateTime = el.classList.contains('standarddatetime-text');
  if (baseId) {
    const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
    const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
    const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
    const dayText = (dateSel?.options[dateSel.selectedIndex]?.text ?? '').trim() || 'DD';
    const monthText = (monthSel?.options[monthSel.selectedIndex]?.text ?? '').trim() || 'MMM';
    const yearText = (yearSel?.options[yearSel.selectedIndex]?.text ?? '').trim() || 'YYYY';
    value = `${dayText}-${monthText}-${yearText}`;
    if (isDateTime) {
      const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
      const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
      const hourText = (hourSel?.options[hourSel?.selectedIndex]?.text ?? '').trim() || 'HH';
      const minuteText = (minuteSel?.options[minuteSel?.selectedIndex]?.text ?? '').trim() || 'MM';
      value += ` ${hourText}:${minuteText}`;
    }
  }
  if (!value) {
    const rawValue = (el.value || '').trim();
    if (rawValue) {
      const alreadySavedFormat = isDateTime
        ? /^(.+)-(.+)-(.+)\s+(.+):(.+)$/.test(rawValue)
        : /^(.+)-(.+)-(.+)$/.test(rawValue);
      value = alreadySavedFormat ? rawValue : (isDateTime ? formatStdDt(rawValue) : formatStd(rawValue));
    }
  }
  if (isPlaceholderStandardDateValue(value)) return '';
  return value;
}

// Inline SVG for CRF row action buttons – use currentColor so CSS can set system primary
const crfActionIconSvg = (name) => {
  const size = 18;
  const stroke = 'currentColor';
  const icons = {
    // Action Items: checklist (check-square) – system primary
    actionitem: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 11 12 14 22 4"></polyline><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"></path></svg>`,
    // Create Action Item: clipboard with plus (single color – system primary)
    plus: `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${stroke}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><line x1="15" y1="17" x2="19" y2="17"/><line x1="17" y1="15" x2="17" y2="19"/></svg>`,
  };
  return icons[name] || '';
};

const CRFForm = (props = {}) => {
  const { user } = useContext(AuthContext);
  const { nRecordedBy: sessionRecordedByMappingId } = useSession();
  const { currentProject, projectInfo } = useProject();
  const auditProjectCode = currentProject && currentProject !== 'all' ? currentProject : undefined;
  const auditProjectId = projectInfo?.projectId ?? projectInfo?.ProjectId;
  const params = useParams();
  const [searchParams] = useSearchParams();
  const templateId = props.templateId ?? params.templateId ?? '';
  const visitId = props.visitId != null ? String(props.visitId) : (params.visitId ?? '');
  const isPreviewInNewTab = searchParams.get('preview') === '1' || (typeof window !== 'undefined' && window.opener && !visitId);
  const visitType = props.visitType ?? params.visitType ?? 'SSV';
  const reportNameFromQuery = (searchParams.get('reportName') || '').trim();
  const reportTitle = reportNameFromQuery || reportTitleByVisitType[visitType] || 'Visit Report';
  const onClose = props.onClose;
  const onDraftSaved = props.onDraftSaved; // (visitId, nCRFHdrNo) => parent can show "Continue" instead of "Start Report"
  const onSubmitSuccess = props.onSubmitSuccess; // optional: parent shows toast after modal closes on Submit
  const onApproveSendbackSuccess = props.onApproveSendbackSuccess; // optional: parent shows "Saved Successfully" when Approve/Send Back succeeds (so toast persists after modal closes)
  const onDcfSaveSuccess = props.onDcfSaveSuccess; // optional: parent toast when DCF resolve/response saves (CRFForm unmounts on onClose — child toast would disappear)
  const embedInModal = props.embedInModal === true;
  const stageVisitsForActionItem = props.stageVisits ?? [];
  const defaultProjectForActionItem = props.defaultProject ?? null;
  const defaultSiteIdForActionItem = props.defaultSiteId ?? '';
  const defaultSiteNameForActionItem = props.defaultSiteName ?? '';
  const onActionItemCreated = props.onActionItemCreated; // optional callback when Create Action Item is saved
  const [resolvedTemplateId, setResolvedTemplateId] = useState(null); // when parent opens without templateId (e.g. First Review), we resolve it from visit
  const [htmlContent, setHtmlContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nCRFHdrNo, setNCRFHdrNo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const [formData, setFormData] = useState({
    protocolNo: '',
    siteNumber: '',
    piName: '',
    visitDate: ''
  });
  const [savedFields, setSavedFields] = useState([]);
  const [crfStage, setCrfStage] = useState(null);
  const [hasOpenDCF, setHasOpenDCF] = useState(false);
  const [availableActions, setAvailableActions] = useState([]);
  const [transitioning, setTransitioning] = useState(false);
  const [dcfModalOpen, setDcfModalOpen] = useState(false);
  const [dcfSelected, setDcfSelected] = useState({ nCRFDtlNo: null, fieldId: '', questionText: '' });
  const [auditModalOpen, setAuditModalOpen] = useState(false);
  const [auditSelected, setAuditSelected] = useState({ fieldId: '', questionText: '', nCRFDtlNo: null });
  const [auditTrailList, setAuditTrailList] = useState([]);
  const [auditTrailLoading, setAuditTrailLoading] = useState(false);
  const [auditTrailError, setAuditTrailError] = useState(null);
  const [actionItemsModalOpen, setActionItemsModalOpen] = useState(false);
  const [actionItemsSelected, setActionItemsSelected] = useState({ nCRFDtlNo: null, fieldId: '', questionText: '' });
  const [actionItemsList, setActionItemsList] = useState([]);
  const [actionItemsLoading, setActionItemsLoading] = useState(false);
  const [actionItemClosingId, setActionItemClosingId] = useState(null);
  const [isCreateActionItemModalOpen, setIsCreateActionItemModalOpen] = useState(false);
  const [createActionItemFieldCode, setCreateActionItemFieldCode] = useState(null); // when opened from row, pre-select this section attribute
  const [createActionItemNCRFDtlNo, setCreateActionItemNCRFDtlNo] = useState(null); // when opened from row, used to resolve vFieldId for section-attribute matching
  const [selectedActionItemIndex, setSelectedActionItemIndex] = useState(0);
  const [actionDescriptionEdit, setActionDescriptionEdit] = useState('');
  const [actionReplyRemark, setActionReplyRemark] = useState('');
  const [statusEdit, setStatusEdit] = useState('Closed');
  const [actionItemSaving, setActionItemSaving] = useState(false);
  const [dcfRemark, setDcfRemark] = useState('');
  const [dcfRemarkError, setDcfRemarkError] = useState('');
  const [dcfResponse, setDcfResponse] = useState(''); // CRA response (vDCF Response) when not editing the field
  const [dcfSubmitting, setDcfSubmitting] = useState(false);
  const [dcfHistory, setDcfHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showESignModal, setShowESignModal] = useState(false);
  const [eSignPendingAction, setESignPendingAction] = useState(null);
  const [toast, setToast] = useState(null);
  const eSignRemarksRef = useRef('');
  const actionItemCloseRef = useRef(null);
  const actionItemUpdateRef = useRef(null);
  const [isViewOnly, setIsViewOnly] = useState(false);
  const [canApproveReject, setCanApproveReject] = useState(false);
  const [canRaiseDCFFromServer, setCanRaiseDCFFromServer] = useState(false);
  // Increment after DCF refetch so useLayoutEffect re-applies field enable/disable without page refresh
  const [permissionsRefreshKey, setPermissionsRefreshKey] = useState(0);
  // CRF Report Preview: choice modal (With/Without) then display modal
  const [showPreviewChoiceModal, setShowPreviewChoiceModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  /** When true, preview/PDF omits workflow signer lines (Submit / First / Second Review) — blank template only. */
  const [previewOmitWorkflowSignerFields, setPreviewOmitWorkflowSignerFields] = useState(false);
  const [includeOpenIssuesInPreview, setIncludeOpenIssuesInPreview] = useState(false);
  const [openIssuesForReport, setOpenIssuesForReport] = useState([]);
  const [openDcfsForReport, setOpenDcfsForReport] = useState([]);
  const [openIssuesLoading, setOpenIssuesLoading] = useState(false);
  /** Site id for visit-report appendix (from visit API or props.defaultSiteId). */
  const [resolvedSiteIdForAppendix, setResolvedSiteIdForAppendix] = useState(null);
  const previewContentRef = useRef(null);
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [requiredFieldCodes, setRequiredFieldCodes] = useState([]);
  const [requiredFieldErrors, setRequiredFieldErrors] = useState([]);
  const [workflowHeader, setWorkflowHeader] = useState(null);
  const [exportedOnDisplay, setExportedOnDisplay] = useState(() => formatDateTimeDDMMMYYYY(new Date()));

  const effectiveTemplateId = templateId || resolvedTemplateId || '';
  const displayedWorkflowDateTime = React.useMemo(() => {
    const dt = workflowHeader?.dateTimeUTC || workflowHeader?.DateTimeUTC;
    const offset = workflowHeader?.dateTimeOffset || workflowHeader?.DateTimeOffset;
    if (!dt) return '';
    return formatPerformedOnUtcToLocal(dt, offset) || '';
  }, [workflowHeader]);

  // Current user profile and CRA flag (used for DCF modal and field editability; must be defined before any return)
  const currentUserProfile = (user?.profileCode ?? user?.vUserTypeCode ?? user?.role ?? '').toString().trim();
  const isCraUser = (currentUserProfile || '').toUpperCase() === 'CRA';

  // Fetch profile permissions from server (IsViewOnly, iApproveSendback, CanRaiseDCF = reviewer-only)
  useEffect(() => {
    let cancelled = false;
    api.get('/users/me/profile-permissions')
      .then((res) => {
        if (!cancelled && res.data) {
          if (res.data.isViewOnly === 1 || res.data.IsViewOnly === 1) setIsViewOnly(true);
          if (res.data.iApproveSendback === 1 || res.data.iApproveSendback === '1') setCanApproveReject(true);
          if (res.data.canRaiseDCF === 1 || res.data.CanRaiseDCF === 1) setCanRaiseDCFFromServer(true);
        }
      })
      .catch(() => { });
    return () => { cancelled = true; };
  }, []);

  const fieldIdToDtlNo = React.useMemo(() => {
    const map = {};
    const toShortCode = (s) => {
      if (!s || s.length <= 5) return s;
      const parts = s.split('_');
      const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
      if (five) return five;
      const last = s.replace(/_answer$/i, '').replace(/_comments$/i, '').split('_').pop();
      return last && last.length <= 5 && /^[A-Za-z0-9]+$/.test(last) ? last : s.substring(0, 5);
    };
    (savedFields || []).forEach((f) => {
      const id = (f.vFieldId || f.VFieldId || '').trim();
      const no = f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo;
      if (id && no != null) {
        map[id] = Number(no);
        const short = toShortCode(id);
        if (short && short !== id) map[short] = Number(no);
      }
    });
    return map;
  }, [savedFields]);

  // Memoize so React does not re-apply innerHTML on every re-render (e.g. when opening DCF/Audit modals).
  // Re-applying would wipe out user-entered answers and comments.
  const dangerousContent = React.useMemo(() => ({ __html: htmlContent }), [htmlContent]);

  // Container ref so we transform THIS instance's content only (fixes modal CRF when multiple .crf-generated-content exist)
  const crfContainerRef = useRef(null);
  // Only try once to auto-create detail rows when reviewer opens CRF with no saved details
  const hydratedForReviewRef = useRef(false);

  // Ref for DCF/Audit/ActionItems/CreateActionItem so transform can attach direct click handlers (works when CRF is inside nested modal)
  const crfActionHandlersRef = useRef({
    openDcf: () => { },
    openAudit: () => { },
    openActionItems: () => { },
    openCreateActionItem: () => { }
  });
  // Synchronous payload when opening Create Action Item so modal can read fieldCode/nCRFDtlNo even if state hasn't updated yet
  const createActionItemPayloadRef = useRef(null);
  crfActionHandlersRef.current = {
    openDcf: (payload) => {
      setDcfSelected({ nCRFDtlNo: payload?.nCRFDtlNo ?? null, fieldId: payload?.fieldId ?? '', questionText: payload?.questionText ?? '' });
      setDcfModalOpen(true);
    },
    openAudit: (payload) => {
      setAuditSelected({ fieldId: payload?.fieldId ?? '', questionText: payload?.questionText ?? '', nCRFDtlNo: payload?.nCRFDtlNo ?? null });
      setAuditModalOpen(true);
    },
    openActionItems: (payload) => {
      setActionItemsSelected({ nCRFDtlNo: payload?.nCRFDtlNo ?? null, fieldId: payload?.fieldId ?? '', questionText: payload?.questionText ?? '' });
      setActionItemsModalOpen(true);
    },
    openCreateActionItem: (payload) => {
      if (!nCRFHdrNo) {
        setToast({ type: 'error', message: actionItemMessages.pleaseDraftSaveFirst });
        return;
      }
      createActionItemPayloadRef.current = payload ? { fieldCode: payload.fieldCode ?? null, nCRFDtlNo: payload.nCRFDtlNo ?? null } : null;
      setCreateActionItemFieldCode(payload?.fieldCode ?? null);
      setCreateActionItemNCRFDtlNo(payload?.nCRFDtlNo ?? null);
      setIsCreateActionItemModalOpen(true);
    }
  };

  useEffect(() => {
    // Define toggleSubGroup function early so it's available when HTML is injected.
    // Use the clicked header's next sibling as the panel to toggle so each group only
    // toggles its own section (avoids duplicate IDs when same subGroup code exists in multiple groups).
    window.toggleSubGroup = function (e, subGroupCode) {
      const clickedElement = e && (e.target && e.target.closest ? e.target.closest('.subGroupDiv') : e) || e;

      if (!clickedElement) {
        console.warn('toggleSubGroup: clicked element not found');
        return;
      }

      // Panel to collapse is always the next sibling of the header (avoids wrong panel when IDs duplicate)
      let subGroupElement = clickedElement.nextElementSibling;
      if (!subGroupElement || !subGroupElement.classList.contains('templateSubGroup')) {
        subGroupElement = document.getElementById(subGroupCode);
      }
      if (!subGroupElement) {
        console.warn(`toggleSubGroup: SubGroup element not found: ${subGroupCode}`);
        return;
      }

      // Check current state - if it has 'show' class or is visible (display not 'none'), collapse it
      const isCurrentlyExpanded = subGroupElement.classList.contains('show') ||
        subGroupElement.style.display !== 'none';

      if (isCurrentlyExpanded) {
        // Collapse
        subGroupElement.classList.add('collapse');
        subGroupElement.classList.remove('show');
        subGroupElement.style.display = 'none';
      } else {
        // Expand
        subGroupElement.classList.remove('collapse');
        subGroupElement.classList.add('show');
        subGroupElement.style.display = 'block';
      }

      // Update toggle icon/arrow if it exists
      const toggleIcon = clickedElement.querySelector('.collapseToggle, .fa, [class*="chevron"], i');
      if (toggleIcon) {
        if (isCurrentlyExpanded) {
          toggleIcon.classList.remove('fa-chevron-up');
          toggleIcon.classList.add('fa-chevron-down');
        } else {
          toggleIcon.classList.remove('fa-chevron-down');
          toggleIcon.classList.add('fa-chevron-up');
        }
      }
    };

    // Backend-generated CRF HTML may call CalculateForTextbox on input blur; define a no-op if missing
    if (typeof window.CalculateForTextbox !== 'function') {
      window.CalculateForTextbox = function () {
        // No-op: prevents ReferenceError when injected HTML has onblur="CalculateForTextbox(...)"
      };
    }

    // Backend-generated CRF HTML may reference ddlAlerton (e.g. onchange); define if missing
    if (window.ddlAlerton === undefined) {
      window.ddlAlerton = function () {
        // No-op: prevents ReferenceError when injected HTML has onchange="ddlAlerton(...)"
      };
    }

    // Ramp: CRF Time control onblur calls AutoTimeConvert(this, this.value). Must be defined for Time/AsyncTime fields.
    if (typeof window.msgalert !== 'function') {
      window.msgalert = function (msg) {
        if (msg) window.alert(msg);
      };
    }
    if (typeof window.TimeConvert !== 'function') {
      window.TimeConvert = function (paramTime, txtElement) {
        if (!paramTime || typeof paramTime !== 'string') return true;
        const s = paramTime.trim();
        if (s.length === 0 || s.toUpperCase() === 'UK:UK') return true;
        const match = s.match(/^(\d{1,2}):?(\d{2})?\s*(AM|PM)?$/i);
        if (!match) return false;
        let h = parseInt(match[1], 10);
        const m = match[2] ? parseInt(match[2], 10) : 0;
        if (match[3]) {
          if (/^PM$/i.test(match[3]) && h < 12) h += 12;
          if (/^AM$/i.test(match[3]) && h === 12) h = 0;
        }
        if (h < 0 || h > 23 || m < 0 || m > 59) return false;
        if (txtElement && txtElement.value !== undefined) {
          txtElement.value = String(h).padStart(2, '0') + ':' + String(m).padStart(2, '0');
        }
        return true;
      };
    }
    if (typeof window.AutoTimeConvert !== 'function') {
      window.AutoTimeConvert = function (txtTime, paramTime) {
        if (paramTime && typeof paramTime === 'string' && paramTime.trim().length > 0) {
          if (paramTime.toUpperCase() === 'UK:UK') return;
          if (!/^[0-9:]*$/.test(paramTime)) {
            window.msgalert('Please enter time in correct format as HH:MM');
            if (txtTime && txtTime.value !== undefined) txtTime.value = '';
            return;
          }
        }
        const result = window.TimeConvert(paramTime, txtTime);
        if (result === false && txtTime && txtTime.value !== undefined) txtTime.value = '';
      };
    }

    // Other Ramp CRF globals referenced by generated HTML (no-ops to avoid ReferenceError)
    if (typeof window.ValidateTextbox !== 'function') {
      window.ValidateTextbox = function () { };
    }
    if (typeof window.Calculate !== 'function') {
      window.Calculate = function () { };
    }
    if (typeof window.LengthValidation !== 'function') {
      window.LengthValidation = function () { return true; };
    }
    if (typeof window.RemoveSelection !== 'function') {
      window.RemoveSelection = function (rblId) {
        const el = document.getElementById(rblId);
        if (el && el.type === 'radio') el.checked = false;
      };
    }
    if (typeof window.CheckStandardDate !== 'function') {
      window.CheckStandardDate = function (selectEl, idDate, idMonth, idYear) {
        if (!selectEl || !idDate || !idMonth || !idYear) return;
        const baseId = selectEl.id.replace(/_Date$|_Month$|_Year$/, '');
        const textInput = document.getElementById(baseId);
        const dateSel = document.getElementById(idDate);
        const monthSel = document.getElementById(idMonth);
        const yearSel = document.getElementById(idYear);
        if (!textInput || !dateSel || !monthSel || !yearSel) return;
        const dd = (dateSel.value || '').trim();
        const monthOpt = monthSel.options[monthSel.selectedIndex];
        const monthText = monthOpt ? monthOpt.text.trim() : '';
        const yyyy = (yearSel.value || '').trim();
        textInput.value = dd + monthText + yyyy;
      };
    }
    if (typeof window.CheckStandardDateTime !== 'function') {
      window.CheckStandardDateTime = function (selectEl, idDate, idMonth, idYear, idHour, idMinute) {
        if (!selectEl || !idDate || !idMonth || !idYear || !idHour || !idMinute) return;
        const baseId = selectEl.id.replace(/_Date$|_Month$|_Year$|_Hour$|_Minute$/, '');
        const hiddenInput = document.getElementById(baseId);
        const dateSel = document.getElementById(idDate);
        const monthSel = document.getElementById(idMonth);
        const yearSel = document.getElementById(idYear);
        const hourSel = document.getElementById(idHour);
        const minuteSel = document.getElementById(idMinute);
        if (!hiddenInput || !dateSel || !monthSel || !yearSel || !hourSel || !minuteSel) return;
        const dd = (dateSel.value || '').trim();
        const monthOpt = monthSel.options[monthSel.selectedIndex];
        const monthText = monthOpt ? monthOpt.text.trim() : '';
        const yyyy = (yearSel.value || '').trim();
        const hh = (hourSel.value || '').trim();
        const mm = (minuteSel.value || '').trim();
        hiddenInput.value = dd + monthText + yyyy + hh + mm;
      };
    }

    if (effectiveTemplateId) {
      loadCRFTemplate();
    }

    // Cleanup: remove CRF globals so they don't leak when navigating away
    return () => {
      const crfGlobals = ['toggleSubGroup', 'CalculateForTextbox', 'ddlAlerton', 'msgalert', 'TimeConvert', 'AutoTimeConvert', 'ValidateTextbox', 'Calculate', 'LengthValidation', 'RemoveSelection', 'CheckStandardDate', 'CheckStandardDateTime'];
      crfGlobals.forEach((k) => { if (window[k] !== undefined) delete window[k]; });
    };
  }, [effectiveTemplateId, visitId]);

  // When opening from First Review (or similar), parent may not have templateId yet; resolve it from visit so loadCRFTemplate runs
  useEffect(() => {
    if (templateId || !visitId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await visitService.getAssignedCRFTemplate(visitId);
        if (cancelled) return;
        const id = getAnyCase(data, 'templateId', 'TemplateId', 'templateID', 'templateNo', 'TemplateNo', 'vTemplateNo', 'VTemplateNo');
        if (id != null) setResolvedTemplateId(String(id));
      } catch (err) {
        if (!cancelled) console.error('[CRFForm] Resolve template for visit failed', err);
      }
    })();
    return () => { cancelled = true; };
  }, [templateId, visitId]);

  // When no CRF header yet (404 / Start Report), CRA still needs Draft Save and Submit so set default actions
  useEffect(() => {
    if (visitId && effectiveTemplateId && !nCRFHdrNo) {
      setAvailableActions(['DraftSave', 'Submit']);
      setCrfStage('In Progress');
    }
  }, [visitId, effectiveTemplateId, nCRFHdrNo]);

  // Fetch CRF stage and available actions when we have a header (for Approve/Reject buttons)
  useEffect(() => {
    if (!nCRFHdrNo) return;
    let cancelled = false;
    const fetchActions = async () => {
      try {
        const res = await api.get(`/crf/page/${nCRFHdrNo}/actions`);
        if (cancelled) return;
        const stage = res.data?.stage ?? res.data?.Stage ?? null;
        if (stage) setCrfStage(stage);
        setHasOpenDCF(res.data?.hasOpenDCF ?? res.data?.HasOpenDCF ?? false);
        const actions = res.data?.actions ?? res.data?.Actions;
        setAvailableActions(Array.isArray(actions) ? actions : []);
      } catch (err) {
        if (!cancelled) {
          setAvailableActions([]);
        }
      }
    };
    fetchActions();
    return () => { cancelled = true; };
  }, [nCRFHdrNo]);

  const populateFormFromSavedData = React.useCallback((content) => {
    const result = { populatedCount: 0, totalWithValue: 0, success: true };
    if (!savedFields?.length) return result;
    if (!content) return result;

    const popCommentsTaSel = CRF_COMMENTS_TEXTAREA_SEL;
    const isCommentColEl = isCrfCommentRegionElement;

    // Helper: set one select by matching option text or value (for Day/Month/Year/Hour/Minute)
    const setSelectByTextOrValue = (sel, val, matchByText = true) => {
      if (!sel || !val) return;
      const v = String(val).trim();
      for (let i = 0; i < sel.options.length; i++) {
        const opt = sel.options[i];
        if ((matchByText && (opt.text || '').trim().toUpperCase() === v.toUpperCase()) || opt.value === v) {
          sel.value = opt.value;
          return;
        }
      }
      if (v) sel.value = v; // fallback by value only
    };

    savedFields.forEach((f) => {
      const vFieldId = (f.vFieldId || f.VFieldId || '').trim();
      if (!vFieldId) return;
      const value = f.vFieldValue ?? f.VFieldValue ?? '';
      const comment = f.vComment ?? f.VComment ?? '';
      if (value || comment) result.totalWithValue++;

      const valStr = String(value || '').trim();
      // Match StandardDate (DD-MMM-YYYY, month letters only) and StandardDateTime — do not treat YYYY-MM-DD (HTML5 date) as StandardDate
      const isStandardDateTime = /^([^-]+)-([A-Za-z]{3})-([^\s-]+)\s+([^:]+):([^:]+)$/.test(valStr);
      const isStandardDate = /^([^-]+)-([A-Za-z]{3})-([^\s-]+)$/.test(valStr) && !isStandardDateTime;

      // StandardDate / StandardDateTime: hidden composite + part dropdowns (never bind generic select[id*=] — empty draft would set DD select to '' and show blank)
      const hiddenDateTimeInput = content.querySelector(`input.standarddatetime-text[id*="${vFieldId}"]`);
      const hiddenDateInput = content.querySelector(`input.standarddate-text[id*="${vFieldId}"]`);
      const hiddenStdInput = hiddenDateTimeInput || hiddenDateInput;
      if (hiddenStdInput) {
        const isDateTimeField = !!hiddenDateTimeInput;
        if (valStr && (isStandardDate || isStandardDateTime)) {
          hiddenStdInput.value = valStr;
          const baseId = hiddenStdInput.id;
          if (baseId) {
            const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
            const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
            const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
            const parts = valStr.match(isStandardDateTime ? /^([^-]+)-([^-]+)-([^\s-]+)\s+([^:]+):([^:]+)$/ : /^([^-]+)-([^-]+)-([^\s-]+)$/);
            const dd = parts ? parts[1].trim() : '';
            const mmm = parts ? parts[2].trim() : '';
            const yyyy = parts ? parts[3].trim() : '';
            if (dateSel && dd) setSelectByTextOrValue(dateSel, dd);
            if (monthSel && mmm) setSelectByTextOrValue(monthSel, mmm, true);
            if (yearSel && yyyy) {
              // Year option for UKUK/UNK has value "0000" in backend; match by text UKUK or UNK, then set by option value
              const yyyyUpper = yyyy.toUpperCase();
              const opt = Array.from(yearSel.options).find(o => {
                const t = (o.text || '').trim().toUpperCase();
                const v = (o.value || '').trim();
                return t === yyyyUpper || v === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || v === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || v === '0000'));
              });
              if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
            }
            if (isDateTimeField && parts && parts.length >= 5) {
              const hh = parts[4].trim();
              const mm = parts[5].trim();
              const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
              const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
              if (hourSel && hh) setSelectByTextOrValue(hourSel, hh);
              if (minuteSel && mm) setSelectByTextOrValue(minuteSel, mm);
            }
          }
        } else if (!valStr) {
          hiddenStdInput.value = '';
          const baseId = hiddenStdInput.id;
          if (baseId) {
            const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
            const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
            const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
            if (dateSel) setSelectByTextOrValue(dateSel, 'DD');
            if (monthSel) setSelectByTextOrValue(monthSel, 'MMM');
            if (yearSel) setSelectByTextOrValue(yearSel, 'YYYY');
            if (isDateTimeField) {
              const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
              const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
              if (hourSel) setSelectByTextOrValue(hourSel, 'HH');
              if (minuteSel) setSelectByTextOrValue(minuteSel, 'MM');
            }
          }
        } else {
          hiddenStdInput.value = valStr;
        }
        result.populatedCount++;
        const row = hiddenStdInput?.closest?.('.crf-report-row') || findCrfReportRowForFieldId(content, vFieldId);
        const commentsEl = (row ? row.querySelector(popCommentsTaSel) : null) ||
          content.querySelector(`[id="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textbox_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[id$="_comments"][id*="${vFieldId}"]`);
        if (commentsEl) {
          commentsEl.value = comment;
          if (comment) result.populatedCount++;
        }
        return; // skip generic input handling for this field
      }

      const input = content.querySelector(`.crf-answer-cell [data-field-id="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-col [data-field-id="${vFieldId}"]`) ||
        content.querySelector(`[data-field-id="${vFieldId}"]`) ||
        content.querySelector(`[id="reportfield_${vFieldId}_answer"]`) ||
        content.querySelector(`[id="textbox_${vFieldId}"]`) ||
        content.querySelector(`[id="textarea_${vFieldId}"]`) ||
        content.querySelector(`[id="combobox_${vFieldId}"]`) ||
        content.querySelector(`[id="datetime_${vFieldId}"]`) ||
        content.querySelector(`[id="time_${vFieldId}"]`) ||
        content.querySelector(`[id="file_${vFieldId}"]`) ||
        content.querySelector(`[name="reportfield_${vFieldId}_answer"]`) ||
        content.querySelector(`[name="textbox_${vFieldId}"]`) ||
        content.querySelector(`[name="textarea_${vFieldId}"]`) ||
        content.querySelector(`input[name="radio_${vFieldId}"], input[name^="radio_${vFieldId}_"]`) ||
        content.querySelector(`input[id^="radio_${vFieldId}_"], input[id="radio_${vFieldId}"]`) ||
        content.querySelector(`input[name="checkbox_${vFieldId}"], input[name^="checkbox_${vFieldId}_"]`) ||
        content.querySelector(`input[id="checkbox_${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[name="radio_${vFieldId}"], .crf-answer-col input[name="radio_${vFieldId}"], .crf-answer-cell input[name^="radio_${vFieldId}_"], .crf-answer-col input[name^="radio_${vFieldId}_"]`) ||
        content.querySelector(`.crf-answer-cell input[name="checkbox_${vFieldId}"], .crf-answer-col input[name="checkbox_${vFieldId}"], .crf-answer-cell input[name^="checkbox_${vFieldId}_"], .crf-answer-col input[name^="checkbox_${vFieldId}_"]`) ||
        content.querySelector(`.crf-answer-cell input[id*="${vFieldId}"], .crf-answer-cell select[id*="${vFieldId}"], .crf-answer-cell textarea[id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-col input[id*="${vFieldId}"], .crf-answer-col select[id*="${vFieldId}"], .crf-answer-col textarea[id*="${vFieldId}"]`) ||
        content.querySelector(`[id$="_answer"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="file"][id*="${vFieldId}"], .crf-answer-col input[type="file"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="date"][id*="${vFieldId}"], .crf-answer-col input[type="date"][id*="${vFieldId}"]`) ||
        content.querySelector(`.crf-answer-cell input[type="datetime-local"][id*="${vFieldId}"], .crf-answer-col input[type="datetime-local"][id*="${vFieldId}"]`) ||
        content.querySelector(`input[id*="${vFieldId}"][id$="_answer"]:not([id*="_comments"]), select[id*="${vFieldId}"], textarea[id*="${vFieldId}"]:not([id*="_comments"])`) ||
        content.querySelector(`input[name*="${vFieldId}"]:not([name*="_comments"]), textarea[name*="${vFieldId}"]:not([name*="_comments"])`) ||
        content.querySelector(`input[type="date"][id*="${vFieldId}"]`) ||
        content.querySelector(`input[type="datetime-local"][id*="${vFieldId}"]`);
      // Use column/DOM like collectFormData: answer widgets in Answer column may legitimately have ids ending in _comments.
      if (input && !isCommentColEl(input)) {
        if (input.type === 'checkbox') {
          // RAMP-style: saved value can be comma-separated list of checked option values
          const savedStr = String(value || '').trim();
          const savedValues = savedStr ? savedStr.split(',').map((v) => v.trim()).filter(Boolean) : [];
          const checkboxes = content.querySelectorAll(`input[type="checkbox"][name="${input.name}"]`);
          if (checkboxes.length > 1) {
            checkboxes.forEach((cb) => {
              const optionValue = (cb.value || '').trim();
              cb.checked = optionValue ? savedValues.includes(optionValue) : /^(yes|true|1|y)$/i.test(savedStr);
            });
          } else {
            input.checked = savedValues.length > 0
              ? savedValues.includes((input.value || '').trim()) || /^(yes|true|1|y)$/i.test(savedStr)
              : false;
          }
          result.populatedCount++;
        } else if (input.type === 'radio') {
          const radios = content.querySelectorAll(`input[name="${input.name}"]`);
          radios.forEach((r) => { r.checked = r.value === value; });
          result.populatedCount++;
        } else if (input.type === 'file') {
          // Browsers do not allow setting input.value on file inputs; show saved path as clickable link to open PDF
          if (value && String(value).trim()) {
            const pathValue = String(value).trim();
            const filename = (pathValue.split(/[/\\]/).pop() || pathValue).trim();
            input.setAttribute('data-saved-filename', filename);
            input.setAttribute('data-uploaded-path', pathValue);
            const answerCell = input.closest(CRF_ANSWER_REGION_SEL);
            const fileNamesEl = answerCell?.querySelector('.file-names-list') ||
              (input.id && content.querySelector(`[id="${input.id}_fileNames"]`)) ||
              (input.id && content.querySelector(`#${CSS.escape(input.id)}_fileNames`));
            const isServerPath = /^[A-Za-z0-9_-]+(\/[^\\]*)?$/i.test(pathValue) && (pathValue.startsWith('CRFUploads') || pathValue.includes('/'));
            const base = (typeof window !== 'undefined' && (sessionStorage.getItem('ctms_api_base_url') || '')) || '';
            const baseNorm = base.replace(/\/$/, '');
            const crfFilePath = baseNorm.toLowerCase().endsWith('/api') ? `${baseNorm}/crf/file` : `${baseNorm || ''}/api/crf/file`;
            const fileUrl = isServerPath ? `${crfFilePath}?path=${encodeURIComponent(pathValue)}` : null;
            const displayText = fileUrl ? '' : `Uploaded: ${filename}`;
            if (fileNamesEl) {
              fileNamesEl.classList.add('crf-saved-filename');
              if (fileUrl) {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                link.textContent = displayText + (filename ? ` (${filename})` : '');
                link.style.marginTop = '4px';
                link.style.display = 'inline-block';
                fileNamesEl.innerHTML = '';
                fileNamesEl.appendChild(link);
              } else {
                fileNamesEl.textContent = `Uploaded: ${filename}`;
              }
            } else {
              let span = answerCell?.querySelector('.crf-saved-filename');
              if (!span && answerCell) {
                span = document.createElement('span');
                span.className = 'crf-saved-filename';
                span.style.display = 'block';
                span.style.marginTop = '4px';
                input.parentNode?.appendChild(span);
              }
              if (span) {
                if (fileUrl) {
                  const link = document.createElement('a');
                  link.href = fileUrl;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.textContent = displayText + (filename ? ` (${filename})` : '');
                  span.innerHTML = '';
                  span.appendChild(link);
                } else {
                  span.textContent = `Uploaded: ${filename}`;
                }
              }
            }
            result.populatedCount++;
          }
        } else if (
          input.tagName === 'SELECT' &&
          (input.hasAttribute('data-standarddate-part') || input.hasAttribute('data-standarddatetime-part'))
        ) {
          // Composite date fields use hidden input + part selects; do not assign '' to DD/MMM (invalid option → blank UI)
          result.populatedCount++;
        } else {
          input.value = value;
          result.populatedCount++;
        }
        const row = input.closest('.crf-report-row');
        const commentsEl = (row ? row.querySelector(popCommentsTaSel) : null) ||
          content.querySelector(`[id="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textbox_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[id$="_comments"][id*="${vFieldId}"]`);
        if (commentsEl) {
          commentsEl.value = comment;
          if (comment) result.populatedCount++;
        }
      } else {
        const row = findCrfReportRowForFieldId(content, vFieldId);
        const commentsEl = (row ? row.querySelector(popCommentsTaSel) : null) ||
          content.querySelector(`[id="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textbox_${vFieldId}_comments"]`) ||
          content.querySelector(`[id="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="reportfield_${vFieldId}_comments"]`) ||
          content.querySelector(`[name="textarea_${vFieldId}_comments"]`) ||
          content.querySelector(`[id$="_comments"][id*="${vFieldId}"]`);
        if (commentsEl) {
          commentsEl.value = comment;
          if (comment) result.populatedCount++;
        }
      }
    });
    if (result.totalWithValue > 0 && result.populatedCount === 0) {
      console.warn('[CRFForm] Saved data could not be populated - no matching inputs found for field IDs:', savedFields.map((f) => f.vFieldId || f.VFieldId));
      result.success = false;
    } else if (result.totalWithValue > 0 && result.populatedCount < result.totalWithValue) {
      console.warn('[CRFForm] Partially populated:', result.populatedCount, 'of', result.totalWithValue, 'saved fields');
    }
    return result;
  }, [savedFields]);

  const isReviewStage = crfStage === 'First Review' || crfStage === 'Second Review';
  // Reviewer profiles (from server CanRaiseDCF): show Reviewer DCF view; only they see Raise DCF comment box and button. CRA does not.
  const showReviewerDcfView = isReviewStage || canRaiseDCFFromServer;

  const setFieldsEnabled = React.useCallback((content, enabled) => {
    if (!content) return;
    const inputs = content.querySelectorAll('input:not([type="hidden"]), select, textarea');
    inputs.forEach((el) => {
      if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
      // STS Auto Capture fields are always non-editable (value is system-populated).
      if (el.getAttribute?.('data-sts-autocapture') === 'true') {
        el.setAttribute('disabled', 'disabled');
        el.setAttribute('readonly', 'readonly');
        return;
      }
      if (enabled) {
        el.removeAttribute('disabled');
        el.removeAttribute('readonly');
      } else {
        el.setAttribute('disabled', 'disabled');
        el.setAttribute('readonly', 'readonly');
      }
    });
  }, []);

  // Track which auto-capture elements have been executed for this component instance.
  const stsAutoCaptureExecutedRef = useRef(new Set());  // Clear execution cache whenever the report HTML is (re)bound.
  // This ensures Draft reopen refreshes the value (e.g. 7 -> 8),
  // while the stage/action gating still prevents calls after Submit.
  useEffect(() => {
    stsAutoCaptureExecutedRef.current = new Set();
  }, [htmlContent, nCRFHdrNo]);

  const handleClearForm = () => {
    setSaveError(null);
    const content = crfContainerRef.current;
    if (content) {
      const inputs = content.querySelectorAll('input:not([type="hidden"]), select, textarea');
      inputs.forEach((el) => {
        if (el.closest?.('.crf-row-actions, [data-crf-action]')) return;
        if (el.disabled || el.readOnly) return;
        if (el.type === 'file') {
          el.removeAttribute('data-saved-filename');
          el.removeAttribute('data-uploaded-path');
          el.value = '';
          const answerCell = el.closest?.('.form-section-box, .crf-report-row, .row') || el.parentElement;
          const fileNamesEl = answerCell?.querySelector('.file-names-list');
          const savedSpan = answerCell?.querySelector('.crf-saved-filename');
          [fileNamesEl, savedSpan].filter(Boolean).forEach((node) => {
            node.innerHTML = '';
            node.textContent = '';
          });
          return;
        }
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = false;
        } else if (el.tagName === 'SELECT') {
          // Reset dropdown to first option (e.g. "Select") instead of blank
          if (el.options.length > 0) {
            el.selectedIndex = 0;
          } else {
            el.value = '';
          }
        } else {
          el.value = '';
        }
      });
      // Clear any remaining file display nodes and file input state (e.g. outside the selector scope)
      content.querySelectorAll('.file-names-list, .crf-saved-filename').forEach((el) => {
        el.innerHTML = '';
        el.textContent = '';
      });
      content.querySelectorAll('input[type="file"]').forEach((input) => {
        input.removeAttribute('data-saved-filename');
        input.removeAttribute('data-uploaded-path');
        input.value = '';
      });
    }
  };

  // Build HTML for CRF Report Preview: clone current report content; optionally blank answers and comments (Without).
  // Renders radio/checkbox/select as proper read-only controls (not plain text).
  const getPreviewHtml = (blankAnswersAndComments) => {
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return '';
    const clone = content.cloneNode(true);
    // Remove action buttons (DCF, Audit, Action Items) so preview is clean
    clone.querySelectorAll('.col-sideBtns, .crf-row-actions, [data-crf-action]').forEach((el) => el.remove());
    // Remove Formula field Calc buttons in preview mode (read-only preview, no calculation)
    clone.querySelectorAll('button.btnCalc').forEach((el) => el.remove());
    // Remove CRA placeholder "—" in comment cells so preview shows actual comment (or empty), not "-" in every field
    clone.querySelectorAll('.crf-comments-placeholder').forEach((el) => el.remove());
    if (blankAnswersAndComments) {
      clone.querySelectorAll('input, select, textarea').forEach((el) => {
        if (el.type === 'checkbox' || el.type === 'radio') {
          el.checked = false;
          el.removeAttribute('checked');
        } else if (el.tagName === 'SELECT') {
          el.removeAttribute('value');
          let emptyOpt = Array.from(el.options).find((o) => (o.value || '').trim() === '');
          if (!emptyOpt) {
            const blank = document.createElement('option');
            blank.value = '';
            blank.textContent = ' ';
            el.insertBefore(blank, el.firstChild);
            emptyOpt = blank;
          }
          Array.from(el.options).forEach((o) => { o.selected = false; o.removeAttribute('selected'); });
          emptyOpt.selected = true;
          emptyOpt.setAttribute('selected', 'selected');
        } else {
          el.value = '';
          el.removeAttribute('value');
        }
      });
      clone.querySelectorAll('textarea').forEach((ta) => {
        ta.textContent = '';
        ta.value = '';
      });
      // Remove uploaded file URLs/links in blank preview so "Open file" is not visible
      clone.querySelectorAll('.file-names-list, .crf-saved-filename').forEach((el) => {
        el.remove();
      });
      clone.querySelectorAll('input[type="file"]').forEach((input) => {
        input.removeAttribute('data-saved-filename');
        input.removeAttribute('data-uploaded-path');
      });
    }
    const replaceWithDisplayDiv = (el, text, className = 'crf-preview-field-value') => {
      const div = document.createElement('div');
      div.className = className;
      div.textContent = text || '\u00A0';
      el.parentNode.replaceChild(div, el);
    };
    // Format StandardDate composite value (DDMMMYYYY e.g. 07FEB2016) to DD-MMM-YYYY (e.g. 07-Feb-2016) for preview
    const formatStandardDateForPreview = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      const m = s.match(/^(\d{2})([A-Za-z]{3})(\d{4})?$/);
      if (!m) return s;
      const [, dd, mmm, yyyy] = m;
      const monthTitle = mmm ? mmm.charAt(0).toUpperCase() + mmm.slice(1).toLowerCase() : mmm;
      return yyyy ? `${dd}-${monthTitle}-${yyyy}` : `${dd}${monthTitle}`;
    };
    // Format StandardDateTime composite (DDMMMYYYYHHmm) to "DD-MMM-YYYY HH:MM" for preview
    const formatStandardDateTimeForPreview = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      const m = s.match(/^(\d{2})([A-Za-z]{3})(\d{4})(\d{2})(\d{2})$/);
      if (!m) return s;
      const [, dd, mmm, yyyy, hh, mm] = m;
      const monthTitle = mmm ? mmm.charAt(0).toUpperCase() + mmm.slice(1).toLowerCase() : mmm;
      return `${dd}-${monthTitle}-${yyyy} ${hh}:${mm}`;
    };
    // Show dates in DD-MMM-YYYY in preview for YYYY-MM-DD inputs (e.g. 24-Feb-2026)
    const previewDateText = (raw) => {
      if (blankAnswersAndComments || !raw) return '';
      const s = (raw || '').trim();
      if (!s) return '';
      // YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss
      if (/^\d{4}-\d{2}-\d{2}(T|\s|$)/.test(s)) {
        const formatted = formatDateDDMMMYYYY(s);
        return formatted !== '—' ? formatted : s;
      }
      return s;
    };
    // 0) StandardDate: replace entire .standarddate-row with one read-only div (so we don't replace 1 input + 3 selects separately)
    clone.querySelectorAll('.standarddate-row').forEach((row) => {
      const textInput = row.querySelector('input.standarddate-text');
      let displayText = '';
      if (textInput && !blankAnswersAndComments) {
        const id = textInput.id;
        const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
        const value = (originalInput?.value || textInput.value || '').trim();
        displayText = formatStandardDateForPreview(value) || value;
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    // 0a) StandardDateTime: replace entire .standarddatetime-row with one read-only div
    clone.querySelectorAll('.standarddatetime-row').forEach((row) => {
      const hiddenInput = row.querySelector('input.standarddatetime-text');
      let displayText = '';
      if (hiddenInput && !blankAnswersAndComments) {
        const id = hiddenInput.id;
        const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
        const value = (originalInput?.value || hiddenInput.value || '').trim();
        displayText = formatStandardDateTimeForPreview(value) || value;
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    // 0a2) HTML5 date/datetime (Attribute Type DateTime): replace input[type="date"] and input[type="datetime-local"] with read-only div for PM Review
    clone.querySelectorAll('input[type="date"], input[type="datetime-local"]').forEach((input) => {
      const id = input.id;
      const originalInput = id ? content.querySelector(`#${CSS.escape(id)}`) : null;
      const value = (originalInput?.value || input.value || '').trim();
      const displayText = blankAnswersAndComments ? '' : (value ? previewDateText(value) || value : '\u00A0');
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      input.parentNode?.replaceChild(wrap, input);
    });
    // 0b) Fallback: old StandardDate HTML (no .standarddate-row, just three selects _Date, _Month, _Year) – replace their parent row with one div
    clone.querySelectorAll('select[id*="standarddate_"][id$="_Date"]').forEach((sel) => {
      if (sel.closest('.standarddate-row')) return;
      const row = sel.closest('.row');
      if (!row) return;
      const dateSel = row.querySelector('select[id$="_Date"]');
      const monthSel = row.querySelector('select[id$="_Month"]');
      const yearSel = row.querySelector('select[id$="_Year"]');
      if (!dateSel || !monthSel || !yearSel) return;
      let displayText = '';
      if (!blankAnswersAndComments) {
        const baseId = sel.id.replace(/_Date$/, '');
        const origDate = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const origMonth = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const origYear = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        const dd = (origDate?.value || dateSel.value || '').trim();
        const monthOpt = origMonth ? origMonth.options[origMonth.selectedIndex] : monthSel.options[monthSel.selectedIndex];
        const mmm = (monthOpt?.text || '').trim();
        const yyyy = (origYear?.value || yearSel.value || '').trim();
        if (dd || mmm || yyyy) displayText = formatStandardDateForPreview(dd + mmm + yyyy) || `${dd} ${mmm} ${yyyy}`.trim();
      }
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-field-value crf-preview-select-value';
      wrap.textContent = displayText || '\u00A0';
      row.parentNode.replaceChild(wrap, row);
    });
    // 1) Radio groups: replace entire .form-check-group with read-only radio-style preview (● selected, ○ unselected)
    clone.querySelectorAll('.form-check-group').forEach((group) => {
      const radios = group.querySelectorAll('input[type="radio"]');
      if (radios.length === 0) return;
      const options = Array.from(radios).map((r) => {
        const label = r.nextElementSibling?.textContent?.trim() || (r.value || '').trim() || 'Yes';
        return { value: (r.value || '').trim() || label, label, checked: r.checked };
      });
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-radio-group';
      options.forEach((opt) => {
        const span = document.createElement('span');
        span.className = 'crf-preview-radio-option' + (opt.checked && !blankAnswersAndComments ? ' crf-preview-radio-selected' : '');
        span.setAttribute('aria-selected', opt.checked && !blankAnswersAndComments ? 'true' : 'false');
        span.textContent = (opt.checked && !blankAnswersAndComments ? '\u25CF ' : '\u25CB ') + opt.label;
        wrap.appendChild(span);
        const sp = document.createElement('span');
        sp.className = 'crf-preview-radio-sep';
        sp.textContent = ' ';
        wrap.appendChild(sp);
      });
      wrap.lastChild?.remove(); // remove trailing space span
      group.parentNode.replaceChild(wrap, group);
    });
    // 2) Checkbox groups: replace entire .form-check-group with read-only checkbox-style preview (☑ checked, ☐ unchecked)
    clone.querySelectorAll('.form-check-group').forEach((group) => {
      const checkboxes = group.querySelectorAll('input[type="checkbox"]');
      if (checkboxes.length === 0) return;
      const options = Array.from(checkboxes).map((cb) => {
        const label = cb.nextElementSibling?.textContent?.trim() || (cb.value || '').trim() || 'Yes';
        return { value: (cb.value || '').trim() || label, label, checked: cb.checked };
      });
      const wrap = document.createElement('div');
      wrap.className = 'crf-preview-checkbox-group';
      options.forEach((opt) => {
        const span = document.createElement('span');
        span.className = 'crf-preview-checkbox-option' + (opt.checked && !blankAnswersAndComments ? ' crf-preview-checkbox-checked' : '');
        span.setAttribute('aria-checked', opt.checked && !blankAnswersAndComments ? 'true' : 'false');
        span.textContent = (opt.checked && !blankAnswersAndComments ? '\u2611 ' : '\u2610 ') + opt.label;
        wrap.appendChild(span);
        const sp = document.createElement('span');
        sp.className = 'crf-preview-checkbox-sep';
        sp.textContent = ' ';
        wrap.appendChild(sp);
      });
      wrap.lastChild?.remove();
      group.parentNode.replaceChild(wrap, group);
    });
    // 3) Select/Combo: replace with a div styled like a dropdown showing selected value.
    // Read selected value from the original content so we show the actual selection (clone can lose selectedIndex).
    clone.querySelectorAll('select').forEach((el) => {
      let text = '';
      if (!blankAnswersAndComments) {
        const selectId = el.id || el.name;
        const originalSelect = selectId ? content.querySelector(`select[id="${CSS.escape(selectId)}"], select[name="${CSS.escape(selectId)}"]`) : null;
        const source = originalSelect || el;
        const selectedOpt = Array.from(source.options).find((o) => o.selected) || source.options[source.selectedIndex];
        if (selectedOpt) {
          const raw = (selectedOpt.textContent || selectedOpt.text || selectedOpt.value || '').trim();
          // Don't show placeholder-only as value (e.g. "Select", empty)
          if (raw && raw.toLowerCase() !== 'select') text = raw;
          else if ((selectedOpt.value || '').trim()) text = (selectedOpt.value || '').trim();
        }
      }
      replaceWithDisplayDiv(el, text || '\u00A0', 'crf-preview-field-value crf-preview-select-value');
    });
    clone.querySelectorAll('textarea').forEach((el) => {
      const text = blankAnswersAndComments ? '' : (el.value || '').trim();
      replaceWithDisplayDiv(el, text);
    });
    clone.querySelectorAll('input').forEach((el) => {
      if (el.type === 'hidden') return;
      if (el.type === 'radio' || el.type === 'checkbox') return; // already replaced as groups
      let text = '';
      if (el.type === 'file') {
        if (!blankAnswersAndComments) {
          if (el.files && el.files[0]) {
            text = el.files[0].name;
          } else {
            const savedName = el.getAttribute('data-saved-filename');
            if (savedName) {
              text = savedName;
            } else {
              const cell = el.closest(CRF_ANSWER_REGION_SEL);
              const savedEl = cell?.querySelector('.crf-saved-filename, .file-names-list');
              const savedText = (savedEl?.textContent || '').trim().replace(/^Uploaded:\s*/i, '').trim();
              text = savedText || 'Choose File';
            }
          }
          if (!text) text = 'Choose File';
        } else {
          // Blank preview: show "Choose File" so user gets the idea, but control is replaced by read-only div
          text = 'Choose File';
        }
      } else if (!blankAnswersAndComments) {
        const raw = (el.value || '').trim();
        text = previewDateText(raw) || raw;
      }
      replaceWithDisplayDiv(el, text);
    });
    return clone.innerHTML;
  };

  const handlePreviewWith = () => {
    const html = getPreviewHtml(false);
    setPreviewHtml(html);
    setExportedOnDisplay(formatDateTimeDDMMMYYYY(new Date()));
    setPreviewOmitWorkflowSignerFields(false);
    setIncludeOpenIssuesInPreview(true);
    setShowPreviewChoiceModal(false);
    setShowPreviewModal(true);
  };

  const handlePreviewWithout = () => {
    const html = getPreviewHtml(true);
    setPreviewHtml(html);
    setExportedOnDisplay(formatDateTimeDDMMMYYYY(new Date()));
    setPreviewOmitWorkflowSignerFields(true);
    setIncludeOpenIssuesInPreview(false);
    setOpenIssuesForReport([]);
    setOpenDcfsForReport([]);
    setShowPreviewChoiceModal(false);
    setShowPreviewModal(true);
  };

  const handleDownloadPreviewPdf = () => {
    const element = previewContentRef.current;
    if (!element) return;
    setPdfDownloading(true);

    const margin = [10, 10, 14, 10];
    const filename = 'Visit-Report-Preview.pdf';

    (async () => {
      try {
        const pdf = new jsPDF({
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          hotfixes: ['px_scaling'],
        });
        const pageWidthMm = pdf.internal.pageSize.getWidth();
        const pageHeightMm = pdf.internal.pageSize.getHeight();
        const innerW = pageWidthMm - margin[1] - margin[3];
        const innerH = pageHeightMm - margin[0] - margin[2];
        const ratio = innerH / innerW;

        const rect = element.getBoundingClientRect();
        const cssW = Math.max(1, Math.ceil(element.scrollWidth || rect.width || 600));
        const cssH = Math.max(1, Math.ceil(element.scrollHeight || rect.height || 400));
        const sliceRefScale = 2;
        const sliceCssH = Math.max(
          1,
          Math.floor(Math.floor(cssW * sliceRefScale * ratio) / sliceRefScale),
        );
        const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
        const preferredScale = Math.min(3, Math.max(2, dpr));

        const slices = computeVisitReportPdfSlices(element, sliceCssH, cssH);
        let pageIndex = 0;
        const sliceEPS = 2;
        for (let s = 0; s < slices.length; s += 1) {
          const { y0, height: sliceH } = slices[s];
          const canvasScale = computeHtml2PdfCanvasScaleForRect(cssW, sliceH, preferredScale);

          const canvas = await html2canvas(element, {
            scale: canvasScale,
            x: 0,
            y: y0,
            width: cssW,
            height: sliceH,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            foreignObjectRendering: false,
          });

          if (!canvas.width || !canvas.height) {
            throw new Error('empty canvas');
          }

          const imgData = canvas.toDataURL('image/png');
          const pageHeightMmDraw = (canvas.height * innerW) / canvas.width;
          const isLastSlice = y0 + sliceH >= cssH - sliceEPS;
          const drawH = isLastSlice ? Math.min(pageHeightMmDraw, innerH) : innerH;

          if (pageIndex > 0) pdf.addPage();
          pdf.addImage(imgData, 'PNG', margin[1], margin[0], innerW, drawH);
          pageIndex += 1;
        }

        const totalPages = pdf.internal.getNumberOfPages();
        pdf.setFontSize(10);
        for (let i = 1; i <= totalPages; i += 1) {
          pdf.setPage(i);
          pdf.text(`Page No. ${i} of ${totalPages}`, margin[1], pageHeightMm - 5, { align: 'left' });
        }
        pdf.save(filename);
      } catch {
        setToast({ type: 'error', message: reportMessages.pdfGenerationFailed });
      } finally {
        setPdfDownloading(false);
      }
    })();
  };

  useEffect(() => {
    const sidFromProp = defaultSiteIdForActionItem != null && String(defaultSiteIdForActionItem).trim() !== ''
      ? Number(defaultSiteIdForActionItem)
      : null;
    if (sidFromProp != null && Number.isFinite(sidFromProp) && sidFromProp > 0) {
      setResolvedSiteIdForAppendix((prev) => (prev != null && prev > 0 ? prev : sidFromProp));
    }
  }, [defaultSiteIdForActionItem]);

  useEffect(() => {
    if (!showPreviewModal || !includeOpenIssuesInPreview || !visitId) {
      if (!includeOpenIssuesInPreview) {
        setOpenIssuesForReport([]);
        setOpenDcfsForReport([]);
      }
      setOpenIssuesLoading(false);
      return;
    }

    let siteId = resolvedSiteIdForAppendix != null && resolvedSiteIdForAppendix > 0 ? resolvedSiteIdForAppendix : null;
    if (siteId == null && defaultSiteIdForActionItem != null && String(defaultSiteIdForActionItem).trim() !== '') {
      const n = Number(defaultSiteIdForActionItem);
      if (Number.isFinite(n) && n > 0) siteId = n;
    }
    const projectCode = (formData.protocolNo || '').trim();
    if (!siteId || siteId <= 0 || !projectCode) {
      setOpenIssuesForReport([]);
      setOpenDcfsForReport([]);
      setOpenIssuesLoading(false);
      return;
    }

    let cancelled = false;
    setOpenIssuesLoading(true);
    const load = async () => {
      try {
        const res = await api.get('/crf/visit-report-site-appendix', {
          params: { siteId, projectCode },
        });
        if (cancelled) return;
        const data = res.data ?? {};
        setOpenIssuesForReport(Array.isArray(data.openIssues) ? data.openIssues : []);
        setOpenDcfsForReport(Array.isArray(data.openDcfs) ? data.openDcfs : []);
      } catch (_) {
        if (!cancelled) {
          setOpenIssuesForReport([]);
          setOpenDcfsForReport([]);
        }
      } finally {
        if (!cancelled) setOpenIssuesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showPreviewModal, includeOpenIssuesInPreview, visitId, resolvedSiteIdForAppendix, defaultSiteIdForActionItem, formData.protocolNo]);

  useLayoutEffect(() => {
    if (!htmlContent) return;

    let rafId;
    const wireBackendDcfAuditButtons = (c) => {
      if (!c) return;
      // Formula Calc buttons: replace inline onclick with data attributes so we can run calculation in React with correct content scope
      const calcBtns = c.querySelectorAll('button.btnCalc');
      calcBtns.forEach((btn) => {
        const onclick = (btn.getAttribute('onclick') || '').trim();
        const match = onclick.match(/Calculate\s*\(\s*true\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*\)\s*;?/);
        if (match) {
          const [, formula, formulaId, decimalNo] = match;
          btn.setAttribute('data-formula', formula || '');
          btn.setAttribute('data-formula-id', formulaId || '');
          btn.setAttribute('data-decimal-no', decimalNo != null ? String(decimalNo) : '0');
          btn.removeAttribute('onclick');
        }
        // Disable Calc button outside In Progress stage (e.g. First/Second Review and later stages)
        if (props.readOnly || props.reviewMode || isViewOnly) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
        } else {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
        }
      });

      // console.log('[CRFForm] Wiring DCF buttons. SavedFields:', savedFields.length); // Debug
      const dcfEls = c.querySelectorAll('[id*="DcfBtn"], [id*="dcfBtn"], [class*="DcfBtn"], [class*="dcfBtn"], [class*="fieldDcfBtn"], [data-crf-action="dcf"]');
      dcfEls.forEach((el) => {
        if (!el.hasAttribute('data-crf-action')) el.setAttribute('data-crf-action', 'dcf');
        el.setAttribute('title', 'Create DCF');

        // DCF field highlighting: yellow background when DCF is raised (Open), and keep yellow when Resolved or Closed so users see DCF-generated fields
        const row = el.closest('.crf-report-row');
        if (row) {
          let fieldId = '';
          const input = row.querySelector(CRF_ROW_ANSWER_CONTROLS_SEL);
          if (input) {
            fieldId = input.getAttribute('data-field-id') || '';
            if (!fieldId) {
              const rawId = input.id || input.name || '';
              fieldId = extractFieldCode(rawId);
            }
          }
          const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
          const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;

          // Lookup field: prefer nCRFDtlNo (reliable after refresh), then fieldId
          let field = null;
          if (nCRFDtlNoForRow != null && !isNaN(nCRFDtlNoForRow)) {
            field = (savedFields || []).find(f => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
          }
          if (!field && fieldId) {
            field = (savedFields || []).find(f => {
              const fId = (f.vFieldId || f.VFieldId || '').trim();
              return fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase();
            });
          }

          if (field) {
            const statusRaw = field.vDCFStatus ?? field.VDCFStatus ?? null;
            const status = (statusRaw || '').trim().toLowerCase();
            // Yellow only when DCF is Open or Resolved; remove yellow when Closed
            const hasDcf = status === 'open' || status === 'resolved';

            const resetStyles = (element) => {
              element.classList.remove('text-danger', 'text-warning', 'text-success', 'text-secondary');
              element.style.removeProperty('color');
              element.style.removeProperty('border-color');
              element.style.removeProperty('background-color');
            };

            resetStyles(el);
            const icon = el.querySelector('i');
            if (icon) resetStyles(icon);

            // Yellow background for answer input and comments cell when field has DCF (raised, resolved, or closed)
            const inputEl = row.querySelector('.crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea');
            const answerCell = row.querySelector('.crf-answer-cell, .crf-answer-col');
            const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
            const dcfYellowBg = '#fff9c4';
            const dcfYellowBorder = '#f0d000';

            if (hasDcf) {
              if (inputEl) {
                inputEl.style.backgroundColor = dcfYellowBg;
                inputEl.style.borderColor = dcfYellowBorder;
                inputEl.style.borderWidth = '1px';
              }
              if (answerCell) {
                answerCell.style.backgroundColor = dcfYellowBg;
              }
              if (commentsCell) {
                const commentsInput = commentsCell.querySelector('textarea, input');
                if (commentsInput) commentsInput.style.backgroundColor = dcfYellowBg;
              }
            } else {
              if (inputEl) {
                inputEl.style.removeProperty('background-color');
                inputEl.style.removeProperty('border-color');
                inputEl.style.removeProperty('border-width');
              }
              if (answerCell) answerCell.style.removeProperty('background-color');
              if (commentsCell) {
                const commentsInput = commentsCell.querySelector('textarea, input');
                if (commentsInput) commentsInput.style.removeProperty('background-color');
              }
            }

            if (status === 'open') {
              el.classList.add('text-danger');
              if (icon) icon.classList.add('text-danger');
              el.style.setProperty('color', '#dc3545', 'important');
              if (icon) icon.style.setProperty('color', '#dc3545', 'important');
            } else if (status === 'resolved') {
              el.classList.add('text-warning');
              if (icon) icon.classList.add('text-warning');
              el.style.setProperty('color', '#ffc107', 'important');
              if (icon) icon.style.setProperty('color', '#ffc107', 'important');
            } else if (status === 'closed') {
              el.classList.add('text-success');
              if (icon) icon.classList.add('text-success');
              el.style.setProperty('color', '#198754', 'important');
              if (icon) icon.style.setProperty('color', '#198754', 'important');
            }
          }
        }
      });

      // Apply yellow background to any row whose field has Open/Resolved DCF (not dependent on DCF button selector)
      const dcfYellowBg = '#fff9c4';
      const dcfYellowBorder = '#f0d000';
      const allRows = c.querySelectorAll('.crf-report-row');
      allRows.forEach((row) => {
        const rowDtlNo = row.getAttribute('data-n-crf-dtl-no');
        const nCRFDtlNoForRow = rowDtlNo != null && rowDtlNo !== '' ? Number(rowDtlNo) : null;
        let fieldId = '';
        const input = row.querySelector(CRF_ROW_ANSWER_CONTROLS_SEL);
        if (input) {
          fieldId = (input.getAttribute('data-field-id') || '').trim();
          if (!fieldId && (input.id || input.name)) fieldId = extractFieldCode(input.id || input.name);
        }
        let field = null;
        if (nCRFDtlNoForRow != null && !isNaN(nCRFDtlNoForRow)) {
          field = (savedFields || []).find(f => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo ?? 0) === nCRFDtlNoForRow);
        }
        if (!field && fieldId) {
          field = (savedFields || []).find(f => {
            const fId = (f.vFieldId || f.VFieldId || '').trim();
            return fId === fieldId || fId.toLowerCase() === fieldId.toLowerCase();
          });
        }
        if (field) {
          const statusRaw = field.vDCFStatus ?? field.VDCFStatus ?? null;
          const status = (statusRaw || '').trim().toLowerCase();
          const hasDcf = status === 'open' || status === 'resolved';
          const inputEl = row.querySelector('.crf-answer-cell input, .crf-answer-cell select, .crf-answer-cell textarea, .crf-answer-col input, .crf-answer-col select, .crf-answer-col textarea');
          const answerCell = row.querySelector('.crf-answer-cell, .crf-answer-col');
          const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
          if (hasDcf) {
            if (inputEl) {
              inputEl.style.setProperty('background-color', dcfYellowBg, 'important');
              inputEl.style.setProperty('border-color', dcfYellowBorder, 'important');
              inputEl.style.setProperty('border-width', '1px', 'important');
            }
            if (answerCell) answerCell.style.setProperty('background-color', dcfYellowBg, 'important');
            if (commentsCell) {
              const commentsInput = commentsCell.querySelector('textarea, input');
              if (commentsInput) commentsInput.style.setProperty('background-color', dcfYellowBg, 'important');
            }
          } else {
            if (inputEl) {
              inputEl.style.removeProperty('background-color');
              inputEl.style.removeProperty('border-color');
              inputEl.style.removeProperty('border-width');
            }
            if (answerCell) answerCell.style.removeProperty('background-color');
            if (commentsCell) {
              const commentsInput = commentsCell.querySelector('textarea, input');
              if (commentsInput) commentsInput.style.removeProperty('background-color');
            }
          }
        }
      });

      const auditEls = c.querySelectorAll('[id*="auditBtn"], [id*="AuditBtn"], [class*="auditBtn"], [class*="fieldauditBtn"]');
      auditEls.forEach((el) => { el.setAttribute('data-crf-action', 'audit'); el.setAttribute('title', 'View Audit'); });
    };

    // CRA with open DCF: all comments visible (no hyphen placeholder) but every comment box disabled/readonly including DCF-generated field
    const hideCommentsForNonDcfFieldsWhenCra = (content) => {
      if (!content) return;
      const rows = content.querySelectorAll('.crf-report-row');
      const craWithOpenDcf = isCraUser && hasOpenDCF && savedFields?.length > 0;

      const ensureCommentsVisibleAndReadOnly = (cell) => {
        if (!cell) return;
        cell.classList.remove('crf-comments-hidden-for-cra');
        cell.querySelector('.crf-comments-placeholder')?.remove();
        const ta = cell.querySelector('textarea, input[type="text"]');
        if (ta) {
          ta.style.removeProperty('display');
          if (craWithOpenDcf) {
            ta.setAttribute('readonly', 'readonly');
            ta.setAttribute('disabled', 'disabled');
          } else {
            ta.removeAttribute('readonly');
            ta.removeAttribute('disabled');
          }
        }
      };

      if (!craWithOpenDcf) {
        rows.forEach((row) => ensureCommentsVisibleAndReadOnly(row.querySelector(CRF_COMMENT_REGION_SEL)));
        return;
      }

      // CRA + open DCF: show all comments (no "—"), make every comment box readonly/disabled including DCF field
      rows.forEach((row) => {
        if (row.classList.contains('crf-report-row-label-only')) {
          const commentsCell = row.querySelector(CRF_COMMENT_REGION_SEL);
          if (commentsCell) commentsCell.classList.add('crf-comments-hidden-for-cra');
          return;
        }
        ensureCommentsVisibleAndReadOnly(row.querySelector(CRF_COMMENT_REGION_SEL));
      });
    };

    const processContent = (content) => {
      removeUnwantedElements(content);
      // Cached/old CRF HTML may still use <label for="..."> on file fields; that opens the picker when clicking the question.
      content.querySelectorAll('label[for]').forEach((lbl) => {
        const fid = lbl.getAttribute('for');
        if (!fid) return;
        let inp;
        try {
          inp = content.querySelector(`#${CSS.escape(fid)}`);
        } catch {
          return;
        }
        if (!inp || inp.type !== 'file') return;
        lbl.removeAttribute('for');
        const lid = lbl.id || `${fid}_label`;
        if (!lbl.id) lbl.id = lid;
        if (!inp.getAttribute('aria-labelledby')) inp.setAttribute('aria-labelledby', lid);
      });
      updateSubGroupHeaders(content);
      transformToReportFormat(content, crfActionHandlersRef, fieldIdToDtlNo, savedFields);
      wireBackendDcfAuditButtons(content);
      const result = populateFormFromSavedData(content);

      // When user opens blank preview (template in new tab, no report): clear file URLs and make file inputs visible but not clickable
      if (isPreviewInNewTab) {
        content.querySelectorAll('.file-names-list, .crf-saved-filename').forEach((el) => {
          el.innerHTML = '';
          el.textContent = '';
        });
        content.querySelectorAll('input[type="file"]').forEach((input) => {
          input.removeAttribute('data-saved-filename');
          input.removeAttribute('data-uploaded-path');
          input.disabled = true;
          input.classList.add('crf-preview-file-disabled');
          input.setAttribute('aria-label', 'Choose File (preview only, not clickable)');
        });
      }

      // Sync StandardDate text input value to DD/MMM/YYYY dropdowns (for display and when loading saved data)
      content.querySelectorAll('input.standarddate-text').forEach((input) => {
        const baseId = input.id;
        if (!baseId) return;
        const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        if (!dateSel || !monthSel || !yearSel) return;
        const v = (input.value || '').trim();
        let dd = '', mmm = '', yyyy = '';
        const savedFormat = v.match(/^(.{2})-([A-Za-z]{3})-(\d{4})$/);
        if (savedFormat) {
          [, dd, mmm, yyyy] = savedFormat;
        } else {
          const withUKUK = v.match(/^([^-]+)-([^-]+)-([^\s-]+)$/);
          if (withUKUK) [, dd, mmm, yyyy] = withUKUK;
          else {
            const rawMatch = v.match(/^(\d{2})([A-Za-z]{3})(\d{4})?$/);
            if (rawMatch) [, dd, mmm, yyyy] = rawMatch;
          }
        }
        if (dd || mmm || yyyy) {
          if (dd) {
            const ddNorm = (dd || '').trim().toUpperCase();
            for (let i = 0; i < dateSel.options.length; i++) {
              const optText = (dateSel.options[i].text || '').trim().toUpperCase();
              if (optText === ddNorm || dateSel.options[i].value === dd) {
                dateSel.value = dateSel.options[i].value;
                break;
              }
            }
          }
          if (mmm) {
            for (let i = 0; i < monthSel.options.length; i++) {
              if ((monthSel.options[i].text || '').trim().toUpperCase() === (mmm || '').toUpperCase()) {
                monthSel.value = monthSel.options[i].value;
                break;
              }
            }
          }
          if (yyyy) {
            const yyyyUpper = (yyyy || '').trim().toUpperCase();
            const opt = Array.from(yearSel.options).find(o => {
              const t = (o.text || '').trim().toUpperCase();
              const val = (o.value || '').trim();
              return t === yyyyUpper || val === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || val === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || val === '0000'));
            });
            if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
          }
        }
      });

      // Sync StandardDateTime hidden input value to DD/MMM/YYYY/HH/MM dropdowns (when loading saved data)
      content.querySelectorAll('input.standarddatetime-text').forEach((input) => {
        const baseId = input.id;
        if (!baseId) return;
        const dateSel = content.querySelector(`#${CSS.escape(baseId + '_Date')}`);
        const monthSel = content.querySelector(`#${CSS.escape(baseId + '_Month')}`);
        const yearSel = content.querySelector(`#${CSS.escape(baseId + '_Year')}`);
        const hourSel = content.querySelector(`#${CSS.escape(baseId + '_Hour')}`);
        const minuteSel = content.querySelector(`#${CSS.escape(baseId + '_Minute')}`);
        if (!dateSel || !monthSel || !yearSel || !hourSel || !minuteSel) return;
        const v = (input.value || '').trim();
        let dd = '', mmm = '', yyyy = '', hh = '', mm = '';
        const savedFormat = v.match(/^(.{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2})$/);
        if (savedFormat) {
          [, dd, mmm, yyyy, hh, mm] = savedFormat;
        } else {
          const withUKUK = v.match(/^([^-]+)-([^-]+)-([^\s-]+)\s+([^:]+):([^:]+)$/);
          if (withUKUK) [, dd, mmm, yyyy, hh, mm] = withUKUK;
          else {
            const rawMatch = v.match(/^(\d{2})([A-Za-z]{3})(\d{4})(\d{2})(\d{2})$/);
            if (rawMatch) [, dd, mmm, yyyy, hh, mm] = rawMatch;
          }
        }
        if (dd || mmm || yyyy || hh || mm) {
          if (dd) {
            const ddNorm = (dd || '').trim().toUpperCase();
            for (let i = 0; i < dateSel.options.length; i++) {
              const optText = (dateSel.options[i].text || '').trim().toUpperCase();
              if (optText === ddNorm || dateSel.options[i].value === dd) {
                dateSel.value = dateSel.options[i].value;
                break;
              }
            }
          }
          if (mmm) {
            for (let i = 0; i < monthSel.options.length; i++) {
              if ((monthSel.options[i].text || '').trim().toUpperCase() === (mmm || '').toUpperCase()) {
                monthSel.value = monthSel.options[i].value;
                break;
              }
            }
          }
          if (yyyy) {
            const yyyyUpper = (yyyy || '').trim().toUpperCase();
            const opt = Array.from(yearSel.options).find(o => {
              const t = (o.text || '').trim().toUpperCase();
              const val = (o.value || '').trim();
              return t === yyyyUpper || val === yyyy || (yyyyUpper === 'UKUK' && (t === 'UNK' || val === '0000')) || (yyyyUpper === 'UNK' && (t === 'UKUK' || val === '0000'));
            });
            if (opt) yearSel.value = opt.value; else yearSel.value = yyyy;
          }
          if (hh) hourSel.value = hh;
          if (mm) minuteSel.value = mm;
        }
      });

      // CRA filling DCF: show remarks only on DCF-generated fields; hide for rest
      hideCommentsForNonDcfFieldsWhenCra(content);

      // Permission Logic:
      // 0. Strict Read-Only Mode (e.g. Dual Visibility for Reviewer, or Viewer profile / IsViewOnly from server)
      if (props.readOnly || isViewOnly) {
        setFieldsEnabled(content, false);
        applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly);
        return result; // Skip all other logic
      }

      // 1. If in Review Stage OR if there are Open DCFs (In Progress Correction), default to DISABLED (locked).
      // 2. If not in Review Stage and no Open DCFs, default to ENABLED.
      // 3. Then, specific fields can override this if IsEditable is strictly set (e.g. Open DCF).

      if (isReviewStage || hasOpenDCF) {
        // Default: Disable all
        setFieldsEnabled(content, false);

        // Override: Enable fields with IsEditable = true (Open DCFs) when CRA can edit: not in reviewer stage, OR in reviewer stage but current user is CRA (CRA must fix DCF fields).
        if (hasOpenDCF && (!isReviewStage || isCraUser) && savedFields && savedFields.length > 0) {
          savedFields.forEach(f => {
            // Check both lowercase and uppercase variations
            const isEditable = f.isEditable === true || f.IsEditable === true;
            if (isEditable) {
              const vFieldId = (f.vFieldId || f.VFieldId || '').trim();
              if (!vFieldId) return;

              // Find inputs for this field and enable them
              const inputs = content.querySelectorAll(`
                        [data-field-id="${vFieldId}"], 
                        [id*="${vFieldId}"], 
                        [name*="${vFieldId}"]
                     `);

              inputs.forEach(el => {
                // Do not enable comment inputs: at CRA stage all comments stay readonly/disabled
                if (el.closest(CRF_COMMENT_REGION_SEL)) return;
                // Never enable STS auto capture fields.
                if (el.getAttribute?.('data-sts-autocapture') === 'true') return;
                // Double check it matches the field ID logic used in populateFormFromSavedData
                if (el.tagName === 'INPUT' || el.tagName === 'SELECT' || el.tagName === 'TEXTAREA') {
                  el.removeAttribute('disabled');
                  el.removeAttribute('readonly');
                }
              });
            }
          });
        }
      } else {
        setFieldsEnabled(content, true);
      }

      // Enforce STS Auto Capture fields disabled in all modes (even if other logic enabled them).
      content.querySelectorAll?.('[data-sts-autocapture="true"]').forEach((el) => {
        try {
          el.setAttribute('disabled', 'disabled');
          el.setAttribute('readonly', 'readonly');
        } catch (_) { /* ignore */ }
      });

      applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly);

      // STS AutoCapture runtime execution: only on live report (not in preview-only HTML snapshot).
      // Populate after permissions so the field stays disabled but gets a value.
      // Requirement:
      // - Draft Save mode: always call API so saved value can refresh (e.g. 7 -> 8 on reopen).
      // - After Submit: never call API again; keep the stored CRF value as-is.
      const shouldRunStsAutoCapture =
        !props.readOnly &&
        !props.reviewMode &&
        !isViewOnly &&
        crfStage === 'In Progress' &&
        Array.isArray(availableActions) &&
        availableActions.includes('DraftSave');

      if (!shouldRunStsAutoCapture) {
        return result;
      }

      const autoCaptureEls = content.querySelectorAll(STS_AUTOCAPTURE_SEL);
      autoCaptureEls.forEach((el) => {
        const detailNoRaw = (el.getAttribute('data-sts-api-detail-no') || '').trim();
        if (!detailNoRaw) return;
        const key = `${detailNoRaw}::${el.id || el.name || ''}`;
        if (stsAutoCaptureExecutedRef.current.has(key)) return;
        stsAutoCaptureExecutedRef.current.add(key);

        // Always keep disabled; just set value when loaded.
        el.setAttribute('disabled', 'disabled');
        el.setAttribute('readonly', 'readonly');
        // Loading marker (do NOT set value; otherwise Draft Save will persist it)
        el.setAttribute(STS_AUTOCAPTURE_LOADING_ATTR, 'true');
        el.removeAttribute(STS_AUTOCAPTURE_ERROR_ATTR);
        if (!el.value) el.setAttribute('placeholder', 'Loading...');

        api.get(`/sts-auto-capture/execute/${encodeURIComponent(detailNoRaw)}`)
          .then((res) => {
            const value = res?.data?.value != null ? String(res.data.value) : '';
            el.value = value;
            el.removeAttribute('placeholder');
            el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
          })
          .catch((err) => {
            const msg = err?.response?.data?.error || err?.message || 'AutoCapture failed';
            // Do not persist error text into CRF values; keep empty and mark error.
            el.value = '';
            el.removeAttribute(STS_AUTOCAPTURE_LOADING_ATTR);
            el.setAttribute(STS_AUTOCAPTURE_ERROR_ATTR, 'true');
            el.setAttribute('placeholder', 'AutoCapture failed');
            try {
              setToast({ type: 'error', message: `AutoCapture failed: ${msg}` });
            } catch (_) { /* ignore */ }
          });
      });

      return result;
    };

    const isContentReady = (el) =>
      el && (el.children.length > 0 || el.querySelector('.templateSubGroup, .crf-report-row, input, select, textarea'));

    const runWhenContentReady = (getContent, onReady) => {
      const check = () => {
        const el = typeof getContent === 'function' ? getContent() : document.querySelector(getContent);
        if (isContentReady(el)) {
          const result = onReady(el);
          if (result?.totalWithValue > 0 && result?.populatedCount === 0) {
            console.warn('[CRFForm] Saved data could not be populated. Field IDs:', savedFields?.map((f) => f.vFieldId || f.VFieldId));
          } else if (result?.totalWithValue > 0 && result?.populatedCount < result?.totalWithValue) {
            console.warn('[CRFForm] Partially populated:', result.populatedCount, 'of', result.totalWithValue);
          }
          // When opened for Review (First/Second) but page has no detail rows, one-time draft save to create rows so Raise DCF works
          if (!hydratedForReviewRef.current && (!savedFields || savedFields.length === 0) && nCRFHdrNo && visitId && (crfStage === 'First Review' || crfStage === 'Second Review') && !props.readOnly && !isViewOnly) {
            hydratedForReviewRef.current = true;
            const hydrateDetailRows = async () => {
              const fields = collectFormData(el);
              if (fields.length === 0) return;
              try {
                await api.post('/crf/page/save', {
                  nCRFHdrNo,
                  vTemplateNo: effectiveTemplateId,
                  vVisitType: visitType,
                  iVisitNodeId: parseInt(visitId, 10),
                  isSubmit: false,
                  fields
                });
                const pageRes = await api.get(`/crf/page/${nCRFHdrNo}`);
                const nextFields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
                setSavedFields(Array.isArray(nextFields) ? nextFields : []);
                setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
              } catch (err) {
                console.error('[CRFForm] Auto-hydrate detail rows for review failed', err);
                hydratedForReviewRef.current = false;
              }
            };
            hydrateDetailRows();
          }
          return;
        }
        rafId = requestAnimationFrame(check);
      };
      rafId = requestAnimationFrame(check);
    };

    runWhenContentReady(() => crfContainerRef.current?.querySelector('.crf-generated-content'), (content) => processContent(content));
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [htmlContent, populateFormFromSavedData, savedFields, isReviewStage, setFieldsEnabled, fieldIdToDtlNo, hasOpenDCF, isCraUser, props.readOnly, isViewOnly, permissionsRefreshKey, isPreviewInNewTab]);

  // When stage changes to/from review or after DCF refetch, re-apply enable/disable to this instance's content only
  useLayoutEffect(() => {
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    if (!content) return;

    if (props.readOnly || isViewOnly) {
      setFieldsEnabled(content, false);
      applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly);
      return;
    }

    // If we have Open DCF, we rely on the main effect (processContent) to handle granular permissions.
    // We only blindly enable here if we are DEFINITELY not in a restricted mode.
    if (!isReviewStage && !hasOpenDCF) {
      setFieldsEnabled(content, true);
    }
    applyCraResolvedDcfCommentHidingToContent(content, savedFields, isCraUser, props.readOnly, isViewOnly);
  }, [isReviewStage, hasOpenDCF, setFieldsEnabled, props.readOnly, isViewOnly, permissionsRefreshKey, savedFields, isCraUser]);

  // File input change: upload file to server, store path in data-uploaded-path, show filename
  useEffect(() => {
    const container = crfContainerRef.current;
    if (!container) return;
    const content = container.querySelector('.crf-generated-content');
    if (!content) return;

    const handleFileChange = async (e) => {
      const input = e.target;
      if (input.type !== 'file' || !input.files?.length) return;
      const file = input.files[0];
      const fieldId = (input.getAttribute('data-field-id') || (input.id || input.name || '').replace(/^file_/i, '').replace(/_\d+$/, '').trim()) || '';
      const formData = new FormData();
      formData.append('file', file);
      if (nCRFHdrNo) formData.append('nCRFHdrNo', String(nCRFHdrNo));
      if (fieldId) formData.append('vFieldId', fieldId);
      try {
        const res = await api.post('/crf/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        const path = res.data?.path;
        const filename = res.data?.filename || file.name;
        if (path) {
          input.setAttribute('data-uploaded-path', path);
          input.setAttribute('data-saved-filename', filename);
          const answerCell = input.closest(CRF_ANSWER_REGION_SEL);
          const fileNamesEl = answerCell?.querySelector('.file-names-list') || (input.id && content.querySelector(`[id="${input.id}_fileNames"]`));
          const span = answerCell?.querySelector('.crf-saved-filename');
          const target = fileNamesEl || span;
          if (target) {
            target.textContent = `${filename}`;
            target.classList.add('crf-saved-filename');
          } else if (answerCell) {
            const s = document.createElement('span');
            s.className = 'crf-saved-filename';
            s.style.display = 'block';
            s.style.marginTop = '4px';
            s.textContent = `Uploaded: ${filename} (will save on Draft Save)`;
            input.parentNode?.appendChild(s);
          }
        }
      } catch (err) {
        setToast({ type: 'error', message: err.response?.data?.error || err.message || 'File upload failed.' });
      }
    };

    content.addEventListener('change', handleFileChange);
    return () => content.removeEventListener('change', handleFileChange);
  }, [htmlContent, nCRFHdrNo]);

  // Document-level handler for DCF/Audit/ActionItems: [data-crf-action] (our transform) OR backend buttons (fieldDcfBtn, fieldauditBtn)
  useEffect(() => {
    const runFormulaCalculation = (content, formula, formulaId, decimalNo) => {
      if (!content || !formula || !formulaId) return;
      const dec = parseInt(decimalNo, 10);
      const decimalNoSafe = Number.isNaN(dec) ? 0 : Math.max(0, dec);
      const resultEl = content.querySelector(`input[id$="${formulaId}"], select[id$="${formulaId}"], textarea[id$="${formulaId}"]`);
      const sanitized = substituteFieldCodesInFormula(content, formula);
      const ev = evaluateSanitizedFormula(sanitized);
      if (!ev.ok) {
        if (resultEl) resultEl.value = '';
        setToast({ type: 'error', message: ev.message || 'Could not calculate formula.' });
        return;
      }
      if (resultEl) {
        resultEl.value = formatFormulaCalcResult(ev.value, decimalNoSafe);
      }
    };

    const handleCrfActionClick = (e) => {
      const target = e.target;
      const calcBtn = target?.closest?.('.btnCalc');
      if (calcBtn) {
        if (calcBtn.disabled || calcBtn.getAttribute('aria-disabled') === 'true') {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        const content = calcBtn.closest('.crf-generated-content');
        if (content && crfContainerRef.current?.contains(content)) {
          let formula = calcBtn.getAttribute('data-formula') || '';
          let formulaId = calcBtn.getAttribute('data-formula-id') || '';
          let decimalNo = calcBtn.getAttribute('data-decimal-no') || '0';
          if (!formula || !formulaId) {
            const onclick = (calcBtn.getAttribute('onclick') || '').trim();
            const match = onclick.match(/Calculate\s*\(\s*true\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*,\s*'([^']*)'\s*\)\s*;?/);
            if (match) {
              formula = match[1] || '';
              formulaId = match[2] || '';
              decimalNo = match[3] != null ? String(match[3]) : '0';
              calcBtn.setAttribute('data-formula', formula);
              calcBtn.setAttribute('data-formula-id', formulaId);
              calcBtn.setAttribute('data-decimal-no', decimalNo);
              calcBtn.removeAttribute('onclick');
            }
          }
          runFormulaCalculation(content, formula, formulaId, decimalNo);
          e.preventDefault();
          e.stopPropagation();
        }
        return;
      }

      if (target?.tagName === 'INPUT' && target.type === 'file') return;
      if (target?.tagName === 'LABEL' && target.htmlFor) {
        const forEl = document.getElementById(target.htmlFor);
        if (forEl?.tagName === 'INPUT' && forEl.type === 'file') return;
      }
      if (target?.closest?.('label[for]')) {
        const forId = target.closest('label').getAttribute('for');
        if (forId && document.getElementById(forId)?.type === 'file') return;
      }
      if (target?.closest?.('input[type="file"]')) return;
      let actionEl = e.target.closest?.('[data-crf-action="dcf"], [data-crf-action="audit"], [data-crf-action="actionitems"], [data-crf-action="createactionitem"]');
      let action = actionEl?.getAttribute('data-crf-action');

      if (!actionEl || !action) {
        const clicked = e.target.closest?.('[id],[class]') || e.target;
        const id = (clicked?.getAttribute?.('id') || '').toLowerCase();
        const cls = (clicked?.getAttribute?.('class') || '').toLowerCase();
        const combined = `${id} ${cls}`;
        if (combined.includes('dcfbtn') || combined.includes('fielddcfbtn')) {
          actionEl = clicked.closest?.('[id*="DcfBtn"], [id*="dcfBtn"], [class*="DcfBtn"], [class*="dcfBtn"]') || clicked;
          action = 'dcf';
        } else if (combined.includes('auditbtn') || combined.includes('fieldauditbtn')) {
          actionEl = clicked.closest?.('[id*="auditBtn"], [id*="AuditBtn"], [class*="auditBtn"], [class*="AuditBtn"]') || clicked;
          action = 'audit';
        } else if (combined.includes('actionitem') || combined.includes('action-item')) {
          actionEl = clicked.closest?.('[id*="ActionItem"], [class*="ActionItem"], [data-crf-action="actionitems"]') || clicked;
          action = combined.includes('create') ? 'createactionitem' : 'actionitems';
        }
      } else {
        action = actionEl.getAttribute('data-crf-action');
      }

      if (!actionEl || !action) return;
      const content = actionEl.closest?.('.crf-generated-content');
      if (!content) return;
      if (crfContainerRef.current != null && !crfContainerRef.current.contains(content)) return;
      e.preventDefault();
      e.stopPropagation();

      const row = actionEl.closest?.('.crf-report-row') || actionEl.closest?.('.row') || actionEl.closest?.('tr') || actionEl.closest?.('.form-group')?.closest?.('.row') || actionEl.closest?.('[class*="report"]');
      if (!row) return;

      // Prefer nCRFDtlNo from row (set by transform) so DCF works even when fieldId doesn't match savedFields keys
      let nCRFDtlNo = row.getAttribute?.('data-n-crf-dtl-no');
      if (nCRFDtlNo != null && nCRFDtlNo !== '') nCRFDtlNo = Number(nCRFDtlNo);
      else nCRFDtlNo = null;

      // For Create Action Item: use MedExMst.vMedExCode stored on the row (data-medex-code); fallback to data-field-id / input id.
      let fieldCode = (row.getAttribute?.('data-medex-code') || '').trim();
      if (!fieldCode) {
        const fieldIdEl =
          row.querySelector('.crf-answer-cell[data-field-id], .crf-answer-cell [data-field-id], .crf-answer-col[data-field-id], .crf-answer-col [data-field-id]') ||
          row.querySelector('[data-field-id]');
        fieldCode = (fieldIdEl?.getAttribute?.('data-field-id') || '').trim();
      }
      let rawFieldId = fieldCode;
      if (!fieldCode) {
        const inputInRow =
          row.querySelector(
            '.crf-answer-cell input:not([type="hidden"]), .crf-answer-col input:not([type="hidden"]), .crf-answer-cell select, .crf-answer-col select, .crf-answer-cell textarea, .crf-answer-col textarea'
          ) || row.querySelector('input:not([type="hidden"]), select, textarea');
        rawFieldId = (inputInRow?.id || inputInRow?.name || '').trim();
        if (rawFieldId.length <= 5) fieldCode = rawFieldId;
        else fieldCode = rawFieldId.replace(/_answer$/i, '').split('_').pop()?.substring(0, 5) || rawFieldId.substring(0, 5);
      }
      if (nCRFDtlNo == null) {
        const trimmed = fieldCode?.replace(/_answer$/i, '').trim();
        nCRFDtlNo = fieldIdToDtlNo[fieldCode] ?? fieldIdToDtlNo[trimmed]
          ?? fieldIdToDtlNo[rawFieldId] ?? fieldIdToDtlNo[rawFieldId?.replace(/_answer$/i, '').trim()]
          ?? (fieldCode && /^\d{1,5}$/.test(fieldCode) ? fieldIdToDtlNo[fieldCode.padStart(5, '0')] : undefined);
      }

      let questionEl = row.querySelector('.crf-question-cell label, .crf-question-cell .form-label, .crf-question-cell');
      if (!questionEl) questionEl = row.querySelector('label, .form-label, [class*="question"]');
      const questionText = (questionEl?.textContent?.trim() || '').replace(/\s+/g, ' ').trim();

      if (action === 'dcf') {
        setDcfSelected({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText });
        setDcfModalOpen(true);
      } else if (action === 'audit') {
        setAuditSelected({ fieldId: fieldCode, questionText, nCRFDtlNo: nCRFDtlNo ?? null });
        setAuditModalOpen(true);
      } else if (action === 'actionitems') {
        setActionItemsSelected({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText });
        setActionItemsModalOpen(true);
      } else if (action === 'createactionitem') {
        // When report is in Start Report mode (no Draft Save yet), there is no CRF header — block and ask for Draft Save first.
        if (!nCRFHdrNo) {
          setToast({ type: 'error', message: actionItemMessages.pleaseDraftSaveFirst });
          return;
        }
        // When from a CRF data row (has data-n-crf-dtl-no): pass nCRFDtlNo and fieldCode so modal shows only that section attribute (selected and disabled). From footer, pass null for full list.
        const isFieldLevelRow = row?.getAttribute?.('data-n-crf-dtl-no') != null && row.getAttribute('data-n-crf-dtl-no') !== '';
        const fc = (fieldCode && String(fieldCode).trim() !== '') ? String(fieldCode).trim() : null;
        const payload = { fieldCode: fc, fieldId: fc, nCRFDtlNo: nCRFDtlNo ?? null };
        createActionItemPayloadRef.current = payload;
        setCreateActionItemNCRFDtlNo(isFieldLevelRow ? (nCRFDtlNo ?? null) : null);
        setCreateActionItemFieldCode(isFieldLevelRow && fieldCode ? fieldCode : null);
        setIsCreateActionItemModalOpen(true);
      }
    };
    document.addEventListener('click', handleCrfActionClick, true);
    return () => document.removeEventListener('click', handleCrfActionClick, true);
  }, [fieldIdToDtlNo, nCRFHdrNo, setToast]);

  // When opening DCF modal in review stage, refetch CRF page so savedFields has latest vDCFStatus and vDCFRaisedByProfileCode
  useEffect(() => {
    if (!dcfModalOpen || !isReviewStage || !nCRFHdrNo) return;
    let cancelled = false;
    api.get(`/crf/page/${nCRFHdrNo}`)
      .then(res => {
        if (cancelled) return;
        const nextFields = res.data?.fields ?? res.data?.Fields ?? [];
        setSavedFields(prev => (Array.isArray(nextFields) && nextFields.length > 0 ? nextFields : prev));
      })
      .catch(() => { if (!cancelled) { /* keep existing savedFields */ } });
    return () => { cancelled = true; };
  }, [dcfModalOpen, isReviewStage, nCRFHdrNo]);

  // Fetch DCF History when modal opens and nCRFDtlNo is set
  useEffect(() => {
    if (dcfModalOpen && dcfSelected.nCRFDtlNo) {
      setLoadingHistory(true);
      setDcfHistory([]);
      api.get(`/crf/page/dcf/${dcfSelected.nCRFDtlNo}/history`)
        .then(res => {
          setDcfHistory(res.data || []);
        })
        .catch(err => {
          console.error("Failed to fetch DCF history", err);
        })
        .finally(() => {
          setLoadingHistory(false);
        });
    } else {
      setDcfHistory([]);
    }
  }, [dcfModalOpen, dcfSelected.nCRFDtlNo]);

  // Fetch field-level audit trail when View Audit modal opens (CRFDtl record)
  useEffect(() => {
    if (!auditModalOpen) {
      setAuditTrailList([]);
      setAuditTrailError(null);
      return;
    }
    const fid = auditSelected.fieldId && auditSelected.fieldId.trim();
    const recordId = auditSelected.nCRFDtlNo
      ?? (fid && fieldIdToDtlNo?.[fid])
      ?? (fid && fieldIdToDtlNo?.[fid.replace(/_answer$/i, '').trim()])
      ?? (fid && /^\d{1,5}$/.test(fid) && fieldIdToDtlNo?.[fid.padStart(5, '0')]);
    if (recordId == null) {
      setAuditTrailList([]);
      setAuditTrailError(null);
      return;
    }
    let cancelled = false;
    setAuditTrailLoading(true);
    setAuditTrailError(null);
    auditService
      .getAuditHdrTrail('CRFDtl', String(recordId), 100, {
        projectId: auditProjectId,
        projectCode:
          auditProjectId != null && auditProjectId !== '' ? undefined : auditProjectCode,
      })
      .then((data) => {
        if (!cancelled) setAuditTrailList(Array.isArray(data) ? data : []);
      })
      .catch((err) => {
        if (!cancelled) setAuditTrailError(err?.response?.data?.error || err?.message || 'Failed to load audit trail');
      })
      .finally(() => {
        if (!cancelled) setAuditTrailLoading(false);
      });
    return () => { cancelled = true; };
  }, [auditModalOpen, auditSelected.nCRFDtlNo, auditSelected.fieldId, fieldIdToDtlNo, auditProjectCode, auditProjectId]);

  // Pre-fill DCF comment (vDCFResponse) when CRA opens the DCF Detail modal
  useEffect(() => {
    if (dcfModalOpen && !isReviewStage && dcfSelected.nCRFDtlNo && savedFields?.length) {
      const no = Number(dcfSelected.nCRFDtlNo);
      const f = savedFields.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no);
      const existing = f?.vDCFResponse ?? f?.VDCFResponse ?? '';
      setDcfResponse(existing || '');
    }
  }, [dcfModalOpen, isReviewStage, dcfSelected.nCRFDtlNo, savedFields]);

  // Fetch action items for the selected CRF field when Action Items modal opens (by nCRFDtlNo)
  useEffect(() => {
    if (!actionItemsModalOpen || !actionItemsSelected.nCRFDtlNo) {
      setActionItemsList([]);
      return;
    }
    setActionItemsLoading(true);
    const nCRFDtlNo = Number(actionItemsSelected.nCRFDtlNo);
    const params = { nCRFDtlNo };
    if (nCRFHdrNo) {
      params.nCRFHdrNo = nCRFHdrNo;
      params.includeHistory = true;
    }
    api.get('/action-items/by-crf-field', { params })
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : [];
        setActionItemsList(nCRFHdrNo ? list : list.filter((x) => (x?.status || '').toString().trim().toLowerCase() !== 'closed'));
        setSelectedActionItemIndex(0);
      })
      .catch(() => {
        setActionItemsList([]);
      })
      .finally(() => {
        setActionItemsLoading(false);
      });
  }, [actionItemsModalOpen, actionItemsSelected.nCRFDtlNo, nCRFHdrNo]);

  // Sync Action Item Review form when selected item or list changes (Field Level: Action Description readonly, Status default Closed and disabled)
  useEffect(() => {
    if (!actionItemsList.length) {
      setActionDescriptionEdit('');
      setActionReplyRemark('');
      setStatusEdit('Closed');
      return;
    }
    const idx = Math.min(selectedActionItemIndex, actionItemsList.length - 1);
    const item = actionItemsList[idx];
    setActionDescriptionEdit(item.findingDescription ?? '');
    setActionReplyRemark(item.replyRemark ?? '');
    setStatusEdit((item.status || '').toString() || 'Open');
  }, [actionItemsList, selectedActionItemIndex]);

  const loadCRFTemplate = async () => {
    const idToUse = effectiveTemplateId;
    if (!idToUse) return;
    let fields = [];
    try {
      setLoading(true);
      setError(null);

      // Get URL parameters for project, site, visit type
      const urlParams = new URLSearchParams(window.location.search);
      const projectCode = urlParams.get('project');
      const siteId = urlParams.get('siteId');
      const urlVisitType = urlParams.get('visitType') || visitType;

      // Fetch template and generate CRF HTML (use effectiveTemplateId so it runs when resolved from visit)
      const queryParams = new URLSearchParams({
        columnView: '1',
        showTemplateName: 'true'
      });
      if (projectCode) queryParams.append('projectCode', projectCode);
      if (siteId) queryParams.append('siteId', siteId);
      if (urlVisitType) queryParams.append('visitType', urlVisitType);

      const response = await api.get(`/crf/template/${idToUse}?${queryParams.toString()}`);
      const resData = response.data ?? {};
      const success = getAnyCase(resData, 'success', 'Success') === true;

      if (success) {
        const html = getAnyCase(resData, 'html', 'Html') ?? '';
        let nCRFHdrNoVal = null;

        if (visitId) {
          try {
            const visitRes = await api.get(`/visit/${visitId}`);
            const visit = visitRes.data ?? {};
            const siteCode = (getAnyCase(visit, 'siteCode', 'SiteCode', 'vSiteCode') || '').toString().trim();
            const siteName = (getAnyCase(visit, 'siteName', 'SiteName') || '').toString().trim();
            const siteDisplay = siteCode && siteName
              ? `${siteCode} - ${siteName}`
              : siteName || (siteCode ? `Site ${siteCode}` : '');
            const visitSiteId = getAnyCase(visit, 'siteID', 'SiteID', 'siteId');
            if (visitSiteId != null && String(visitSiteId).trim() !== '') {
              const nSid = Number(visitSiteId);
              if (Number.isFinite(nSid) && nSid > 0) setResolvedSiteIdForAppendix(nSid);
            }
            setFormData({
              protocolNo: getAnyCase(visit, 'projectCode', 'ProjectCode') || projectCode || '',
              siteNumber: siteDisplay,
              piName: getAnyCase(visit, 'piName', 'PiName', 'investigatorName', 'InvestigatorName') || '',
              visitDate: (() => {
                const d = getAnyCase(visit, 'scheduledStartDate', 'ScheduledStartDate', 'plannedStartDate', 'PlannedStartDate');
                return d ? formatDateDDMMMYYYY(d) : formatDateDDMMMYYYY(new Date());
              })()
            });

            let pageData = null;
            try {
              const pageRes = await api.get(`/crf/page/by-visit/${visitId}/template/${idToUse}?visitType=${visitType}`);
              pageData = pageRes.data ?? {};
            } catch (pageErr) {
              if (pageErr.response?.status === 404) {
                // No CRF page yet (user has not saved draft); show empty form until first Draft Save
                pageData = { fields: [], requiredFieldCodes: [] };
              } else {
                throw pageErr;
              }
            }
            if (pageData) {
              nCRFHdrNoVal = getAnyCase(pageData, 'nCRFHdrNo', 'NCRFHdrNo', 'ncrfHdrNo');
              const rawFields = getAnyCase(pageData, 'fields', 'Fields') ?? [];
              fields = Array.isArray(rawFields) ? rawFields : [];
              const pageStage = getAnyCase(pageData, 'vStage', 'VStage', 'stage', 'Stage');
              if (pageStage != null) setCrfStage(String(pageStage));
              const rawRequired = getAnyCase(pageData, 'requiredFieldCodes', 'RequiredFieldCodes') ?? [];
              const rawWorkflow = getAnyCase(pageData, 'workflowHeader', 'WorkflowHeader') ?? null;
              setRequiredFieldCodes(Array.isArray(rawRequired) ? rawRequired : []);
              setRequiredFieldErrors([]);
              setWorkflowHeader(rawWorkflow);
            }
          } catch (err) {
            console.error('Error fetching visit/CRF page data:', err);
            setFormData({
              protocolNo: projectCode || '',
              siteNumber: '',
              piName: '',
              visitDate: formatDateDDMMMYYYY(new Date())
            });
            setWorkflowHeader(null);
          }
        } else {
          // Preview from CRF Template Management (no visit): keep Visit Date blank
          const isTemplatePreview = searchParams.get('preview') === '1';
          setFormData({
            protocolNo: projectCode || '',
            siteNumber: '',
            piName: '',
            visitDate: isTemplatePreview ? '' : formatDateDDMMMYYYY(new Date())
          });
          setRequiredFieldCodes([]);
          setRequiredFieldErrors([]);
          setWorkflowHeader(null);
        }

        setHtmlContent(html);
        if (nCRFHdrNoVal != null) setNCRFHdrNo(Number(nCRFHdrNoVal));
        else setNCRFHdrNo(null);
        setSavedFields(fields);
      } else {
        setError('Failed to generate CRF form');
      }
    } catch (err) {
      const resData = err.response?.data ?? {};
      const errorMessage = getAnyCase(resData, 'error', 'Error', 'message', 'Message') || err.message || 'Failed to load CRF template';
      setError(errorMessage);
      console.error('Error loading CRF template:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeUnwantedElements = (content) => {
    if (!content) return;

    // 1. Remove template name header
    const templateHeaders = content.querySelectorAll('section h4');
    templateHeaders.forEach(header => {
      const text = header.textContent || '';
      if (text.includes('Template') || text.includes('Standard Template') || text.trim()) {
        const section = header.closest('section');
        if (section) {
          // Check if section only contains the header
          const sectionContent = section.innerHTML || '';
          if (sectionContent.includes('<h4') && !sectionContent.includes('templateSubGroup')) {
            section.remove();
          } else {
            header.remove();
          }
        } else {
          header.remove();
        }
      }
    });

    // 2. Remove version information section (Version No, Version Date, Version Status)
    const allSections = content.querySelectorAll('section');
    allSections.forEach(section => {
      const html = section.innerHTML || '';
      const text = section.textContent || '';

      // Check if this section contains version information
      if (html.includes('Version:') || html.includes('Version Date:') ||
        html.includes('Version Status:') || html.includes('VersionLabel') ||
        html.includes('btnRepeatGo') || html.includes('btnReview') ||
        html.includes('ddlRepeatation') ||
        (text.includes('Version:') && text.includes('Version Date:'))) {
        section.remove();
        return;
      }
    });

    // 3. Remove Go button (if still present)
    const goButtons = content.querySelectorAll('#btnRepeatGo, .btngo, button[id*="RepeatGo"], button.btn.btngo');
    goButtons.forEach(btn => {
      btn.remove();
      // Also remove parent container if it only contains the button
      const parent = btn.parentElement;
      if (parent && parent.children.length === 1) {
        parent.remove();
      }
    });

    // 4. Remove Review button (if still present)
    const reviewButtons = content.querySelectorAll('#btnReview, button[id*="Review"]');
    reviewButtons.forEach(btn => {
      btn.remove();
      // Also remove parent container if it only contains the button
      const parent = btn.parentElement;
      if (parent && parent.children.length === 1) {
        parent.remove();
      }
    });

    // 5. Remove OpenGroup buttons (tab buttons)
    const tabContainers = content.querySelectorAll('.tab');
    tabContainers.forEach(container => container.remove());

    const tabButtons = content.querySelectorAll('.tablinks, button[onclick*="openGroup"]');
    tabButtons.forEach(btn => btn.remove());

    // 6. Remove empty textarea/select elements (ddlRepeatation)
    const repeatSelects = content.querySelectorAll('#ddlRepeatation, select[id*="Repeatation"]');
    repeatSelects.forEach(select => {
      const parent = select.closest('section, div');
      if (parent) {
        // Check if parent only contains version-related elements
        const parentHtml = parent.innerHTML || '';
        if (parentHtml.includes('Version') || parentHtml.includes('btnRepeatGo')) {
          parent.remove();
        } else {
          select.remove();
        }
      } else {
        select.remove();
      }
    });

    // 7. Remove any remaining version labels
    const versionLabels = content.querySelectorAll('.VersionLabel, span.VersionLabel');
    versionLabels.forEach(label => {
      const parent = label.closest('p, label, div');
      if (parent) {
        const parentText = parent.textContent || '';
        if (parentText.includes('Version')) {
          parent.remove();
        } else {
          label.remove();
        }
      } else {
        label.remove();
      }
    });
  };

  const updateSubGroupHeaders = (content) => {
    if (!content) return;

    // Find all subgroup header divs
    const subGroupHeaders = content.querySelectorAll('.subGroupDiv');

    subGroupHeaders.forEach((headerDiv) => {
      // Get the onclick attribute to extract subgroup ID
      const onclickAttr = headerDiv.getAttribute('onclick');
      if (!onclickAttr) return;

      // Extract subgroup ID from onclick: toggleSubGroup(this, 'SUBGROUP_ID')
      const match = onclickAttr.match(/toggleSubGroup\(this,\s*['"]([^'"]+)['"]\)/);
      if (!match || !match[1]) return;

      const subGroupId = match[1];

      // Find the corresponding templateSubGroup element (use [id="..."] because #id is invalid when id starts with a digit)
      const subGroupElement = content.querySelector(`[id="${String(subGroupId).replace(/"/g, '\\"')}"].templateSubGroup`);
      if (!subGroupElement) return;

      // Get the description from aria-labelledby attribute
      // Format: "INVESTIGATOR'S_BROCHURE" -> "INVESTIGATOR'S BROCHURE"
      const ariaLabelledBy = subGroupElement.getAttribute('aria-labelledby');
      if (ariaLabelledBy) {
        const description = ariaLabelledBy.replace(/_/g, ' ');
        const label = headerDiv.querySelector('label.form-label');
        if (label) {
          // Only update if the label is empty or contains just the code
          // This ensures we use the correct subgroup description from the backend
          const currentText = label.textContent.trim();
          if (!currentText || currentText.length < 3 || currentText === subGroupId.split('_')[0]) {
            label.textContent = description.toUpperCase();
          }
        }
      }
    });
  };

  const transformToReportFormat = (content, actionHandlerRef, fieldIdToDtlNoMap, savedFieldsList) => {
    if (!content) return;
    const idToDtl = fieldIdToDtlNoMap || {};

    // Remove unwanted buttons from CRF preview (Submit, Save, Approve, Reject)
    // These buttons are generated by the backend inside each section but should not appear in preview
    // NOTE: Footer buttons (Draft Save, Submit, Print, Close) are NOT in .crf-generated-content, so they remain
    const unwantedButtonSelectors = [
      '.template-Submit-btn',
      '.template-SaveContinue-btn',
      '.template-Approve-btn',
      '.template-Reject-btn',
      'button[class*="template-Submit"]',
      'button[class*="template-Save"]',
      'button[class*="template-Approve"]',
      'button[class*="template-Reject"]'
    ];

    unwantedButtonSelectors.forEach(selector => {
      const buttons = content.querySelectorAll(selector);
      buttons.forEach((btn) => {
        // Make sure we're not removing footer buttons (they're outside .crf-generated-content)
        const isInFooter = btn.closest('.crf-form-footer');
        if (!isInFooter) {
          // Remove the button and its container if it only contains unwanted buttons
          const container = btn.closest('.container.m-3.text-center, .text-center, .container');
          if (container) {
            const buttonsInContainer = container.querySelectorAll('button');
            const hasOnlyUnwantedButtons = Array.from(buttonsInContainer).every(b => {
              const classList = Array.from(b.classList);
              return classList.some(cls =>
                cls.includes('template-Submit') ||
                cls.includes('template-Save') ||
                cls.includes('template-Approve') ||
                cls.includes('template-Reject')
              );
            });
            if (hasOnlyUnwantedButtons && buttonsInContainer.length > 0) {
              container.remove();
            } else {
              btn.remove();
            }
          } else {
            btn.remove();
          }
        }
      });
    });

    // Process each template subgroup (and fallback for First Review / alternate HTML without .templateSubGroup)
    let subGroups = content.querySelectorAll('.templateSubGroup');
    if (subGroups.length === 0) {
      subGroups = content.querySelectorAll('section, [class*="templateSubGroup"], [class*="template-subgroup"], .templateSubGroup');
    }
    subGroups.forEach((subGroup) => {
      // Check if already transformed
      if (subGroup.classList.contains('crf-report-transformed')) return;

      // Get form sections: .form-section-box, or .row with .form-group, or .crf-report-row (ReportField)
      // Include .form-group that are not inside .form-section-box so Label-only rows (template type Label) are shown
      let formSections = subGroup.querySelectorAll('.form-section-box');
      const labelOnlyGroups = subGroup.querySelectorAll('.form-group:not(.form-section-box .form-group)');
      const labelOnlySections = Array.from(labelOnlyGroups).filter((g) => !g.closest('.form-section-box')).map((r) => ({
        querySelector: (sel) => (sel === '.row' ? r.parentElement : r.querySelector(sel)),
        closest: (s) => r.closest(s)
      }));
      if (formSections.length === 0 && labelOnlySections.length === 0) {
        const reportRows = subGroup.querySelectorAll('.crf-report-row');
        if (reportRows.length > 0) {
          formSections = Array.from(reportRows).map((r) => ({
            querySelector: (sel) => (sel === '.row' ? r : r.querySelector(sel)),
            closest: () => null
          }));
        } else {
          let rows = subGroup.querySelectorAll('.row');
          if (rows.length === 0) rows = subGroup.querySelectorAll('.form-group');
          if (rows.length === 0) return;
          formSections = Array.from(rows).map((r) => ({
            querySelector: (sel) => (sel === '.row' ? r : r.querySelector(sel)),
            closest: () => null
          }));
        }
      } else {
        // Merge and order: form-section-box and label-only .form-group in document order so Label rows appear
        const ordered = [];
        const allCandidates = subGroup.querySelectorAll('.form-section-box, .form-group');
        allCandidates.forEach((el) => {
          if (el.classList.contains('form-section-box')) {
            ordered.push(el);
          } else if (el.classList.contains('form-group') && !el.closest('.form-section-box')) {
            ordered.push({
              querySelector: (sel) => (sel === '.row' ? el.parentElement : el.querySelector(sel)),
              closest: (s) => el.closest(s)
            });
          }
        });
        if (ordered.length > 0) formSections = ordered;
        else if (labelOnlySections.length > 0) formSections = labelOnlySections;
      }

      // Create table container
      const tableContainer = document.createElement('div');
      tableContainer.className = 'crf-report-table';
      tableContainer.style.display = 'table';
      tableContainer.style.width = '100%';
      tableContainer.style.borderCollapse = 'collapse';

      // Create header row
      const headerRow = document.createElement('div');
      headerRow.className = 'crf-table-header';
      headerRow.style.display = 'table-row';
      headerRow.style.backgroundColor = '#f0f0f0';
      headerRow.style.fontWeight = 'bold';

      const header1 = document.createElement('div');
      header1.className = 'crf-header-cell';
      header1.style.display = 'table-cell';
      header1.style.padding = '12px';
      header1.style.border = '1px solid #ddd';
      header1.textContent = 'Question';

      const header2 = document.createElement('div');
      header2.className = 'crf-header-cell';
      header2.style.display = 'table-cell';
      header2.style.padding = '12px';
      header2.style.border = '1px solid #ddd';
      header2.textContent = 'Answer';

      const header3 = document.createElement('div');
      header3.className = 'crf-header-cell';
      header3.style.display = 'table-cell';
      header3.style.padding = '12px';
      header3.style.border = '1px solid #ddd';
      header3.textContent = 'Comments';

      headerRow.appendChild(header1);
      headerRow.appendChild(header2);
      headerRow.appendChild(header3);
      tableContainer.appendChild(headerRow);

      // Transform each form section
      formSections.forEach((section) => {
        const row = section.querySelector('.row') || section;
        const isReportRow = row.classList?.contains('crf-report-row');
        const label = row.querySelector('label.form-label, .crf-question-text');
        const controlGroup = row.querySelector('.form-group');
        const answerCol = row.querySelector('.crf-answer-col');
        const input = controlGroup?.querySelector('input, select, textarea') || answerCol?.querySelector('input, select, textarea');
        const labelEl = label || row.querySelector('.crf-question-text');

        // Label-only row (template type Label): no input/select/textarea, only label text — still show as a table row
        if (!input) {
          if (!labelEl) return;
          const tableRow = document.createElement('div');
          tableRow.className = 'crf-report-row crf-report-row-label-only';
          tableRow.style.display = 'table-row';
          const labelId = labelEl.id || labelEl.getAttribute?.('for') || '';
          const labelFieldCode = (labelId && (labelId.startsWith('lbl_') || labelId.match(/^[a-z]+_(\d{4,5})$/i))) ? (labelId.split('_')[1] || labelId) : '';
          if (labelFieldCode) tableRow.setAttribute('data-medex-code', labelFieldCode);
          const questionCell = document.createElement('div');
          questionCell.className = 'crf-question-cell';
          questionCell.style.display = 'table-cell';
          questionCell.style.width = '55%';
          questionCell.style.padding = '12px';
          questionCell.style.verticalAlign = 'top';
          questionCell.style.border = '1px solid #ddd';
          questionCell.style.backgroundColor = '#fafafa';
          questionCell.innerHTML = labelEl.outerHTML || labelEl.textContent || '';
          const answerCell = document.createElement('div');
          answerCell.className = 'crf-answer-cell';
          answerCell.style.display = 'table-cell';
          answerCell.style.width = '25%';
          answerCell.style.padding = '12px';
          answerCell.style.border = '1px solid #ddd';
          const commentsCell = document.createElement('div');
          commentsCell.className = 'crf-comments-cell';
          commentsCell.style.display = 'table-cell';
          commentsCell.style.width = '20%';
          commentsCell.style.padding = '12px';
          commentsCell.style.border = '1px solid #ddd';
          tableRow.appendChild(questionCell);
          tableRow.appendChild(answerCell);
          tableRow.appendChild(commentsCell);
          tableContainer.appendChild(tableRow);
          return;
        }
        if (!labelEl) return;

        const rawFieldId = input.id || input.name || '';
        let fieldCode = rawFieldId.length <= 5 ? rawFieldId : (() => {
          const parts = rawFieldId.split('_');
          const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
          return five || parts.pop()?.substring(0, 5) || rawFieldId.substring(0, 5);
        })();
        // For radio/checkbox/file, avoid using control-type as code (e.g. "radio"); use name or id to get real field code
        const controlTypes = ['radio', 'checkbox', 'textbox', 'textarea', 'combobox', 'label', 'formula', 'file', 'reportfield', 'datetime', 'time', 'standarddate'];
        if (fieldCode && controlTypes.includes(fieldCode.toLowerCase()) && (input.type === 'radio' || input.type === 'checkbox' || input.type === 'file') && input.name) {
          const parts = input.name.trim().split('_');
          const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
          if (five) fieldCode = five;
        }
        // If fieldCode is still a control-type (e.g. id was "radio"), derive from name, id, or sibling inputs
        if (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) {
          if (input.name && input.name.includes('_')) {
            const parts = input.name.trim().split('_');
            const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
            if (five) fieldCode = five;
          }
          if ((!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) && input.id && input.id.includes('_')) {
            const parts = input.id.split('_');
            const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
            if (five) fieldCode = five;
          }
          if (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase())) {
            const siblings = (controlGroup || answerCol || row).querySelectorAll('input, select, textarea');
            for (let i = 0; i < siblings.length; i++) {
              const s = siblings[i];
              const rid = (s.id || s.name || '').trim();
              if (rid && rid.includes('_')) {
                const parts = rid.split('_');
                const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
                if (five) { fieldCode = five; break; }
              }
            }
          }
        }
        // For radio/checkbox, prefer data-field-id from backend so data-n-crf-dtl-no and data-medex-code bind correctly
        if ((input.type === 'radio' || input.type === 'checkbox') && (input.getAttribute && input.getAttribute('data-field-id'))) {
          const dataFieldId = (input.getAttribute('data-field-id') || '').trim();
          if (dataFieldId) fieldCode = dataFieldId;
        }
        if ((input.type === 'radio' || input.type === 'checkbox') && (!fieldCode || controlTypes.includes((fieldCode || '').toLowerCase()))) {
          const siblingWithDataFieldId = (controlGroup || answerCol || row).querySelector && (controlGroup || answerCol || row).querySelector('input[data-field-id]');
          if (siblingWithDataFieldId) {
            const dataFieldId = (siblingWithDataFieldId.getAttribute('data-field-id') || '').trim();
            if (dataFieldId) fieldCode = dataFieldId;
          }
        }
        // Use full rawFieldId (do not strip trailing _\d+) so SSV TextBox, SSV TextBox1, SSV TextBox2 each get unique comment fields
        // Resolve Textbox showing in Comment Section Instead of Textarea
        // START
        const commentsBase = rawFieldId.replace(/_answer$/i, '');
        const commentsId = commentsBase + '_comments';
        // Find any existing comments control (input or textarea)
        const existingComments =
          row.querySelector('.crf-comments-col textarea, [id$="_comments"], [name$="_comments"]') || null;
        const commentsField = document.createElement('textarea');
        commentsField.id = commentsId;
        commentsField.name = commentsId;
        commentsField.className = 'form-control crf-comments-field';
        commentsField.placeholder = 'Enter the Comments';
        commentsField.rows = 2;
        commentsField.maxLength = 500;
        // If we had an existing comments control, copy its content/value into the textarea
        if (existingComments) {
          const value =
            (existingComments.value !== undefined ? existingComments.value : existingComments.textContent) || '';
          commentsField.value = value;
        }
        // END

        // Create table row: store nCRFDtlNo and MedExMst.vMedExCode on every field for Create Action Item (section-attributes-for-crf).
        const tableRow = document.createElement('div');
        tableRow.className = 'crf-report-row';
        tableRow.style.display = 'table-row';
        const nCRFDtlNoForRow = idToDtl[fieldCode] ?? idToDtl[fieldCode.replace(/_answer$/i, '').trim()] ?? (fieldCode && /^\d{1,5}$/.test(fieldCode) ? idToDtl[fieldCode.padStart(5, '0')] : undefined) ?? null;
        if (nCRFDtlNoForRow != null) tableRow.setAttribute('data-n-crf-dtl-no', String(nCRFDtlNoForRow));
        const savedField = (savedFieldsList || []).find((f) => Number(f.nCRFDtlNo ?? f.NCRFDtlNo ?? 0) === nCRFDtlNoForRow)
          || (savedFieldsList || []).find((f) => (f.vFieldId || f.VFieldId || '').trim().toLowerCase() === (fieldCode || '').trim().toLowerCase());
        const medExCode = (savedField && (savedField.vFieldId || savedField.VFieldId)) ? String(savedField.vFieldId || savedField.VFieldId).trim() : (fieldCode ? String(fieldCode).trim() : '');
        if (medExCode) tableRow.setAttribute('data-medex-code', medExCode);

        // Question cell
        const questionCell = document.createElement('div');
        questionCell.className = 'crf-question-cell';
        questionCell.style.display = 'table-cell';
        questionCell.style.width = '55%';
        questionCell.style.padding = '12px';
        questionCell.style.verticalAlign = 'top';
        questionCell.style.border = '1px solid #ddd';
        questionCell.style.backgroundColor = '#fafafa';
        questionCell.innerHTML = labelEl.outerHTML || labelEl.textContent || '';
        const sideBtns = (controlGroup || answerCol || row).querySelector('.col-sideBtns');
        const actionsWrap = document.createElement('div');
        actionsWrap.className = 'col-sideBtns crf-row-actions';
        actionsWrap.style.display = 'inline-flex';
        actionsWrap.style.gap = '12px';
        actionsWrap.style.marginLeft = '0';
        actionsWrap.style.verticalAlign = 'middle';
        if (sideBtns) {
          const sideBtnsClone = sideBtns.cloneNode(true);
          const wrapperSelector = 'a, button, [role="button"], .icon-btn';
          const topLevelWrappers = Array.from(sideBtnsClone.querySelectorAll(wrapperSelector)).filter(
            (el) => el.closest(wrapperSelector) === el
          );
          const allClickables = sideBtnsClone.querySelectorAll('button, a, [role="button"], .icon-btn, i, span');
          const btns = topLevelWrappers.length >= 2 ? topLevelWrappers : Array.from(allClickables).slice(0, 2);
          btns[0]?.setAttribute('data-crf-action', 'dcf');
          btns[0]?.setAttribute('title', 'Create DCF');
          btns[0]?.setAttribute('role', 'button');
          if (btns[1]) {
            btns[1].setAttribute('data-crf-action', 'audit');
            btns[1].setAttribute('title', 'View Audit');
            btns[1].setAttribute('role', 'button');
          }
          actionsWrap.appendChild(sideBtnsClone);
        } else {
          const dcfBtn = document.createElement('button');
          dcfBtn.type = 'button';
          dcfBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          dcfBtn.setAttribute('data-crf-action', 'dcf');
          dcfBtn.setAttribute('title', 'Create DCF');
          dcfBtn.textContent = 'DCF';
          const auditBtn = document.createElement('button');
          auditBtn.type = 'button';
          auditBtn.className = 'btn btn-sm btn-outline-secondary crf-action-btn';
          auditBtn.setAttribute('data-crf-action', 'audit');
          auditBtn.setAttribute('title', 'View Audit');
          auditBtn.textContent = 'Audit';
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issues for this question');
          actionItemsBtn.setAttribute('aria-label', 'Issues for this question');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          const createActionItemBtn = document.createElement('span');
          createActionItemBtn.className = 'btn btn-sm btn-outline-primary crf-action-btn crf-btn-icon';
          createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
          createActionItemBtn.setAttribute('title', 'Create Issue');
          createActionItemBtn.setAttribute('aria-label', 'Create Issue');
          createActionItemBtn.setAttribute('role', 'button');
          createActionItemBtn.setAttribute('tabindex', '0');
          createActionItemBtn.innerHTML = crfActionIconSvg('plus');
          actionsWrap.appendChild(dcfBtn);
          actionsWrap.appendChild(auditBtn);
          actionsWrap.appendChild(actionItemsBtn);
          actionsWrap.appendChild(createActionItemBtn);
        }
        if (sideBtns) {
          const actionItemsBtn = document.createElement('span');
          actionItemsBtn.setAttribute('style', 'color: #208094;');
          actionItemsBtn.setAttribute('data-crf-action', 'actionitems');
          actionItemsBtn.setAttribute('title', 'Issues for this question');
          actionItemsBtn.setAttribute('aria-label', 'Issues for this question');
          actionItemsBtn.setAttribute('role', 'button');
          actionItemsBtn.setAttribute('tabindex', '0');
          actionItemsBtn.innerHTML = crfActionIconSvg('actionitem');
          actionsWrap.appendChild(actionItemsBtn);
          const createActionItemBtn = document.createElement('span');
          createActionItemBtn.setAttribute('style', 'color: #208094;');
          createActionItemBtn.setAttribute('data-crf-action', 'createactionitem');
          createActionItemBtn.setAttribute('title', 'Create Issue');
          createActionItemBtn.setAttribute('aria-label', 'Create Issue');
          createActionItemBtn.setAttribute('role', 'button');
          createActionItemBtn.setAttribute('tabindex', '0');
          createActionItemBtn.innerHTML = crfActionIconSvg('plus');
          actionsWrap.appendChild(createActionItemBtn);
        }
        questionCell.appendChild(actionsWrap);

        // Ensure DCF, View Audit, Action Items and Create Action Item are never disabled and stay clickable (incl. First/Second Review)
        const dcfBtnEl = actionsWrap.querySelector('[data-crf-action="dcf"]');
        const auditBtnEl = actionsWrap.querySelector('[data-crf-action="audit"]');
        const actionItemsBtnEl = actionsWrap.querySelector('[data-crf-action="actionitems"]');
        const createActionItemBtnEl = actionsWrap.querySelector('[data-crf-action="createactionitem"]');
        [dcfBtnEl, auditBtnEl, actionItemsBtnEl, createActionItemBtnEl].forEach((el) => {
          if (!el) return;
          el.removeAttribute('disabled');
          el.setAttribute('tabindex', '0');
          el.style.pointerEvents = 'auto';
          el.style.cursor = 'pointer';
        });
        actionsWrap.style.pointerEvents = 'auto';

        // Direct click handlers so DCF/Audit work when CRF is inside nested modal (First/Second Review)
        const questionTextForAction = (labelEl?.textContent || '').trim().replace(/\s+/g, ' ');
        if (dcfBtnEl && actionHandlerRef?.current) {
          dcfBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlerRef.current.openDcf({ nCRFDtlNo: nCRFDtlNoForRow, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (auditBtnEl && actionHandlerRef?.current) {
          auditBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            // Resolve nCRFDtlNo from row at click time so radio (and any) row works even if transform ran before savedFields had this field
            const row = e.target.closest?.('.crf-report-row');
            const nCRFDtlNoFromRow = row?.getAttribute?.('data-n-crf-dtl-no');
            const nCRFDtlNo = (nCRFDtlNoFromRow != null && nCRFDtlNoFromRow !== '') ? Number(nCRFDtlNoFromRow) : nCRFDtlNoForRow;
            actionHandlerRef.current.openAudit({ nCRFDtlNo: nCRFDtlNo ?? null, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (actionItemsBtnEl && actionHandlerRef?.current) {
          actionItemsBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            actionHandlerRef.current.openActionItems({ nCRFDtlNo: nCRFDtlNoForRow, fieldId: fieldCode, questionText: questionTextForAction });
          });
        }
        if (createActionItemBtnEl && actionHandlerRef?.current) {
          createActionItemBtnEl.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const r = e.target.closest('.crf-report-row');
            const fromRow = r?.getAttribute?.('data-medex-code')
              || (r?.querySelector?.('.crf-answer-cell[data-field-id], .crf-answer-col[data-field-id]')?.getAttribute?.('data-field-id'))?.trim();
            const vMedExCode = (fromRow && fromRow !== '') ? fromRow : (medExCode || fieldCode || '');
            actionHandlerRef.current.openCreateActionItem({ fieldCode: vMedExCode || null, nCRFDtlNo: nCRFDtlNoForRow });
          });
        }

        // Answer cell (set data-field-id on cell too so DCF/Audit row lookup works)
        const answerCell = document.createElement('div');
        answerCell.className = 'crf-answer-cell';
        answerCell.setAttribute('data-field-id', fieldCode);
        answerCell.style.display = 'table-cell';
        answerCell.style.width = '15%';
        answerCell.style.padding = '12px';
        answerCell.style.verticalAlign = 'top';
        answerCell.style.border = '1px solid #ddd';
        answerCell.style.backgroundColor = 'white';

        // Clone the entire container to preserve radio groups, checkboxes with labels, etc.
        const containerToClone = controlGroup || answerCol || input.closest('.form-group') || input.parentElement;
        if (containerToClone) {
          const containerClone = containerToClone.cloneNode(true);

          // Clean up the clone: remove side buttons, main label (question), and any other non-answer elements
          const sideBtnsInClone = containerClone.querySelectorAll('.col-sideBtns');
          sideBtnsInClone.forEach(el => el.remove());
          const labelsInClone = containerClone.querySelectorAll('label.form-label, .crf-question-text');
          labelsInClone.forEach(el => {
            // Only remove if it's the main question label (usually has specific classes or is the first label)
            // But for checkboxes/radios, the label NEXT to the input is the option label, keep it.
            // Heuristic: if it looks like the main question text, remove it.
            if (el.textContent === (labelEl?.textContent || '')) {
              el.remove();
            }
          });

          // Ensure all inputs in the clone have data-field-id (exclude hidden to prevent population issues)
          const inputsInClone = containerClone.querySelectorAll('input:not([type="hidden"]), select, textarea');
          inputsInClone.forEach(inp => inp.setAttribute('data-field-id', fieldCode));

          // Append the cleaned container
          answerCell.appendChild(containerClone);
        } else {
          // Fallback: just clone the input if no container found
          const inputClone = input.cloneNode(true);
          inputClone.setAttribute('data-field-id', fieldCode);
          inputClone.style.width = '100%';
          answerCell.appendChild(inputClone);
        }

        // Comments cell
        const commentsCell = document.createElement('div');
        commentsCell.className = 'crf-comments-cell';
        commentsCell.style.display = 'table-cell';
        commentsCell.style.width = '25%';
        commentsCell.style.padding = '12px';
        commentsCell.style.verticalAlign = 'top';
        commentsCell.style.border = '1px solid #ddd';
        commentsCell.style.borderLeft = 'none';
        commentsCell.style.backgroundColor = 'white';
        commentsCell.appendChild(commentsField);

        tableRow.appendChild(questionCell);
        tableRow.appendChild(answerCell);
        tableRow.appendChild(commentsCell);
        tableContainer.appendChild(tableRow);
      });

      // Replace entire subgroup content with the table (so no duplicate rows remain)
      while (subGroup.firstChild) {
        subGroup.removeChild(subGroup.firstChild);
      }
      subGroup.appendChild(tableContainer);

      // Mark as transformed
      subGroup.classList.add('crf-report-transformed');
    });
  };

  // Control-type words that must not be used as field codes (backend expects vMedExCode e.g. 00005)
  const CONTROL_TYPE_NAMES = ['radio', 'checkbox', 'textbox', 'textarea', 'combobox', 'label', 'formula', 'file', 'reportfield', 'datetime', 'time', 'standarddate'];

  const isControlTypeName = (id) => typeof id === 'string' && CONTROL_TYPE_NAMES.includes(id.trim().toLowerCase());

  // Extract 5-char field code (vMedExCode) from HTML id/name (e.g. reportfield_IB001_answer -> IB001, textbox_IB001 -> IB001, radio_00005_0 -> 00005)
  const extractFieldCode = React.useCallback((raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const s = raw.trim();
    if (!s) return '';
    // Do not use control-type words as field codes (e.g. "radio" would break save)
    const lower = s.toLowerCase();
    if (CONTROL_TYPE_NAMES.some((t) => lower === t || lower.startsWith(t + '_'))) {
      // Parse from pattern like radio_00005_0 -> 00005
      const parts = s.split('_');
      const fiveChar = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
      if (fiveChar) return fiveChar;
      const numericPart = parts.find((p) => /^\d{4,5}$/.test(p));
      if (numericPart) return numericPart;
      return '';
    }
    // Short code: accept only if not a control-type name
    if (s.length <= 5 && /^[A-Za-z0-9]+$/.test(s)) {
      if (CONTROL_TYPE_NAMES.includes(lower)) return '';
      return s;
    }
    const parts = s.split('_');
    // Find 5-char alphanumeric segment (field codes like IB001, 00001)
    const fiveChar = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p));
    if (fiveChar) return fiveChar;
    // Fallback: last segment before _answer or _comments (e.g. reportfield_IB001_answer -> IB001)
    const beforeAnswer = s.replace(/_answer$/i, '').replace(/_comments$/i, '');
    const lastPart = beforeAnswer.split('_').pop();
    if (lastPart && lastPart.length <= 5 && /^[A-Za-z0-9]+$/.test(lastPart)) {
      if (CONTROL_TYPE_NAMES.includes(lastPart.toLowerCase())) return '';
      return lastPart;
    }
    // Last resort: first 5 chars if alphanumeric and not a control type
    const code = s.substring(0, 5);
    if (/^[A-Za-z0-9]+$/.test(code) && !CONTROL_TYPE_NAMES.includes(code.toLowerCase())) return code;
    return '';
  }, []);

  // Format StandardDate/StandardDateTime for vFieldValue (DD-MMM-YYYY and DD-MMM-YYYY HH:MM). Used by collectFormData and getCurrentFieldValueFromForm.
  // Preserve UK (day) and UNK (month) as-is in saved value.
  const formatStandardDateValueForSave = React.useCallback((raw) => {
    const s = (raw || '').trim();
    if (!s || s.length < 9) return s;
    const dd = s.substring(0, 2);
    const mmm = s.substring(2, 5).toUpperCase();
    const yyyy = s.substring(5, 9);
    const dayDisplay = dd === '00' ? 'UK' : dd; // save UK when day dropdown value is 00
    const monthDisplay = mmm === 'UNK' ? 'UNK' : mmm.charAt(0) + mmm.slice(1).toLowerCase();
    return `${dayDisplay}-${monthDisplay}-${yyyy}`;
  }, []);
  const formatStandardDateTimeValueForSave = React.useCallback((raw) => {
    const s = (raw || '').trim();
    if (!s || s.length < 13) return s;
    const dd = s.substring(0, 2);
    const mmm = s.substring(2, 5).toUpperCase();
    const yyyy = s.substring(5, 9);
    const hh = s.substring(9, 11);
    const mm = s.substring(11, 13);
    const dayDisplay = dd === '00' ? 'UK' : dd;
    const monthDisplay = mmm === 'UNK' ? 'UNK' : mmm.charAt(0) + mmm.slice(1).toLowerCase();
    return `${dayDisplay}-${monthDisplay}-${yyyy} ${hh}:${mm}`;
  }, []);

  const collectFormData = (contentRoot = null) => {
    const content = contentRoot ?? crfContainerRef.current?.querySelector('.crf-generated-content') ?? document.querySelector('.crf-generated-content');
    if (!content) return [];
    const fields = [];
    const seen = new Set();

    // Answer vs Comment: isCrfCommentFieldControl (module) — all control types, not textarea-only.
    const rowCommentsTextareaSel = CRF_COMMENTS_TEXTAREA_SEL;

    // Helper: derive 5-char field code from backend name format (e.g. radio_00005, checkbox_00005, file_00005)
    const fieldIdFromName = (nameStr) => {
      if (!nameStr || typeof nameStr !== 'string') return '';
      const parts = nameStr.trim().split('_');
      const five = parts.find((p) => p.length === 5 && /^[A-Za-z0-9]+$/.test(p) && !isControlTypeName(p));
      if (five) return five;
      const four = parts.find((p) => p.length === 4 && /^[A-Za-z0-9]+$/.test(p) && !isControlTypeName(p));
      if (four) return four;
      return extractFieldCode(nameStr);
    };

    // First pass: collect all radio groups by fieldId and push one entry per group (checked value)
    // Parse id/name first so we never use "radio" as fieldId (e.g. radio_00001_Yes -> 00001); then data-field-id
    const radioInputs = content.querySelectorAll('input[type="radio"]');
    const radioGroupsByFieldId = new Map();
    radioInputs.forEach((el) => {
      if (isCrfCommentFieldControl(el)) return;
      const rawId = (el.id || el.name || '').trim();
      let fieldId = '';
      // 1) From id like radio_00001_Yes or radio_00005_No: take parts[1] (never use "radio" as fieldId)
      if (el.id && typeof el.id === 'string' && el.id.toLowerCase().startsWith('radio_')) {
        const parts = el.id.split('_');
        const candidate = parts.length >= 2 && parts[1] ? parts[1].trim() : '';
        if (candidate && candidate.length <= 5 && !isControlTypeName(candidate)) fieldId = candidate;
        if (!fieldId && parts.length >= 3 && parts[2] && /^[A-Za-z0-9]{4,5}$/.test(parts[2]) && !isControlTypeName(parts[2])) fieldId = parts[2].trim();
      }
      // 2) From name (e.g. radio_00001) or data-field-id
      if (!fieldId && el.name) fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      if (!fieldId && el.name && typeof el.name === 'string') {
        const afterPrefix = el.name.replace(/^radio_/i, '').trim();
        const segments = afterPrefix.split('_').filter(Boolean);
        const last = segments[segments.length - 1];
        if (last && /^[A-Za-z0-9]{4,5}$/.test(last) && !isControlTypeName(last)) fieldId = last;
        else if (/^[A-Za-z0-9]{4,5}$/.test(afterPrefix) && !isControlTypeName(afterPrefix)) fieldId = afterPrefix;
      }
      const dataFieldIdAttr = el.getAttribute?.('data-field-id');
      if (!fieldId && dataFieldIdAttr && dataFieldIdAttr.trim() && !isControlTypeName(dataFieldIdAttr.trim())) fieldId = dataFieldIdAttr.trim();
      if (!fieldId) fieldId = extractFieldCode(rawId);
      // Reject control-type names (e.g. "radio") so we never group by them
      if (fieldId && isControlTypeName(fieldId)) fieldId = '';
      // Backend CRFDtl.VFieldId is max 5 chars; normalize so save succeeds
      if (fieldId && fieldId.length > 5) fieldId = fieldId.substring(0, 5);
      if (!fieldId || isControlTypeName(fieldId)) return;
      if (!radioGroupsByFieldId.has(fieldId)) radioGroupsByFieldId.set(fieldId, []);
      radioGroupsByFieldId.get(fieldId).push(el);
    });
    radioGroupsByFieldId.forEach((inputs, fieldId) => {
      if (isControlTypeName(fieldId)) return;
      const checked = inputs.find((r) => r.checked);
      seen.add(fieldId);
      const row = inputs[0]?.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${fieldId}_comments"], [name="${fieldId}_comments"]`) ||
        content.querySelector(`[id="${(inputs[0].name || '').replace(/_\d+$/, '')}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="radio_${fieldId}_comments"], [name="radio_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      const radioValue = checked ? ((checked.value || '').trim() || 'Yes') : '';
      fields.push({ vFieldId: fieldId, vFieldValue: radioValue, vComment: comment || null });
    });

    // Second pass (RAMP-style): collect checkboxes by group (same name = one field), then other controls
    // Checkboxes: one entry per field with vFieldValue = comma-separated checked values (RAMP CheckList.jsx)
    // Prefer data-field-id so checkbox values are collected and saved reliably
    const checkboxInputs = content.querySelectorAll('input[type="checkbox"]');
    const checkboxGroupsByFieldId = new Map();
    checkboxInputs.forEach((el) => {
      if (isCrfCommentFieldControl(el)) return;
      const dataFieldIdAttr = el.getAttribute?.('data-field-id');
      const rawId = (el.id || el.name || '').trim();
      let fieldId = (dataFieldIdAttr && dataFieldIdAttr.trim() && !isControlTypeName(dataFieldIdAttr.trim())) ? dataFieldIdAttr.trim() : extractFieldCode(rawId);
      if (!fieldId && el.id && typeof el.id === 'string' && el.id.toLowerCase().startsWith('checkbox_')) {
        const parts = el.id.split('_');
        const candidate = parts.length >= 2 && parts[1] ? parts[1].trim() : '';
        if (candidate && candidate.length <= 5 && !isControlTypeName(candidate)) fieldId = candidate;
        if (!fieldId && parts.length >= 3 && parts[2] && /^[A-Za-z0-9]{4,5}$/.test(parts[2]) && !isControlTypeName(parts[2])) fieldId = parts[2].trim();
      }
      if (!fieldId && el.name) fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      if (fieldId && fieldId.length > 5) fieldId = fieldId.substring(0, 5);
      if (!fieldId || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      if (!checkboxGroupsByFieldId.has(fieldId)) checkboxGroupsByFieldId.set(fieldId, []);
      checkboxGroupsByFieldId.get(fieldId).push(el);
    });
    checkboxGroupsByFieldId.forEach((inputs, fieldId) => {
      if (isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      const checkedValues = inputs.filter((c) => c.checked).map((c) => (c.value || '').trim()).filter(Boolean);
      let value = checkedValues.length ? checkedValues.join(',') : '';
      const firstInput = inputs[0];
      const rawId = (firstInput.id || firstInput.name || '').trim();
      const commentsBase = rawId.replace(/_answer$/i, '');
      const row = firstInput?.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${commentsBase}_comments"], [id="${fieldId}_comments"], [name="${commentsBase}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="checkbox_${fieldId}_comments"], [name="checkbox_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    // Third pass: text, select, textarea, file (non-radio, non-checkbox) — excludes hidden and StandardDate/StandardDateTime part dropdowns (collected in fourth pass)
    const controls = Array.from(
      content.querySelectorAll(
        'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]), select:not(.standarddate-select):not(.standarddatetime-select), textarea'
      )
    ).filter((el) => !isCrfCommentFieldControl(el));
    controls.forEach((el) => {
      const dataFieldId = el.getAttribute?.('data-field-id');
      const rawId = (el.id || el.name || '').trim();
      let fieldId = (dataFieldId?.trim() && !isControlTypeName(dataFieldId)) ? dataFieldId.trim() : extractFieldCode(rawId);
      if (!fieldId && el.name && el.type === 'file') {
        fieldId = fieldIdFromName(el.name) || extractFieldCode(el.name.trim());
      }
      if (!fieldId || fieldId.length > 5 || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      let value = '';
      // STS AutoCapture: never save placeholder/loading/error into CRF values.
      if (el.getAttribute?.('data-sts-autocapture') === 'true') {
        const isLoading = el.getAttribute?.(STS_AUTOCAPTURE_LOADING_ATTR) === 'true';
        const isError = el.getAttribute?.(STS_AUTOCAPTURE_ERROR_ATTR) === 'true';
        if (isLoading || isError) value = '';
        else value = (el.value || '').trim();
      } else if (el.type === 'file') {
        value = (el.getAttribute('data-uploaded-path') || (el.value || '').trim()) || '';
      } else {
        value = (el.value || '').trim();
      }
      const commentsBase = rawId.replace(/_answer$/i, '');
      const row = el.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${commentsBase}_comments"], [id="${fieldId}_comments"], [name="${commentsBase}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    // Fourth pass: StandardDate and StandardDateTime — placeholder-only values saved as '' (see computeStandardDateFieldValueForSave).
    content.querySelectorAll('input.standarddate-text, input.standarddatetime-text').forEach((el) => {
      if (isCrfCommentFieldControl(el)) return;
      const rawId = (el.id || el.name || '').trim();
      const fieldId = extractFieldCode(rawId);
      if (!fieldId || fieldId.length > 5 || seen.has(fieldId) || isControlTypeName(fieldId)) return;
      seen.add(fieldId);
      let value = computeStandardDateFieldValueForSave(content, el, formatStandardDateValueForSave, formatStandardDateTimeValueForSave);
      const row = el.closest?.('.crf-report-row');
      const commentsEl = (row ? row.querySelector(rowCommentsTextareaSel) : null) ||
        content.querySelector(`[id="${fieldId}_comments"], [name="${fieldId}_comments"]`) ||
        content.querySelector(`[id="reportfield_${fieldId}_comments"], [name="reportfield_${fieldId}_comments"]`) ||
        content.querySelector(`[id="textbox_${fieldId}_comments"], [name="textbox_${fieldId}_comments"]`) ||
        content.querySelector(`[id="textarea_${fieldId}_comments"], [name="textarea_${fieldId}_comments"]`);
      const comment = commentsEl ? (commentsEl.value || '').trim() : '';
      fields.push({ vFieldId: fieldId, vFieldValue: value, vComment: comment || null });
    });

    return fields;
  };

  // Get current value for a single field from the form DOM (for DCF validation: compare with saved value).
  const getCurrentFieldValueFromForm = React.useCallback((fieldId) => {
    const content = crfContainerRef.current?.querySelector?.('.crf-generated-content') ?? document.querySelector('.crf-generated-content');
    if (!content || !fieldId) return '';
    const vFieldId = String(fieldId).trim();
    const stdInAnswer = content.querySelector(
      `.crf-answer-cell input.standarddate-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-col input.standarddate-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-cell input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"], .crf-answer-col input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"]`
    );
    const stdHidden =
      stdInAnswer ||
      content.querySelector(`input.standarddate-text[id*="${CSS.escape(vFieldId)}"], input.standarddatetime-text[id*="${CSS.escape(vFieldId)}"]`);
    if (stdHidden) {
      return computeStandardDateFieldValueForSave(content, stdHidden, formatStandardDateValueForSave, formatStandardDateTimeValueForSave);
    }
    const input = content.querySelector(`.crf-answer-cell [data-field-id="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-col [data-field-id="${vFieldId}"]`) ||
      content.querySelector(`[data-field-id="${vFieldId}"]`) ||
      content.querySelector(`[id="reportfield_${vFieldId}_answer"]`) ||
      content.querySelector(`[id="textbox_${vFieldId}"]`) ||
      content.querySelector(`[id="formula_${vFieldId}"]`) ||
      content.querySelector(`[id="textarea_${vFieldId}"]`) ||
      content.querySelector(`[id="combobox_${vFieldId}"]`) ||
      content.querySelector(`[id="datetime_${vFieldId}"]`) ||
      content.querySelector(`[id="time_${vFieldId}"]`) ||
      content.querySelector(`[id="file_${vFieldId}"]`) ||
      content.querySelector(`[name="reportfield_${vFieldId}_answer"]`) ||
      content.querySelector(`[name="textbox_${vFieldId}"]`) ||
      content.querySelector(`[name="textarea_${vFieldId}"]`) ||
      content.querySelector(`input[name="radio_${vFieldId}"], input[name^="radio_${vFieldId}_"]`) ||
      content.querySelector(`input[id^="radio_${vFieldId}_"], input[id="radio_${vFieldId}"]`) ||
      content.querySelector(`input[name="checkbox_${vFieldId}"], input[name^="checkbox_${vFieldId}_"]`) ||
      content.querySelector(`input[id="checkbox_${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[name="radio_${vFieldId}"], .crf-answer-col input[name="radio_${vFieldId}"], .crf-answer-cell input[name^="radio_${vFieldId}_"], .crf-answer-col input[name^="radio_${vFieldId}_"]`) ||
      content.querySelector(`.crf-answer-cell input[name="checkbox_${vFieldId}"], .crf-answer-col input[name="checkbox_${vFieldId}"], .crf-answer-cell input[name^="checkbox_${vFieldId}_"], .crf-answer-col input[name^="checkbox_${vFieldId}_"]`) ||
      content.querySelector(`.crf-answer-cell input[id*="${vFieldId}"], .crf-answer-cell select[id*="${vFieldId}"], .crf-answer-cell textarea[id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-col input[id*="${vFieldId}"], .crf-answer-col select[id*="${vFieldId}"], .crf-answer-col textarea[id*="${vFieldId}"]`) ||
      content.querySelector(`[id$="_answer"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="file"][id*="${vFieldId}"], .crf-answer-col input[type="file"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="date"][id*="${vFieldId}"], .crf-answer-col input[type="date"][id*="${vFieldId}"]`) ||
      content.querySelector(`.crf-answer-cell input[type="datetime-local"][id*="${vFieldId}"], .crf-answer-col input[type="datetime-local"][id*="${vFieldId}"]`) ||
      content.querySelector(`input[id*="${vFieldId}"][id$="_answer"]:not([id*="_comments"]), select[id*="${vFieldId}"], textarea[id*="${vFieldId}"]:not([id*="_comments"])`) ||
      content.querySelector(`input[name*="${vFieldId}"]:not([name*="_comments"]), textarea[name*="${vFieldId}"]:not([name*="_comments"])`) ||
      content.querySelector(`input[type="date"][id*="${vFieldId}"]`) ||
      content.querySelector(`input[type="datetime-local"][id*="${vFieldId}"]`);
    if (!input || isCrfCommentRegionElement(input)) return '';
    if (input.type === 'checkbox') {
      // RAMP-style: one field can have multiple checkboxes (same name); return comma-separated checked values
      const checkboxes = content.querySelectorAll(`input[type="checkbox"][name="${input.name}"]`);
      if (checkboxes.length > 1) {
        const checkedValues = Array.from(checkboxes).filter((cb) => cb.checked).map((cb) => (cb.value || '').trim()).filter(Boolean);
        return checkedValues.join(',');
      }
      return input.checked ? (input.value || 'Yes').trim() : '';
    }
    if (input.type === 'radio') {
      const radios = content.querySelectorAll(`input[name="${input.name}"]`);
      const checked = Array.from(radios).find((r) => r.checked);
      return (checked?.value || '').trim() || 'Yes';
    }
    let val = (input.value || '').trim();
    if (input.classList?.contains('standarddate-text') && val) {
      const alreadySaved = /^(.{2})-([A-Za-z]{3})-(\d{4})$/.test(val);
      if (!alreadySaved) val = formatStandardDateValueForSave(val);
    } else if (input.classList?.contains('standarddatetime-text') && val) {
      const alreadySaved = /^(.{2})-([A-Za-z]{3})-(\d{4})\s+(\d{2}):(\d{2})$/.test(val);
      if (!alreadySaved) val = formatStandardDateTimeValueForSave(val);
    }
    if (input.classList?.contains('standarddate-text') || input.classList?.contains('standarddatetime-text')) {
      if (isPlaceholderStandardDateValue(val)) return '';
    }
    return val;
  }, [formatStandardDateValueForSave, formatStandardDateTimeValueForSave]);

  const hasAtLeastOneValue = (fields) => {
    return Array.isArray(fields) && fields.some(
      f => ((f.vFieldValue || '').trim() !== '' || (f.vComment || '').trim() !== '')
    );
  };

  const hasDataChange = (currentFields, savedFieldsList) => {
    if (!Array.isArray(savedFieldsList) || savedFieldsList.length === 0) return true;
    const savedMap = new Map();
    savedFieldsList.forEach((f) => {
      const id = (f.vFieldId ?? f.VFieldId ?? '').toString().trim();
      if (!id) return;
      const val = (f.vFieldValue ?? f.VFieldValue ?? '').toString().trim();
      const comment = (f.vComment ?? f.VComment ?? '').toString().trim();
      savedMap.set(id.toLowerCase(), { val, comment });
    });
    for (const f of currentFields) {
      const id = (f.vFieldId ?? '').toString().trim();
      if (!id) continue;
      const currentVal = (f.vFieldValue ?? '').toString().trim();
      const currentComment = (f.vComment ?? '').toString().trim();
      const saved = savedMap.get(id.toLowerCase());
      if (!saved) return true;
      if (currentVal !== saved.val || currentComment !== saved.comment) return true;
    }
    return false;
  };

  const handleDraftSave = async () => {
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required to save.' });
      return;
    }
    const fields = collectFormData();
    if (nCRFHdrNo != null && savedFields?.length > 0 && !hasDataChange(fields, savedFields)) {
      setToast({ type: 'error', message: reportMessages.noDataToSave });
      return;
    }
    // Allow empty draft on first save (Start Report → Draft Save with no data) so user can save and continue later
    setSaving(true);
    setSaveError(null);
    try {
      const nCRFHdrNoSafe = (nCRFHdrNo != null && nCRFHdrNo !== undefined && !Number.isNaN(Number(nCRFHdrNo))) ? Number(nCRFHdrNo) : null;
      const payload = {
        nCRFHdrNo: nCRFHdrNoSafe,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: false,
        fields
      };
      const res = await api.post('/crf/page/save', payload);
      if (res.data?.nCRFHdrNo) setNCRFHdrNo(res.data.nCRFHdrNo);
      setSaveError(null);
      if (typeof onDraftSaved === 'function' && visitId) onDraftSaved(visitId, res.data?.nCRFHdrNo);
      // Refetch page so savedFields get nCRFDtlNo (needed for Raise DCF)
      try {
        const pageRes = await api.get(`/crf/page/${res.data.nCRFHdrNo}`);
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        setSavedFields(Array.isArray(fields) ? fields : []);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
      } catch (_) { /* non-fatal */ }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save draft';
      setSaveError(msg);
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    // Form submit (e.g. Enter key): do not auto-submit to First Review; require explicit Submit button with e-sign
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required.' });
      return;
    }
    setSaveError(null);
  };

  // CRA Submit Report: validate required (NotNull) fields, then DCF-generated fields must be updated, then require e-sign
  const handleSubmitReport = () => {
    if (!effectiveTemplateId || !visitId) {
      setToast({ type: 'error', message: 'Template and Visit are required to submit.' });
      return;
    }
    const fields = collectFormData();
    const fieldByIdLower = {};
    fields.forEach((f) => {
      const id = (f.vFieldId || '').trim();
      if (id) fieldByIdLower[id.toLowerCase()] = f;
    });
    const fieldForCode = (code) => fieldByIdLower[String(code || '').trim().toLowerCase()];
    // Label-only rows (template type Label) have no input — exclude them from required validation
    const content = crfContainerRef.current?.querySelector('.crf-generated-content');
    const labelOnlyCodes = content
      ? new Set(
        Array.from(content.querySelectorAll('.crf-report-row-label-only[data-medex-code]'))
          .map((row) => (row.getAttribute('data-medex-code') || '').trim())
          .filter(Boolean)
      )
      : new Set();
    // Treat dropdown placeholder values as empty so "All fields are required" when user leaves SelectBox on "Select" (value -1 or __default_combobox__value__).
    const isPlaceholderSelectValue = (val) => {
      const v = (val || '').trim();
      if (!v) return true;
      if (v === '-1') return true;
      if (v.toLowerCase() === 'select') return true;
      if (v === '__default_combobox__value__') return true;
      return false;
    };
    const requiredToCheck = (requiredFieldCodes || []).filter(
      (code) => !labelOnlyCodes.has(String(code || '').trim())
    );
    const missing = requiredToCheck.filter((code) => {
      const f = fieldForCode(code);
      const val = (f?.vFieldValue || '').trim();
      const com = (f?.vComment || '').trim();
      if (!val && !com) return true;
      if (val) {
        if (isPlaceholderSelectValue(val)) return !com;
        if (isPlaceholderOnlyStandardDateOrDateTime(val)) return !com;
      }
      return false;
    });
    if (missing.length > 0) {
      setSaveError(null);
      setToast({ type: 'error', message: reportMessages.allFieldsRequired });
      return;
    }
    // DCF-generated fields (Open or Resolved DCF): CRA must update at least one before Submit
    const dcfSavedFields = (savedFields || []).filter((f) => {
      const status = (f.vDCFStatus ?? f.VDCFStatus ?? '').toString().trim().toLowerCase();
      return status === 'open' || status === 'resolved';
    });
    const dcfFieldIds = dcfSavedFields.map((f) => (f.vFieldId ?? f.VFieldId ?? '').toString().trim()).filter(Boolean);
    if (dcfFieldIds.length > 0) {
      const dcfFieldNotFilled = dcfFieldIds.find((id) => {
        const cur = fieldForCode(id);
        const v = (cur?.vFieldValue ?? '').trim();
        const c = (cur?.vComment ?? '').trim();
        return !v && !c;
      });
      if (dcfFieldNotFilled !== undefined) {
        setToast({ type: 'error', message: reportMessages.updateDcfGeneratedField });
        return;
      }
      const atLeastOneDcfUpdated = dcfSavedFields.some((f) => {
        const id = (f.vFieldId ?? f.VFieldId ?? '').toString().trim();
        if (!id) return false;
        const cur = fieldForCode(id);
        const currentVal = (cur?.vFieldValue ?? '').trim();
        const currentCom = (cur?.vComment ?? '').trim();
        const saved = (f.vFieldValue ?? f.VFieldValue ?? '').toString().trim();
        const savedCom = (f.vComment ?? f.VComment ?? '').toString().trim();
        return currentVal !== saved || currentCom !== savedCom;
      });
      if (!atLeastOneDcfUpdated) {
        setToast({ type: 'error', message: reportMessages.updateDcfGeneratedField });
        return;
      }
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('submit');
    setShowESignModal(true);
  };

  const doSubmitReport = async (vRemarks) => {
    if (!effectiveTemplateId || !visitId) return;
    const fields = collectFormData();
    setSaving(true);
    setSaveError(null);
    try {
      const nCRFHdrNoSafe = (nCRFHdrNo != null && nCRFHdrNo !== undefined && !Number.isNaN(Number(nCRFHdrNo))) ? Number(nCRFHdrNo) : null;
      const payload = {
        nCRFHdrNo: nCRFHdrNoSafe,
        vTemplateNo: effectiveTemplateId,
        vVisitType: visitType,
        iVisitNodeId: parseInt(visitId, 10),
        isSubmit: true,
        fields,
        vRemarks: vRemarks || null
      };
      const res = await api.post('/crf/page/save', payload);
      if (res.data?.nCRFHdrNo) setNCRFHdrNo(res.data.nCRFHdrNo);
      setSaveError(null);
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onSubmitSuccess === 'function') onSubmitSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to submit';
      setSaveError(null);
      setToast({ type: 'error', message: msg });
    } finally {
      setSaving(false);
    }
  };

  const doApprove = async (vRemarks) => {
    if (!nCRFHdrNo) return;
    setTransitioning(true);
    setSaveError(null);
    try {
      await api.post(`/crf/page/${nCRFHdrNo}/transition`, { action: 'Approve', vRemarks: vRemarks || undefined });
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onApproveSendbackSuccess === 'function') onApproveSendbackSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Approve failed.';
      setToast({ type: 'error', message: msg });
    } finally {
      setTransitioning(false);
    }
  };

  const handleApprove = () => {
    if (!nCRFHdrNo) return;
    if (hasOpenDCF) return;
    eSignRemarksRef.current = '';
    setESignPendingAction('approve');
    setShowESignModal(true);
  };

  const doReject = async (vRemarks) => {
    if (!nCRFHdrNo) return;
    setTransitioning(true);
    setSaveError(null);
    try {
      await api.post(`/crf/page/${nCRFHdrNo}/transition`, { action: 'Reject', vRemarks: vRemarks || undefined });
      setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onApproveSendbackSuccess === 'function') onApproveSendbackSuccess();
      if (onClose) onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Send Back failed.';
      setToast({ type: 'error', message: msg });
    } finally {
      setTransitioning(false);
    }
  };

  const handleReject = () => {
    if (!nCRFHdrNo) return;
    if (hasOpenDCF) return;
    eSignRemarksRef.current = '';
    setESignPendingAction('reject');
    setShowESignModal(true);
  };

  // Optimistically update savedFields state so UI colors/icons reflect change immediately
  const updateLocalFieldStatus = (nCRFDtlNo, status, remark, response) => {
    setSavedFields(prev => {
      return prev.map(f => {
        const fNo = f.nCRFDtlNo ?? f.NCRFDtlNo ?? f.ncrfDtlNo;
        if (Number(fNo) === Number(nCRFDtlNo)) {
          const out = { ...f, vDCFStatus: status, vDCFRemark: remark ?? f.vDCFRemark, VDCFStatus: status, VDCFRemark: remark ?? f.VDCFRemark };
          if (response !== undefined) {
            out.vDCFResponse = response ?? f.vDCFResponse;
            out.VDCFResponse = response ?? f.VDCFResponse;
          }
          return out;
        }
        return f;
      });
    });
  };

  const doRaiseDCF = async (vRemarks) => {
    if (!nCRFHdrNo || dcfSelected.nCRFDtlNo == null) return;
    setDcfSubmitting(true);
    try {
      await api.post(`/crf/page/${nCRFHdrNo}/dcf`, {
        nCRFDtlNo: dcfSelected.nCRFDtlNo,
        vDCFRemark: dcfRemark.trim(),
        vDCFRaisedByProfileCode: user?.role || 'Unknown',
        vRemarks: vRemarks || null
      });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Open', dcfRemark.trim());
      setDcfRemark('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      setHasOpenDCF(true);
      setDcfModalOpen(false);
      if (nCRFHdrNo) {
        const pageRes = await api.get(`/crf/page/${nCRFHdrNo}`);
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        setHasOpenDCF(pageRes.data?.hasOpenDCF ?? pageRes.data?.HasOpenDCF ?? false);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        setPermissionsRefreshKey((k) => k + 1); // Re-apply field enable/disable without page refresh
      }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to create DCF.';
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const handleCRFDCFSubmit = async (e) => {
    e.preventDefault();
    const info = getDCFInfo();
    const statusLower = (info.status || '').trim().toLowerCase();
    const alreadyRaised = statusLower === 'open' || statusLower === 'resolved';
    if (alreadyRaised) {
      setToast({ type: 'error', message: dcfMessages.alreadyRaised });
      return;
    }
    // Allow in First/Second Review, or when there are already open DCFs (Reviewer adding more on another field)
    const canRaiseDCF = isReviewStage || hasOpenDCF;
    if (!canRaiseDCF) {
      setToast({ type: 'error', message: 'Raise DCF is only available in First Review or Second Review.' });
      return;
    }
    if (!dcfRemark.trim()) {
      setDcfRemarkError('Please Enter Remarks.');
      return;
    }
    if (dcfSelected.nCRFDtlNo == null || dcfSelected.nCRFDtlNo === undefined || dcfSelected.nCRFDtlNo === 0) {
      setToast({ type: 'error', message: 'Cannot create DCF: this field is not linked to a saved row. Save the form first.' });
      return;
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('raiseDCF');
    setShowESignModal(true);
  };

  const doSubmitDCFResponse = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    setDcfSubmitting(true);
    try {
      await api.post(`/crf/page/dcf/${dcfSelected.nCRFDtlNo}/response`, {
        vDCFResponse: dcfResponse.trim() || null,
        vRemarks: vRemarks || null
      });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Resolved', null, dcfResponse.trim() || null);
      if (nCRFHdrNo) {
        const pageRes = await api.get(`/crf/page/${nCRFHdrNo}`);
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        const hasOpen = pageRes.data?.hasOpenDCF ?? pageRes.data?.HasOpenDCF ?? false;
        setHasOpenDCF(hasOpen);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        setPermissionsRefreshKey((k) => k + 1);
      }
      if (typeof onDcfSaveSuccess === 'function') onDcfSaveSuccess();
      else setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save comment.';
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const doResolveDCF = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    setDcfSubmitting(true);
    try {
      const body = { vRemarks: vRemarks || null };
      if (dcfResponse.trim()) body.vDCFResponse = dcfResponse.trim();
      await api.post(`/crf/page/dcf/${dcfSelected.nCRFDtlNo}/resolve`, body);
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Resolved', null, dcfResponse.trim() || undefined);
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfResponse('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      if (nCRFHdrNo) {
        const pageRes = await api.get(`/crf/page/${nCRFHdrNo}`);
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        setHasOpenDCF(pageRes.data?.hasOpenDCF ?? pageRes.data?.HasOpenDCF ?? false);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        setPermissionsRefreshKey((k) => k + 1);
      }
      if (typeof onDcfSaveSuccess === 'function') onDcfSaveSuccess();
      else setToast({ type: 'success', message: commonMessages.authSuccess });
      if (typeof onClose === 'function') onClose();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to resolve DCF.';
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const doCloseDCF = async (vRemarks) => {
    if (!dcfSelected.nCRFDtlNo) return;
    setDcfSubmitting(true);
    try {
      await api.post(`/crf/page/dcf/${dcfSelected.nCRFDtlNo}/close`, { vRemarks: vRemarks || null });
      updateLocalFieldStatus(dcfSelected.nCRFDtlNo, 'Closed');
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      if (nCRFHdrNo) {
        const pageRes = await api.get(`/crf/page/${nCRFHdrNo}`);
        const fields = pageRes.data?.fields ?? pageRes.data?.Fields ?? [];
        if (Array.isArray(fields)) setSavedFields(fields);
        setHasOpenDCF(pageRes.data?.hasOpenDCF ?? pageRes.data?.HasOpenDCF ?? false);
        setWorkflowHeader(pageRes.data?.workflowHeader ?? pageRes.data?.WorkflowHeader ?? null);
        setPermissionsRefreshKey((k) => k + 1);
      }
      setToast({ type: 'success', message: commonMessages.authSuccess });
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to close DCF.';
      setToast({ type: 'error', message: msg });
    } finally {
      setDcfSubmitting(false);
    }
  };

  const runAfterESign = () => {
    const vRemarks = eSignRemarksRef.current || '';
    const action = eSignPendingAction;
    setShowESignModal(false);
    setESignPendingAction(null);
    eSignRemarksRef.current = '';
    if (action === 'raiseDCF') doRaiseDCF(vRemarks);
    else if (action === 'resolveDCF') doResolveDCF(vRemarks);
    else if (action === 'submitDCFResponse') doSubmitDCFResponse(vRemarks);
    else if (action === 'closeDCF') doCloseDCF(vRemarks);
    else if (action === 'approve') doApprove(vRemarks);
    else if (action === 'reject') doReject(vRemarks);
    else if (action === 'submit') doSubmitReport(vRemarks);
    else if (action === 'closeActionItem') doCloseActionItem(vRemarks);
    else if (action === 'updateActionItemRemark') doUpdateActionItemRemark();
  };

  const doUpdateActionItemRemark = async () => {
    const payload = actionItemUpdateRef.current;
    actionItemUpdateRef.current = null;
    if (!payload?.actionItemID || !payload.replyRemark) return;
    const submitToCreator = payload.submitToCreator === true;
    setActionItemSaving(true);
    try {
      const res = await api.post(`/action-items/${payload.actionItemID}/update`, {
        replyRemark: payload.replyRemark,
        submitToCreator,
      });
      const nextStatus = (res.data?.status ?? (submitToCreator ? 'Resolved' : 'Open')).toString();
      setActionItemsList((prev) =>
        prev.map((x) =>
          (x.actionItemID ?? x.nCRFActionItemNo) === payload.actionItemID
            ? { ...x, status: nextStatus, replyRemark: payload.replyRemark }
            : x
        )
      );
      if (submitToCreator) {
        setToast({ type: 'success', message: actionItemMessages.issueSubmittedToCreatorSuccess });
        dispatchNotificationsRefresh();
        setActionItemsModalOpen(false);
        setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
        setActionItemsList([]);
        setSelectedActionItemIndex(0);
        setActionReplyRemark('');
      } else {
        setToast({ type: 'success', message: actionItemMessages.remarkSavedSuccess });
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to update remark.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemSaving(false);
    }
  };

  const doCloseActionItem = async (vRemarks) => {
    const payload = actionItemCloseRef.current;
    actionItemCloseRef.current = null;
    if (!payload?.actionItemID) return;
    setActionItemSaving(true);
    try {
      await api.post(`/action-items/${payload.actionItemID}/close`, {
        remark: (vRemarks || '').trim() || undefined
      });
      setActionItemsList((prev) => prev.map((x) => (x.actionItemID === payload.actionItemID ? { ...x, status: 'Closed' } : x)));
      setToast({ type: 'success', message: commonMessages.authSuccess });
      dispatchNotificationsRefresh();
      setActionItemsModalOpen(false);
      setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      setActionItemsList([]);
      setSelectedActionItemIndex(0);
      setActionReplyRemark('');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to close issue.';
      setToast({ type: 'error', message: msg });
    } finally {
      setActionItemSaving(false);
    }
  };

  const handleSubmitDCFResponse = () => {
    if (!dcfSelected.nCRFDtlNo) return;
    if (!dcfResponse.trim()) {
      setToast({ type: 'error', message: dcfMessages.remarksOrUpdateRequired });
      return;
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('submitDCFResponse');
    setShowESignModal(true);
  };

  const handleResolveDCF = () => {
    if (!dcfSelected.nCRFDtlNo) {
      setDcfModalOpen(false);
      setDcfRemark('');
      setDcfResponse('');
      setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' });
      return;
    }
    const no = Number(dcfSelected.nCRFDtlNo);
    const savedField = savedFields?.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no)
      || savedFields?.find(x => (x.vFieldId || x.VFieldId) === dcfSelected.fieldId)
      || savedFields?.find(x => ((x.vFieldId || x.VFieldId) || '').toLowerCase() === (dcfSelected.fieldId || '').toLowerCase());
    const savedValue = (savedField?.vFieldValue ?? savedField?.VFieldValue ?? '').trim();
    const currentValue = getCurrentFieldValueFromForm(dcfSelected.fieldId);
    const fieldUnchanged = (currentValue === savedValue) || (currentValue === (savedField?.vFieldValue ?? savedField?.VFieldValue ?? ''));
    if (fieldUnchanged && !dcfResponse.trim()) {
      setToast({ type: 'error', message: dcfMessages.remarksOrUpdateRequired });
      return;
    }
    eSignRemarksRef.current = '';
    setESignPendingAction('resolveDCF');
    setShowESignModal(true);
  };

  const handleFinalCloseDCF = () => {
    if (!dcfSelected.nCRFDtlNo) return;
    eSignRemarksRef.current = '';
    setESignPendingAction('closeDCF');
    setShowESignModal(true);
  };

  // Helper to check DCF Status (for the opened field row). Prefer nCRFDtlNo so we always get the correct row when opening details.
  const getDCFInfo = () => {
    if (!savedFields) return { status: null, remarks: '' };
    let f = null;
    if (dcfSelected.nCRFDtlNo != null && dcfSelected.nCRFDtlNo !== 0) {
      const no = Number(dcfSelected.nCRFDtlNo);
      f = savedFields.find(x => Number(x.nCRFDtlNo ?? x.NCRFDtlNo ?? x.ncrfDtlNo ?? 0) === no);
    }
    if (!f && dcfSelected.fieldId) {
      f = savedFields.find(x => (x.vFieldId || x.VFieldId) === dcfSelected.fieldId);
    }
    if (!f && dcfSelected.fieldId) {
      f = savedFields.find(x => (x.vFieldId || x.VFieldId || '').toLowerCase() === dcfSelected.fieldId.toLowerCase());
    }
    const status = f?.vDCFStatus ?? f?.VDCFStatus ?? null;
    return {
      status,
      remarks: f?.vDCFRemark || f?.VDCFRemark,
      raisedByProfile: f?.vDCFRaisedByProfileCode || f?.VDCFRaisedByProfileCode,
      response: f?.vDCFResponse || f?.VDCFResponse || '',
      isResolved: (status || '').toLowerCase() === 'resolved'
    };
  };

  const dcfInfo = getDCFInfo();

  // When savedFields is stale, derive Resolved from DCF history (latest action "Resolved DCF" or "CRA Response")
  const latestAction = (dcfHistory && dcfHistory.length > 0)
    ? (dcfHistory[0].action || dcfHistory[0].Action || '').toLowerCase()
    : '';
  const isResolvedFromHistory = latestAction === 'resolved dcf' || latestAction === 'cra response';
  const isResolved = dcfInfo.isResolved || isResolvedFromHistory;

  // Allow "Raise DCF" only when no DCF is open on this field (status empty/null or closed). When Open or Resolved, show message instead.
  const dcfStatusLower = (dcfInfo.status || '').trim().toLowerCase();
  const hasRaisedInHistory = dcfHistory.length > 0 && (latestAction === 'raised dcf' || (dcfHistory[0].action || dcfHistory[0].Action || '').toLowerCase().includes('raised'));
  const dcfAlreadyRaisedOnField = dcfStatusLower === 'open' || isResolved || (hasRaisedInHistory && dcfStatusLower !== 'closed');
  const canRaiseNewDcf = !dcfAlreadyRaisedOnField;

  // Show Close DCF when Resolved and (no raiser profile set, or current user profile matches vDCFRaisedByProfileCode)
  const canCloseDCF = isResolved && (
    !dcfInfo.raisedByProfile ||
    !currentUserProfile ||
    (currentUserProfile.toLowerCase() === (dcfInfo.raisedByProfile || '').toLowerCase())
  );

  // When CRA has DCF-generated fields (Open or Resolved) to fill, hide Draft Save so they must Submit after updating (avoids "Please update field" after draft save).
  const hasDcfGeneratedFieldsToFill = (savedFields || []).filter((f) => {
    const status = (f.vDCFStatus ?? f.VDCFStatus ?? '').toString().trim().toLowerCase();
    return status === 'open' || status === 'resolved';
  }).length > 0;

  if (loading) {
    return (
      <div className="crf-form-container">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="crf-form-container">
        <div className="alert alert-danger">
          <strong>Error:</strong> {error}
        </div>
      </div>
    );
  }

  const handleClose = () => {
    if (isPreviewInNewTab) {
      window.close();
      return;
    }
    if (onClose) onClose();
    else window.history.back();
  };

  return (
    <div ref={crfContainerRef} className={`crf-form-container${isReviewStage ? ' crf-form-review-stage' : ''}`}>
      <div className="crf-form-wrapper">
        {/* Form Header */}
        <div className="crf-form-header">
          <div className="crf-form-title">
            <h2>Report : {reportTitle}</h2>
            <button className="crf-close-btn" onClick={handleClose}>
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form className="crf-form" onSubmit={handleFormSubmit}>
          {/* Metadata Section */}
          <div className="crf-metadata-section">
            <div className="crf-metadata-grid">
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Project:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.protocolNo}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Site:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.siteNumber}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">PI Name:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.piName}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Visit Date:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formData.visitDate}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Submit By:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={workflowHeader?.submittedBy || workflowHeader?.SubmittedBy || ''}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">First Review By:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formatWorkflowReviewerDisplay(workflowHeader?.reviewedBy ?? workflowHeader?.ReviewedBy)}
                  readOnly
                />
              </div>
              <div className="crf-metadata-item">
                <label className="crf-metadata-label">Second Review By:</label>
                <input
                  type="text"
                  className="crf-metadata-input"
                  value={formatWorkflowReviewerDisplay(workflowHeader?.approvedBy ?? workflowHeader?.ApprovedBy)}
                  readOnly
                />
              </div>

            </div>
          </div>

          {/* Generated CRF Content - use memoized __html so opening DCF/Audit modals does not wipe answers/comments */}
          <div
            className="crf-generated-content"
            dangerouslySetInnerHTML={dangerousContent}
          />

          {/* Form Footer */}
          <div className="crf-form-footer">
            {saveError && <div className="alert alert-danger mb-2">{saveError}</div>}


            <div className="crf-form-actions">
              {/* Draft Save and Submit: CRA profile only. Hide Draft Save when there are DCF-generated fields (Open/Resolved) so CRA must Submit after filling them. */}
              {isCraUser && !isViewOnly && availableActions.includes('DraftSave') && !props.readOnly && !hasDcfGeneratedFieldsToFill && (
                <button type="button" className="btn btn-primary" onClick={handleDraftSave} disabled={saving}>
                  {saving ? 'Saving...' : 'Draft Save'}
                </button>
              )}
              {isCraUser && !isViewOnly && availableActions.includes('Submit') && !props.readOnly && (
                <button type="button" className="btn btn-primary" onClick={handleSubmitReport} disabled={saving}>
                  {saving ? 'Submitting...' : 'Submit'}
                </button>
              )}
              {!isViewOnly && canApproveReject && (isReviewStage || availableActions.includes('Approve') || props.reviewMode) && (
                <button type="button" className="btn btn-success" onClick={handleApprove} disabled={transitioning || hasOpenDCF}>
                  {transitioning ? 'Processing...' : 'Approve'}
                </button>
              )}
              {!isViewOnly && canApproveReject && (isReviewStage || availableActions.includes('Reject') || props.reviewMode) && (
                <button type="button" className="btn btn-danger" onClick={handleReject} disabled={transitioning || hasOpenDCF}>
                  {transitioning ? 'Processing...' : 'Send Back'}
                </button>
              )}
              {!isPreviewInNewTab && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => setShowPreviewChoiceModal(true)}
                  disabled={!nCRFHdrNo}
                >
                  Preview
                </button>
              )}
              {isCraUser && !isViewOnly && availableActions.includes('DraftSave') && !props.readOnly && !hasDcfGeneratedFieldsToFill && (
                <button type="button" className="btn btn-primary" onClick={handleClearForm} disabled={saving}>
                  Clear
                </button>
              )}
              <button type="button" className="btn btn-primary" onClick={handleClose}>
                Close
              </button>
            </div>
          </div>
        </form>

        {/* Create Action Item modal (same as Action Items tab in Visit Management) */}
        <CreateActionItemModal
          isOpen={isCreateActionItemModalOpen}
          onClose={() => {
            setIsCreateActionItemModalOpen(false);
            setCreateActionItemFieldCode(null);
            setCreateActionItemNCRFDtlNo(null);
            createActionItemPayloadRef.current = null;
          }}
          onSave={() => {
            setIsCreateActionItemModalOpen(false);
            setCreateActionItemFieldCode(null);
            setCreateActionItemNCRFDtlNo(null);
            createActionItemPayloadRef.current = null;
            if (typeof onActionItemCreated === 'function') onActionItemCreated();
          }}
          defaultProject={defaultProjectForActionItem}
          defaultSiteId={defaultSiteIdForActionItem}
          defaultSiteName={defaultSiteNameForActionItem}
          defaultVisitId={visitId != null && visitId !== '' ? String(visitId) : ''}
          defaultVisitType={visitType}
          defaultAssignActionItemTo="Section Attributes"
          defaultNCRFDtlNo={createActionItemNCRFDtlNo != null ? Number(createActionItemNCRFDtlNo) : null}
          defaultFieldCode={createActionItemFieldCode ?? null}
          openPayloadRef={createActionItemPayloadRef}
          openedFromCRF={true}
          stageVisits={stageVisitsForActionItem}
        />

        {/* CRF Report Preview: first choose With (filled) or Without (blank) answers and comments */}
        <Modal
          isOpen={showPreviewChoiceModal}
          onClose={() => setShowPreviewChoiceModal(false)}
          title="Preview Visit Report"
          size="small"
          className="crf-preview-choice-modal"
        >
          <p className="crf-preview-choice-prompt">Choose how to preview the report:</p>
          <div className="crf-preview-choice-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePreviewWith}
            >
              View Filled Responses
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handlePreviewWithout}
            >
              View Blank Responses
            </button>
            <button
              type="button"
              className="btn btn-secondary crf-preview-choice-btn crf-preview-choice-cancel"
              onClick={() => setShowPreviewChoiceModal(false)}
            >
              Cancel
            </button>
          </div>
        </Modal>

        {/* CRF Report Preview display modal */}
        <Modal
          isOpen={showPreviewModal}
          onClose={() => {
            setShowPreviewModal(false);
            setPreviewHtml('');
            setPreviewOmitWorkflowSignerFields(false);
            setPdfDownloading(false);
            setOpenIssuesForReport([]);
            setOpenDcfsForReport([]);
          }}
          title="Visit Report Preview"
          size="large"
          className="crf-report-preview-modal"
          footer={
            <>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDownloadPreviewPdf}
                disabled={pdfDownloading || openIssuesLoading || !previewHtml}
              >
                {pdfDownloading ? 'Generating PDF...' : 'Download PDF'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowPreviewModal(false);
                  setPreviewHtml('');
                  setPreviewOmitWorkflowSignerFields(false);
                  setPdfDownloading(false);
                  setOpenIssuesForReport([]);
                  setOpenDcfsForReport([]);
                }}
              >
                Close
              </button>
            </>
          }
        >
          <div ref={previewContentRef} className="crf-report-preview-content-wrapper">
            <div className="crf-report-preview-header">
              <h2>Report : {reportTitle}</h2>
            </div>
            <div className="crf-metadata-section">
              <div className="crf-metadata-grid">
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Project:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.protocolNo || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Site:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.siteNumber || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">PI Name:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.piName || '\u00A0'}</div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Visit Date:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{formData.visitDate || '\u00A0'}</div>
                </div>

                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Submit By:</label>
                  <div className="crf-preview-field-value crf-metadata-value">
                    {previewOmitWorkflowSignerFields
                      ? '\u00A0'
                      : workflowHeader?.submittedBy || workflowHeader?.SubmittedBy || '\u00A0'}
                  </div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">First Review By:</label>
                  <div className="crf-preview-field-value crf-metadata-value">
                    {previewOmitWorkflowSignerFields
                      ? '\u00A0'
                      : formatWorkflowReviewerDisplay(workflowHeader?.reviewedBy ?? workflowHeader?.ReviewedBy) || '\u00A0'}
                  </div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Second Review By:</label>
                  <div className="crf-preview-field-value crf-metadata-value">
                    {previewOmitWorkflowSignerFields
                      ? '\u00A0'
                      : formatWorkflowReviewerDisplay(workflowHeader?.approvedBy ?? workflowHeader?.ApprovedBy) || '\u00A0'}
                  </div>
                </div>
                <div className="crf-metadata-item">
                  <label className="crf-metadata-label">Exported On:</label>
                  <div className="crf-preview-field-value crf-metadata-value">{exportedOnDisplay || '\u00A0'}</div>
                </div>
              </div>
            </div>
            <div className="crf-generated-content crf-report-preview-content" dangerouslySetInnerHTML={{ __html: previewHtml }} />
            {includeOpenIssuesInPreview && (
              <>
                <div className="crf-open-issues-section">
                  <h3 className="crf-open-issues-title">Issues (Open / Resolved)</h3>
                  <div className="table-responsive">
                    <table className="crf-open-issues-table">
                      <thead>
                        <tr>
                          <th>Reference No</th>
                          <th>Site</th>
                          <th>Visit Type</th>
                          <th>Visit Date</th>
                          <th>Issue On</th>
                          <th>Issue Description</th>
                          <th>Assigned To</th>
                          <th>Due Date</th>
                          <th>Priority</th>
                          <th>Current Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openIssuesLoading ? (
                          <tr>
                            <td colSpan="10" className="crf-open-issues-empty">Loading issues...</td>
                          </tr>
                        ) : openIssuesForReport.length > 0 ? (
                          openIssuesForReport.map((item) => {
                            const siteCell = [item.siteCode, item.siteDescription].filter(Boolean).join(' – ') || formData.siteNumber || '-';
                            const vt = item.visitType ?? item.VisitType ?? '';
                            const vd = item.visitDate ?? item.VisitDate ?? item.plannedStartDate;
                            return (
                              <tr key={item.actionItemID ?? item.nCRFActionItemNo ?? item.actionItemNumber}>
                                <td>{item.actionItemNumber ?? item.vActionItemNumber ?? '-'}</td>
                                <td>{siteCell}</td>
                                <td>{vt || '-'}</td>
                                <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
                                <td>{item.actionItemOn || '-'}</td>
                                <td>{item.findingDescription || '-'}</td>
                                <td>{item.assignedToName || item.assignedToProfileCode || '-'}</td>
                                <td>{item.dueDate ? formatDateDDMMMYYYY(item.dueDate) : '-'}</td>
                                <td>{item.priorityDescription ?? item.severity ?? '-'}</td>
                                <td>{item.status || 'Open'}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="10" className="crf-open-issues-empty">No Open or Resolved issues for this site.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="crf-open-dcfs-section">
                  <h3 className="crf-open-issues-title">DCF (Open / Resolved)</h3>
                  <div className="table-responsive">
                    <table className="crf-open-dcfs-table">
                      <thead>
                        <tr>
                          <th>Visit Type</th>
                          <th>Visit Date</th>
                          <th>Template</th>
                          <th>Question</th>
                          <th>DCF remark</th>
                          <th>Stage raised</th>
                          <th>Current Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openIssuesLoading ? (
                          <tr>
                            <td colSpan="7" className="crf-open-issues-empty">Loading DCFs...</td>
                          </tr>
                        ) : openDcfsForReport.length > 0 ? (
                          openDcfsForReport.map((d) => {
                            const vd = d.visitDate ?? d.VisitDate ?? d.plannedStartDate;
                            return (
                              <tr key={d.nCRFDtlNo ?? d.NCRFDtlNo}>
                                <td>{d.visitType ?? d.VisitType ?? '-'}</td>
                                <td>{vd ? formatDateDDMMMYYYY(vd) : '-'}</td>
                                <td>{d.vTemplateNo ?? d.VTemplateNo ?? '-'}</td>
                                <td>{d.fieldDescription ?? d.FieldDescription ?? d.vFieldId ?? '-'}</td>
                                <td>{d.vDCFRemark ?? d.VDCFRemark ?? '-'}</td>
                                <td>{d.vDCFStageRaised ?? d.VDCFStageRaised ?? '-'}</td>
                                <td>{d.vDCFStatus ?? d.VDCFStatus ?? 'Open'}</td>
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan="7" className="crf-open-issues-empty">No Open or Resolved DCFs for this site.</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </Modal>

        {/* CRF DCF Modal - wireframe: DCF Detail with 5-column table, Remarks, Raise DCF / Close */}
        {dcfModalOpen && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Modal
              isOpen={true}
              onClose={() => { setDcfModalOpen(false); setDcfRemark(''); setDcfRemarkError(''); setDcfResponse(''); setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); }}
              title="DCF Detail"
              showCloseButton={true}
              className="crf-dcf-modal"
              overlayClassName="crf-nested-modal-overlay"
            >
              <div className="table-responsive crf-dcf-modal-table-wrap">
                <table className="table crf-dcf-modal-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Remarks</th>
                      <th>Performed By</th>
                      <th>Performed On UTC</th>
                      <th>Performed At Offset</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingHistory ? (
                      <tr><td colSpan="5" className="text-center py-3">Loading history...</td></tr>
                    ) : dcfHistory.length > 0 ? (
                      dcfHistory.map((h, i) => {
                        const remarksCell = (h.remarks || h.Remarks) || '';
                        return (
                          <tr key={i}>
                            <td>{h.action || h.Action}</td>
                            <td>{remarksCell}</td>
                            <td>{h.performedBy || h.PerformedBy}</td>
                            <td>{h.performedOnUTC ? formatPerformedOnUtcToLocal(h.performedOnUTC || h.PerformedOnUTC, h.performedAtOffset || h.PerformedAtOffset) : ''}</td>
                            <td>{formatOffsetForDisplay(h.performedAtOffset || h.PerformedAtOffset)}</td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-muted text-center py-3">
                          No DCFs yet
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {/* Reviewer (PM/SRM or First/Second Review): only details + Enter Remarks + Raise/Close DCF. No CRA comment section (vDCF Response is for CRA only). CRA users always see CRA branch (DCF Response box). */}
              {showReviewerDcfView && !isCraUser ? (
                <form onSubmit={handleCRFDCFSubmit}>
                  {!isViewOnly && dcfAlreadyRaisedOnField && (
                    <div className="alert alert-warning mt-3">
                      {dcfMessages.alreadyRaised}
                    </div>
                  )}
                  {!isViewOnly && canRaiseDCFFromServer && canRaiseNewDcf && (
                    <div className="mt-4 crf-dcf-comment-box-wrap">
                      <label className="form-label">
                        Remarks <span className="required-asterisk">*</span>
                      </label>
                      <textarea
                        className="form-control crf-dcf-comment-box"
                        rows={4}
                        value={dcfRemark}
                        onChange={(e) => { setDcfRemark(e.target.value); setDcfRemarkError(''); }}
                        aria-label="Remarks for raising DCF (Reviewer only)"
                      />
                      {dcfRemarkError && (
                        <div className="field-error text-danger mt-1" style={{ fontSize: '12px' }}>
                          {dcfRemarkError}
                        </div>
                      )}
                    </div>
                  )}
                  {canCloseDCF && (
                    <div className="alert alert-info mt-3">
                      This DCF has been resolved. You can now Close it.
                    </div>
                  )}
                  <div className="modal-footer crf-dcf-modal-footer">
                    {/* Raise DCF: only for reviewer profiles (from server CanRaiseDCF); CRA does not see this */}
                    {!isViewOnly && canRaiseDCFFromServer && canRaiseNewDcf && (
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={dcfSubmitting}
                      >
                        {dcfSubmitting ? 'Creating...' : 'Raise DCF'}
                      </button>
                    )}
                    {/* Close DCF: only when Resolved and current user profile matches vDCFRaisedByProfileCode (e.g. SRM who raised can close); hide when view-only */}
                    {!isViewOnly && canCloseDCF && (
                      <button type="button" className="btn btn-success" onClick={handleFinalCloseDCF} disabled={dcfSubmitting}>{dcfSubmitting ? 'Closing...' : 'Close DCF'}</button>
                    )}

                    <button type="button" className="btn btn-secondary" onClick={() => { setDcfModalOpen(false); setDcfRemark(''); setDcfRemarkError(''); setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); }}>Cancel</button>
                  </div>
                </form>
              ) : (
                <>
                  {/* CRA View only: DCF Comment (vDCF Response). Hidden once DCF is Resolved (awaiting reviewer Close). */}
                  {dcfHistory.length > 0 ? (
                    <>
                      {!(isCraUser && isResolved) && (
                        <div className="mt-4 crf-dcf-comment-box-wrap">
                          <label className="form-label">
                            Remarks <span className="required-asterisk">*</span>
                          </label>
                          <textarea
                            className="form-control crf-dcf-comment-box"
                            rows={4}
                            value={dcfResponse}
                            onChange={(e) => setDcfResponse(e.target.value)}
                            style={{ resize: 'vertical', minHeight: '100px' }}
                            readOnly={isViewOnly}
                            aria-label="DCF response comment (CRA)"
                          />
                        </div>
                      )}
                      <div className="modal-footer crf-dcf-modal-footer">
                        {!(isCraUser && isResolved) && !isViewOnly && (
                          <button type="button" className="btn btn-primary" onClick={handleSubmitDCFResponse} disabled={dcfSubmitting}>{dcfSubmitting ? 'Saving...' : 'Submit'}</button>
                        )}
                        <button type="button" className="btn btn-secondary" onClick={() => { setDcfModalOpen(false); setDcfResponse(''); setDcfRemark(''); setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); }}>Close</button>
                      </div>
                    </>
                  ) : (
                    <div className="modal-footer crf-dcf-modal-footer">
                      <button type="button" className="btn btn-secondary" onClick={() => { setDcfModalOpen(false); setDcfResponse(''); setDcfRemark(''); setDcfSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); }}>Close</button>
                    </div>
                  )}
                </>
              )}
            </Modal>
          </div>,
          document.body
        )}

        {/* E-sign modal: before DCF/Visit actions that impact transaction tables */}
        {showESignModal && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ElectronicSign
              show={true}
              onClose={() => {
                setShowESignModal(false);
                setESignPendingAction(null);
                eSignRemarksRef.current = '';
                actionItemUpdateRef.current = null;
              }}
              onAuthSuccess={(vRemarks) => { eSignRemarksRef.current = vRemarks || ''; }}
              onSave={runAfterESign}
              onValidationError={(message) => setToast({ type: 'error', message })}
            />
          </div>,
          document.body
        )}

        {/* View Audit Modal - portaled to body in high-z wrapper (First/Second Review) */}
        {auditModalOpen && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Modal
              isOpen={true}
              onClose={() => { setAuditModalOpen(false); setAuditSelected({ fieldId: '', questionText: '', nCRFDtlNo: null }); setAuditTrailList([]); setAuditTrailError(null); }}
              title={auditSelected.questionText ? `Field Audit - ${String(auditSelected.questionText).replace(/\s*DCF View Audit\s*$/i, '').trim()}` : 'Field Audit'}
              showCloseButton={true}
              className="crf-audit-modal"
              overlayClassName="crf-nested-modal-overlay"
            >
              {auditTrailLoading && <div className="mb-3"><Spinner /></div>}
              {!auditTrailLoading && auditTrailError && (
                <div className="alert alert-danger mb-3">{auditTrailError}</div>
              )}
              {!auditTrailLoading && !auditTrailError && auditTrailList.length === 0 && (
                <p className="text-muted mb-3">No audit entries found for this field.</p>
              )}
              {!auditTrailLoading && !auditTrailError && auditTrailList.length > 0 && (() => {
                // CRFDtl answer field is audited as VFieldValue – show only that row per header (Old Value —, New Value e.g. Yes)
                const answerFieldName = 'vfieldvalue';
                const filteredRows = auditTrailList.flatMap((hdr, hi) => {
                  const details = hdr.details || hdr.Details || [];
                  const recordedOn = hdr.dRecordedOnUTC || hdr.dRecordedOnUtc;
                  const sign = hdr.performedBy || hdr.vRecordedSign || '—';
                  const offset = hdr.vRecordedAtOffSet || hdr.vRecordedAtOffset || '—';
                  const answerDetails = details.filter((d) => (d.vFieldName || d.VFieldName || '').toLowerCase() === answerFieldName);
                  if (answerDetails.length === 0) return [];
                  return answerDetails.map((dtl, di) => {
                    const oldRaw = dtl.vOldValue ?? dtl.VOldValue;
                    const newRaw = dtl.vNewValue ?? dtl.VNewValue;
                    const oldFmt = formatAuditValueForDisplay(oldRaw);
                    const newFmt = formatAuditValueForDisplay(newRaw);
                    return {
                      key: `${hdr.nAuditHdrNo ?? hi}-${di}`,
                      oldV: oldFmt == null || String(oldFmt).trim() === '' ? '—' : String(oldFmt),
                      newV: newFmt == null || String(newFmt).trim() === '' ? '—' : String(newFmt),
                      recordedOn,
                      sign,
                      offset,
                    };
                  });
                });
                return (
                  <div className="table-responsive mb-3">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Old Value</th>
                          <th>New Value</th>
                          <th>Performed on UTC</th>
                          <th>Performed At Offset</th>
                          <th>Performed By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRows.length === 0 ? (
                          <tr><td colSpan={5} className="text-muted">No audit entries for this field.</td></tr>
                        ) : (
                          filteredRows.map((row) => (
                            <tr key={row.key}>
                              <td title={row.oldV !== '—' ? row.oldV : undefined}>{row.oldV.length > 80 ? row.oldV.slice(0, 80) + '…' : row.oldV}</td>
                              <td title={row.newV !== '—' ? row.newV : undefined}>{row.newV.length > 80 ? row.newV.slice(0, 80) + '…' : row.newV}</td>
                              <td>{formatPerformedOnUtcToLocal(row.recordedOn, row.offset)}</td>
                              <td>{formatOffsetForDisplay(row.offset)}</td>
                              <td title={row.sign}>{String(row.sign).length > 60 ? String(row.sign).slice(0, 60) + '…' : row.sign}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
              <button type="button" className="btn btn-secondary" onClick={() => setAuditModalOpen(false)}>Close</button>
            </Modal>
          </div>,
          document.body
        )}

        {/* Action Item Review modal - single action item view matching design (this question only, by nCRFDtlNo) */}
        {actionItemsModalOpen && createPortal(
          <div className="crf-nested-modal-wrapper" style={{ position: 'fixed', inset: 0, zIndex: 1200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Modal
              isOpen={true}
              onClose={() => { setActionItemsModalOpen(false); setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); setActionItemsList([]); setSelectedActionItemIndex(0); setActionReplyRemark(''); }}
              title="Issue Review"
              showCloseButton={true}
              className="crf-action-item-review-modal"
              overlayClassName="crf-nested-modal-overlay"
              size="medium"
            >
              {actionItemsLoading ? (
                <Spinner />
              ) : actionItemsList.length === 0 ? (
                <p className="text-muted mb-0">No issues for this question.</p>
              ) : (
                <>
                  {(() => {
                    const item = actionItemsList[Math.min(selectedActionItemIndex, actionItemsList.length - 1)];
                    const currentProfile = (user?.profileCode || user?.vUserTypeCode || user?.role || '').toString().toUpperCase().trim();
                    const assignedCode = (item.assignedToProfileCode || '').toString().toUpperCase().trim();
                    const raisedByProfile = (item.createdByProfileCode || item.CreatedByProfileCode || '').toString().toUpperCase().trim();
                    const sessionMid = Number(sessionRecordedByMappingId ?? 0) || 0;
                    const canReply = (item.status || '').toString().toLowerCase() === 'open' && currentProfile && assignedCode && currentProfile === assignedCode;
                    const canCreatorClose =
                      (item.status || '').toString().toLowerCase() === 'resolved'
                      && sessionMid > 0
                      && Number(item.createdBy || 0) === sessionMid
                      && (!raisedByProfile || (Boolean(currentProfile) && currentProfile === raisedByProfile));
                    return (
                      <>
                        {actionItemsList.length > 1 ? (
                          <div className="crf-action-item-review-field">
                            <label className="crf-action-item-review-label">Issue record</label>
                            <select
                              className="form-select"
                              value={selectedActionItemIndex}
                              onChange={(e) => setSelectedActionItemIndex(Number(e.target.value))}
                            >
                              {actionItemsList.map((x, i) => (
                                <option key={x.actionItemID ?? x.nCRFActionItemNo ?? i} value={i}>
                                  {(x.actionItemNumber || `Record ${i + 1}`)} — {(x.status || '').toString()}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : null}
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">Reference No</label>
                          <input
                            type="text"
                            className="form-control crf-action-item-review-readonly"
                            value={item.actionItemNumber ?? ''}
                            readOnly
                          />
                        </div>
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">Issue</label>
                          <div className="crf-action-item-review-readonly crf-action-item-review-textarea-readonly">
                            {(() => {
                              const raw = actionItemsSelected.questionText || item.actionItemOn || '';
                              const nameWithoutSuffix = raw.replace(/\s+DCF\s+View\s+Audit$/i, '').trim();
                              return nameWithoutSuffix || actionItemsSelected.fieldId || item.findingDescription || '-';
                            })()}
                          </div>
                        </div>
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">Issue Description</label>
                          <textarea
                            className="form-control crf-action-item-review-textarea-disabled"
                            value={actionDescriptionEdit}
                            readOnly
                            rows={4}
                            placeholder="Issue description..."
                          />
                        </div>
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">Priority</label>
                          <select
                            className="form-select crf-action-item-review-readonly"
                            value={(item.priorityDescription ?? item.severity ?? item.priority ?? '').toString().trim()}
                            disabled
                          >
                            <option value="">Select priority</option>
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                            {(() => {
                              const val = (item.priorityDescription ?? item.severity ?? item.priority ?? '').toString().trim();
                              if (val && !['Low', 'Medium', 'High', 'Critical'].includes(val)) {
                                return <option value={val}>{val}</option>;
                              }
                              return null;
                            })()}
                          </select>
                        </div>
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">Status</label>
                          <select
                            className="form-select crf-action-item-review-readonly"
                            value={(item.status || 'Open').toString()}
                            disabled
                          >
                            <option value="Open">Open</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Closed">Closed</option>
                          </select>
                        </div>
                        <div className="crf-action-item-review-field">
                          <label className="crf-action-item-review-label">
                            Action Item Description
                            {canReply ? <span className="required-asterisk">*</span> : null}
                          </label>
                          <textarea
                            className={`form-control ${!canReply || actionItemSaving ? 'crf-action-item-review-textarea-disabled' : 'crf-action-item-review-editable'}`}
                            value={actionReplyRemark}
                            onChange={(e) => setActionReplyRemark(e.target.value)}
                            readOnly={!canReply || actionItemSaving}
                            rows={4}
                            placeholder="Enter Action Item Description"
                          />
                        </div>
                        <div className="crf-action-item-review-footer">
                          {canReply && (
                            <>
                              <button
                                type="button"
                                className="btn btn-primary me-2"
                                disabled={actionItemSaving}
                                onClick={() => {
                                  if (!actionReplyRemark.trim()) {
                                    setToast({ type: 'error', message: actionItemMessages.replyRemarkRequired });
                                    return;
                                  }
                                  const replyId = item.actionItemID ?? item.nCRFActionItemNo;
                                  if (!replyId) {
                                    setToast({ type: 'error', message: 'Issue id is missing; cannot save.' });
                                    return;
                                  }
                                  actionItemUpdateRef.current = {
                                    actionItemID: replyId,
                                    replyRemark: actionReplyRemark.trim(),
                                    submitToCreator: false,
                                  };
                                  eSignRemarksRef.current = '';
                                  setESignPendingAction('updateActionItemRemark');
                                  setShowESignModal(true);
                                }}
                              >
                                Update
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={actionItemSaving}
                                onClick={() => {
                                  if (!actionReplyRemark.trim()) {
                                    setToast({ type: 'error', message: actionItemMessages.replyRemarkRequired });
                                    return;
                                  }
                                  const replyId = item.actionItemID ?? item.nCRFActionItemNo;
                                  if (!replyId) {
                                    setToast({ type: 'error', message: 'Issue id is missing; cannot save.' });
                                    return;
                                  }
                                  // Resolve + submit to creator: require electronic sign (Authenticate) first, same as Update
                                  actionItemUpdateRef.current = {
                                    actionItemID: replyId,
                                    replyRemark: actionReplyRemark.trim(),
                                    submitToCreator: true,
                                  };
                                  eSignRemarksRef.current = '';
                                  setESignPendingAction('updateActionItemRemark');
                                  setShowESignModal(true);
                                }}
                              >
                                Save
                              </button>
                            </>
                          )}
                          {canCreatorClose && (
                            <button
                              type="button"
                              className="btn btn-success"
                              disabled={actionItemSaving}
                              onClick={() => {
                                actionItemCloseRef.current = { actionItemID: item.actionItemID };
                                eSignRemarksRef.current = '';
                                setESignPendingAction('closeActionItem');
                                setShowESignModal(true);
                              }}
                            >
                              Close Issue
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => { setActionItemsModalOpen(false); setActionItemsSelected({ nCRFDtlNo: null, fieldId: '', questionText: '' }); setActionItemsList([]); setSelectedActionItemIndex(0); setActionReplyRemark(''); }}
                          >
                            Close
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </Modal>
          </div>,
          document.body
        )}
      </div>
      {toast && (embedInModal
        ? createPortal(
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
            <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>,
          document.body
        )
        : (
          <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
            <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
          </div>
        )
      )}
    </div>
  );
};

export default CRFForm;
